import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {Checkbox, CircularProgress, FormControl, InputLabel, MenuItem, Select, Dialog, DialogTitle, DialogContent, DialogActions, TablePagination} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import TextField from "@material-ui/core/TextField";
import {Button} from "@material-ui/core";
import { v4 as uuid } from 'uuid';
import Autocomplete from "@material-ui/lab/Autocomplete";
import { fromUtcForDisplay, formatUtcForDisplay, toUtcForStorage, localToUtcForStorage } from '../../../utils/dateUtils';
import dayjs from 'dayjs';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faLock} from "@fortawesome/free-solid-svg-icons";
import Papa from "papaparse";
import {getNumberInputDisplayValue, parseNumberInputValue} from '../../../utils/numberInputUtils';
import { followAppLink } from '../../../utils/appLink';
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
import DateTimeLocalInput from '../../Common/DateTimeLocalInput';
import ManualSalesLogDrawer from './ManualSalesLogDrawer';
import { AuthenticationService } from "../../../Services/AuthenticationService";
import { filterPersistenceService } from "../../../Services/FilterPersistenceService";
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import Tooltip from '@material-ui/core/Tooltip';
import Zoom from '@material-ui/core/Zoom';

const apiBaseUrl = process.env.REACT_APP_API_URL;

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    virtualizedTable: {
        height: '600px',
        overflow: 'auto',
    },
    virtualizedRow: {
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
    },
});

// Debounce utility function
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Enhanced memoization utility with size limits and LRU behavior
const memoize = (fn, keyFn, maxSize = 100) => {
    const cache = new Map();
    const memoizedFn = (...args) => {
        const key = keyFn ? keyFn(...args) : JSON.stringify(args);
        if (cache.has(key)) {
            // Move to end (most recently used)
            const value = cache.get(key);
            cache.delete(key);
            cache.set(key, value);
            return value;
        }
        
        // Check if cache is full and remove oldest entry
        if (cache.size >= maxSize) {
            const firstKey = cache.keys().next().value;
            cache.delete(firstKey);
        }
        
        const result = fn(...args);
        cache.set(key, result);
        return result;
    };
    
    memoizedFn.clear = () => cache.clear();
    memoizedFn.size = () => cache.size;
    
    return memoizedFn;
};

// Status normalization function to ensure consistent casing
const normalizeStatus = (status) => {
    if (!status) return null; // Return null for blank values, let caller handle fallback
    
    const normalized = status.toString().toLowerCase();
    if (normalized === 'fulfilled' || normalized === 'fulfill') {
        return 'Fulfilled';
    } else if (normalized === 'pending') {
        return 'Pending';
    } else if (normalized === 'cancelled' || normalized === 'canceled') {
        return 'Cancelled';
    } else if (normalized === 'shipped') {
        return 'Shipped';
    }
    
    // For unknown statuses, capitalize first letter and lowercase the rest
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

// Streaming CSV parser for large files
const streamParseCSV = (file, chunkSize = 1024 * 1024) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const results = [];
        let offset = 0;
        let isFirstChunk = true;
        let partialLine = '';
        
        reader.onload = (e) => {
            const chunk = e.target.result;
            const lines = (partialLine + chunk).split('\n');
            partialLine = lines.pop() || ''; // Keep incomplete line for next chunk
            
            if (isFirstChunk) {
                // Parse header from first chunk as an array (not as header: true)
                const headerLine = lines.shift();
                if (headerLine) {
                    const parsed = Papa.parse(headerLine, { header: false }).data[0];
                    if (parsed && parsed.length > 0) {
                        results.push(parsed);
                    }
                }
                isFirstChunk = false;
            }
            
            // Process complete lines
            lines.forEach(line => {
                if (line.trim()) {
                    const parsed = Papa.parse(line, { header: false }).data[0];
                    if (parsed && parsed.length > 0) {
                        results.push(parsed);
                    }
                }
            });
            
            offset += chunk.length;
            
            if (offset < file.size) {
                // Continue reading
                const slice = file.slice(offset, offset + chunkSize);
                reader.readAsText(slice);
            } else {
                // Finished reading
                if (partialLine.trim()) {
                    const parsed = Papa.parse(partialLine, { header: false }).data[0];
                    if (parsed && parsed.length > 0) {
                        results.push(parsed);
                    }
                }
                resolve(results);
            }
        };
        
        reader.onerror = reject;
        
        // Start reading
        const slice = file.slice(0, chunkSize);
        reader.readAsText(slice);
    });
};

// Helper functions for determining currently selling assembly
const getCurrentlySellingAssemblyId = (assemblyId, assemblies) => {
    // Get all assemblies with the same assemblyId that don't have a finalOrderNumber
    const assembliesWithoutFinalOrder = assemblies.filter(assembly => 
        assembly.assembly.assemblyId === assemblyId && 
        !assembly.assembly.finalOrderNumber
    );
    
    // If only one assembly without finalOrderNumber, it's the currently selling one
    if (assembliesWithoutFinalOrder.length === 1) {
        return assembliesWithoutFinalOrder[0].assembly.id;
    }
    
    // If multiple assemblies without finalOrderNumber, find the one with the lowest revision
    if (assembliesWithoutFinalOrder.length > 1) {
        // Sort by revision (ascending) and return the lowest
        const sortedAssemblies = assembliesWithoutFinalOrder.sort((a, b) => {
            const revisionA = a.assembly.revision || '';
            const revisionB = b.assembly.revision || '';
            return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return sortedAssemblies[0].assembly.id;
    }
    
    // If no assemblies without finalOrderNumber, return null
    return null;
};

const isAssemblyCurrentlySelling = (assembly, assemblies) => {
    if (!assembly || assembly.finalOrderNumber) {
        return false;
    }
    
    const currentlySellingId = getCurrentlySellingAssemblyId(assembly.assemblyId, assemblies);
    return currentlySellingId === assembly.id;
};

// Helper function to get base part number from a part ID
const getBasePartNumber = (partId, parts) => {
    const part = parts.find(p => p.part.id === partId);
    return part ? part.part.partId : '';
};

// Create lookup maps for faster access
const createLookupMaps = (parts, assemblies, subAssemblies, adminUsers) => {
    const partsMap = new Map();
    const assembliesMap = new Map();
    const subAssembliesMap = new Map();
    const adminUsersMap = new Map();
    
    parts.forEach(part => partsMap.set(part.part.id, part));
    assemblies.forEach(assembly => assembliesMap.set(assembly.assembly.id, assembly));
    subAssemblies.forEach(subAssembly => subAssembliesMap.set(subAssembly.subAssembly.id, subAssembly));
    adminUsers.forEach(user => adminUsersMap.set(user.id, user));
    
    return { partsMap, assembliesMap, subAssembliesMap, adminUsersMap };
};



class AdminSales extends Component {
    constructor(props) {
        super(props);
        globalProps = props;
        
        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
        
        // Initialize lookup maps with size limits
        this.lookupMaps = {
            partsMap: new Map(),
            assembliesMap: new Map(),
            subAssembliesMap: new Map(),
            adminUsersMap: new Map()
        };
        
        // Enhanced memoized functions with size limits
        this.memoizedGetFilteredSalesLogs = memoize(
            this.getFilteredSalesLogs.bind(this),
            (salesLogs, searchTerm, dateRangeStart, dateRangeEnd) => `${searchTerm}-${dateRangeStart || ''}-${dateRangeEnd || ''}-${salesLogs.length}`,
            50 // Limit cache size
        );
        
        // Create a memoized function that takes all necessary parameters
        this.memoizedGetSortedAndFilteredSalesLogs = memoize(
            (salesLogs, searchTerm, dateRangeStart, dateRangeEnd, sortField, sortDirection) => {
                // First filter using the memoized filter function
                let filteredLogs = this.memoizedGetFilteredSalesLogs(salesLogs, searchTerm, dateRangeStart, dateRangeEnd);
                
                // Then sort
                return filteredLogs.sort((a, b) => {
                    let aValue, bValue;
                    let aSecondaryValue, bSecondaryValue;
                    let aOrderNum, bOrderNum;
                    let aNum, bNum;
                    
                    switch (sortField) {
                        case 'compositeOrderNumber':
                            const aCompositeStr = (a.compositeOrderNumber || '').toString();
                            const bCompositeStr = (b.compositeOrderNumber || '').toString();
                            
                            // Check if both values contain only numeric characters and dashes
                            const aIsNumeric = /^[\d\-]+$/.test(aCompositeStr);
                            const bIsNumeric = /^[\d\-]+$/.test(bCompositeStr);
                            
                            if (aIsNumeric && bIsNumeric) {
                                // Both are numeric with dashes, convert to decimal for numeric sorting
                                const aCompositeNum = parseFloat(aCompositeStr.replace('-', '.'));
                                const bCompositeNum = parseFloat(bCompositeStr.replace('-', '.'));
                                
                                aValue = isNaN(aCompositeNum) ? 0 : aCompositeNum;
                                bValue = isNaN(bCompositeNum) ? 0 : bCompositeNum;
                            } else {
                                // At least one contains non-numeric characters, sort alphabetically
                                aValue = aCompositeStr.toLowerCase();
                                bValue = bCompositeStr.toLowerCase();
                            }
                            break;
                        case 'logDate':
                            aValue = new Date(a.logDate);
                            bValue = new Date(b.logDate);
                            // Secondary sort by Dealer Order # (orderNumber) when dates match
                            aOrderNum = a.compositeOrderNumber || '';
                            bOrderNum = b.compositeOrderNumber || '';
                            
                            // Try to parse as numbers for proper numeric sorting
                            aNum = parseInt(aOrderNum);
                            bNum = parseInt(bOrderNum);
                            
                            if (!isNaN(aNum) && !isNaN(bNum)) {
                                // Both are valid numbers, sort numerically
                                aSecondaryValue = aNum;
                                bSecondaryValue = bNum;
                            } else {
                                // At least one is not a number, sort as strings
                                aSecondaryValue = aOrderNum.toLowerCase();
                                bSecondaryValue = bOrderNum.toLowerCase();
                            }
                            break;
                        case 'user':
                            const aUser = this.lookupMaps.adminUsersMap.get(a.userId);
                            const bUser = this.lookupMaps.adminUsersMap.get(b.userId);
                            aValue = aUser ? `${aUser.firstName} ${aUser.lastName}` : (a.source === 'SquareSpace' ? 'Automatic Import' : 'Unknown');
                            bValue = bUser ? `${bUser.firstName} ${bUser.lastName}` : (b.source === 'SquareSpace' ? 'Automatic Import' : 'Unknown');
                            break;
                        case 'source':
                            aValue = a.source || '';
                            bValue = b.source || '';
                            break;
                        case 'orderNumber':
                            // Check if values are null/empty - these should always go to end
                            const aOrderIsEmpty = !a.orderNumber || a.orderNumber.trim() === '';
                            const bOrderIsEmpty = !b.orderNumber || b.orderNumber.trim() === '';
                            
                            if (aOrderIsEmpty && bOrderIsEmpty) {
                                return 0; // Both empty, equal
                            }
                            if (aOrderIsEmpty) {
                                return 1; // a is empty, goes to end
                            }
                            if (bOrderIsEmpty) {
                                return -1; // b is empty, goes to end
                            }
                            
                            aValue = a.orderNumber || '';
                            bValue = b.orderNumber || '';
                            // Handle both numeric and string order numbers
                            aOrderNum = a.compositeOrderNumber || '';
                            bOrderNum = b.compositeOrderNumber || '';
                            
                            // Try to parse as numbers for proper numeric sorting
                            aNum = parseInt(aOrderNum);
                            bNum = parseInt(bOrderNum);
                            
                            if (!isNaN(aNum) && !isNaN(bNum)) {
                                // Both are valid numbers, sort numerically
                                aSecondaryValue = aNum;
                                bSecondaryValue = bNum;
                            } else {
                                // At least one is not a number, sort as strings
                                aSecondaryValue = aOrderNum.toLowerCase();
                                bSecondaryValue = bOrderNum.toLowerCase();
                            }
                            break;
                        case 'partNumber':
                            const aPart = this.lookupMaps.partsMap.get(a.partId);
                            const aAssembly = this.lookupMaps.assembliesMap.get(a.assemblyId);
                            const aSubAssembly = this.lookupMaps.subAssembliesMap.get(a.subAssemblyId);
                            const bPart = this.lookupMaps.partsMap.get(b.partId);
                            const bAssembly = this.lookupMaps.assembliesMap.get(b.assemblyId);
                            const bSubAssembly = this.lookupMaps.subAssembliesMap.get(b.subAssemblyId);
                            aValue = aAssembly ? aAssembly.assembly.assemblyId : (aSubAssembly ? aSubAssembly.subAssembly.subAssemblyId : (aPart ? aPart.part.partId : ''));
                            bValue = bAssembly ? bAssembly.assembly.assemblyId : (bSubAssembly ? bSubAssembly.subAssembly.subAssemblyId : (bPart ? bPart.part.partId : ''));
                            break;
                        case 'status':
                            aValue = normalizeStatus(a.status);
                            bValue = normalizeStatus(b.status);
                            break;
                        case 'customerName':
                            // Check if values are null/empty - these should always go to end
                            const aCustomerIsEmpty = !a.customerName || a.customerName.trim() === '';
                            const bCustomerIsEmpty = !b.customerName || b.customerName.trim() === '';
                            
                            if (aCustomerIsEmpty && bCustomerIsEmpty) {
                                return 0; // Both empty, equal
                            }
                            if (aCustomerIsEmpty) {
                                return 1; // a is empty, goes to end
                            }
                            if (bCustomerIsEmpty) {
                                return -1; // b is empty, goes to end
                            }
                            
                            aValue = (a.customerName || '').toLowerCase();
                            bValue = (b.customerName || '').toLowerCase();
                            break;
                        case 'shippingAddress':
                            // Check if values are null/empty - these should always go to end
                            const aAddressIsEmpty = !a.shippingAddress || a.shippingAddress.trim() === '';
                            const bAddressIsEmpty = !b.shippingAddress || b.shippingAddress.trim() === '';
                            
                            if (aAddressIsEmpty && bAddressIsEmpty) {
                                return 0; // Both empty, equal
                            }
                            if (aAddressIsEmpty) {
                                return 1; // a is empty, goes to end
                            }
                            if (bAddressIsEmpty) {
                                return -1; // b is empty, goes to end
                            }
                            
                            aValue = (a.shippingAddress || '').toLowerCase();
                            bValue = (b.shippingAddress || '').toLowerCase();
                            break;
                        default:
                            return 0;
                    }
                    
                    // Primary sort comparison
                    let primaryComparison = 0;
                    if (sortDirection === 'asc') {
                        primaryComparison = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                    } else {
                        primaryComparison = aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                    }
                    
                    // If primary values are equal and we have secondary values (for logDate sorting), use secondary sort
                    if (primaryComparison === 0 && aSecondaryValue !== undefined && bSecondaryValue !== undefined) {
                        if (sortDirection === 'asc') {
                            return aSecondaryValue > bSecondaryValue ? 1 : aSecondaryValue < bSecondaryValue ? -1 : 0;
                        } else {
                            return aSecondaryValue < bSecondaryValue ? 1 : aSecondaryValue > bSecondaryValue ? -1 : 0;
                        }
                    }
                    
                    return primaryComparison;
                });
            },
            (salesLogs, searchTerm, dateRangeStart, dateRangeEnd, sortField, sortDirection) => 
                `${searchTerm}-${dateRangeStart || ''}-${dateRangeEnd || ''}-${sortField}-${sortDirection}-${salesLogs.length}`,
            50 // Limit cache size
        );
        
        // Debounced filtering function for performance
        this.debouncedFilter = debounce(() => {
            // Force re-render to update filtered results
            this.forceUpdate();
        }, 300);
        
        // Memory management
        this.memoryCleanupInterval = null;
        this.lastMemoryCheck = Date.now();
    }

    state = {
        loading: true,
        user: null,
        permissions: [],
        adminUsers: [],
        assemblies: [],
        subAssemblies: [],
        parts: [],
        salesLogs: [],
        salesLogSources: [],
        dataRefreshKey: 0,
        page: 0,
        rowsPerPage: 100,
        searchTerm: '',
        dateRangeStart: null,
        dateRangeEnd: null,
        sortField: 'logDate',
        sortDirection: 'desc',
        newLogOrderNumber: '',
        newLogQuantity: 1,
        newLogReason: '',
        newLogPart: null,
        newLogDate: new Date(),
        newLogDateManuallyChanged: false,
        newLogSource: 'Customer Service',
        newLogStatus: 'Fulfilled',
        newLogAffectsProjections: true,
        newLogCustomerName: '',
        newLogShippingAddress: '',
        newLogCustomerEmail: '',
        manualSalesLogOpen: false,
        manualLogEntries: [],
        importSalesLogsOpen: false,
        importFiles: [],
        importDealer: null,
        parsedImportData: [],
        importStep: 'upload',
        importDuplicates: [],
        importErrors: [],
        importOverrideExactDuplicates: false,
        importIndividualLineOverrides: {},
        savingImportedData: false,
        // Pagination for import review to prevent memory issues with large files
        importReviewItemsPerPage: 500,
        importReviewCurrentPages: {}, // Track current page per file
        importReviewShowProblemsOnly: false, // Filter to show only problematic rows
        importReviewHideExactDuplicates: false, // Filter to hide exact duplicate rows
        ecsOrderLogDates: {}, // Track logDate for each ECS order/file (keyed by filename)
        // Performance optimization states
        isProcessingData: false,
        virtualScrollEnabled: false,
        // Copy table dialog state
        showCopyTable: false,
        copyTableData: [],
        // Squarespace automated import control
        squarespaceImportPaused: false,
        loadingImportStatus: false,
        // Multiline sales order edit drawer
        editSalesOrderOpen: false,
        editingSalesOrderLogs: [],
        editingSalesOrderGlobalSeq: null,
        savingSalesOrder: false,
        salesOrderHeader: null,
        salesOrderHeaderSeq: null,
        salesOrderHeaderDirty: false,
        savingSalesOrderHeader: false,
        deletedSalesLogIds: [],
        pendingNewSalesLogs: [],
        unlockingSalesOrderLogId: null,
        salesOrderDataNeedsReload: false,
        showCopyTableLineSelector: false,
        copyTableSelectedIds: [],
        // Multiselect for bulk operations
        selectedSalesLogs: [],
        lastSelectedIndex: null, // Track last clicked index for Shift+Click range selection
        isProcessingModifierClick: false, // Flag to prevent checkbox onChange during modifier clicks
        bulkEditDrawerOpen: false,
        bulkEditAffectsProjections: null,
        bulkEditCustomerName: '',
        bulkEditShippingAddress: '',
        bulkEditCustomerEmail: '',
        bulkEditStatus: '',
        bulkEditReason: '',
        bulkEditLockUnlock: null, // null = no change, true = lock, false = unlock
        savingBulkEdit: false,
        packagingSelectedSalesLogs: false
    };

    componentDidMount() {
        this.loadSavedFilterSettings();
        this.loadData();
        
        // Enable virtual scrolling for large datasets
        this.checkVirtualScrollNeeded();
        
        // Start memory management
        this.startMemoryManagement();
        
        // Check automated import status
        this.checkSquarespaceImportStatus();
    }
    
    componentWillUnmount() {
        // Clean up memory management
        if (this.memoryCleanupInterval) {
            clearInterval(this.memoryCleanupInterval);
        }
        
        // Clear all caches
        this.memoizedGetFilteredSalesLogs.clear();
        this.memoizedGetSortedAndFilteredSalesLogs.clear();
        
        // Clear lookup maps
        this.lookupMaps.partsMap.clear();
        this.lookupMaps.assembliesMap.clear();
        this.lookupMaps.subAssembliesMap.clear();
        this.lookupMaps.adminUsersMap.clear();
    }
    
    // Squarespace automated import control methods
    checkSquarespaceImportStatus = () => {
        axios.get(apiBaseUrl + 'Squarespace/status')
            .then(response => {
                this.setState({ squarespaceImportPaused: response.data.isPaused });
            })
            .catch(error => {
                console.error('Error checking SquareSpace import status:', error);
            });
    }
    
    toggleSquarespaceImport = () => {
        const { squarespaceImportPaused } = this.state;
        const endpoint = squarespaceImportPaused ? apiBaseUrl + 'Squarespace/resume' : apiBaseUrl + 'Squarespace/pause';
        const action = squarespaceImportPaused ? 'resume' : 'pause';
        
        this.setState({ loadingImportStatus: true });
        
        axios.post(endpoint)
            .then(response => {
                this.setState({ 
                    squarespaceImportPaused: response.data.isPaused,
                    loadingImportStatus: false
                });
                window.$alertSuccess(`Automatic SquareSpace import ${action}d`);
            })
            .catch(error => {
                console.error(`Error ${action}ing SquareSpace import:`, error);
                window.$alertError(`Failed to ${action} automatic SquareSpace import`);
                this.setState({ loadingImportStatus: false });
            });
    }
    
    startMemoryManagement = () => {
        // Check memory usage every 30 seconds
        this.memoryCleanupInterval = setInterval(() => {
            this.performMemoryCleanup();
        }, 30000);
    }
    
    performMemoryCleanup = () => {
        const now = Date.now();
        
        // Only perform cleanup if enough time has passed
        if (now - this.lastMemoryCheck < 30000) {
            return;
        }
        
        this.lastMemoryCheck = now;
        
        // Force garbage collection if available
        if (window.gc) {
            window.gc();
        }
        
        // Clear caches if they're getting too large
        if (this.memoizedGetFilteredSalesLogs.size() > 100) {
            this.memoizedGetFilteredSalesLogs.clear();
        }
        
        if (this.memoizedGetSortedAndFilteredSalesLogs.size() > 100) {
            this.memoizedGetSortedAndFilteredSalesLogs.clear();
        }
        
        // Log memory usage for debugging
        if (performance.memory) {
            const memoryInfo = performance.memory;
            const usedMB = Math.round(memoryInfo.usedJSHeapSize / 1024 / 1024);
            const totalMB = Math.round(memoryInfo.totalJSHeapSize / 1024 / 1024);
            
            if (usedMB > 500) { // Log if using more than 500MB
                console.warn(`High memory usage: ${usedMB}MB / ${totalMB}MB`);
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Check if we need to enable virtual scrolling
        if (prevState.salesLogs.length !== this.state.salesLogs.length) {
            this.checkVirtualScrollNeeded();
        }
        
        // Update lookup maps when data changes
        if (prevState.parts !== this.state.parts || 
            prevState.assemblies !== this.state.assemblies || 
            prevState.subAssemblies !== this.state.subAssemblies || 
            prevState.adminUsers !== this.state.adminUsers) {
            this.updateLookupMaps();
        }

        const seq = this.props.match?.params?.salesLogSequence;
        if (!seq) return;
        const seqChanged = String(prevProps.match?.params?.salesLogSequence || '') !== String(seq);
        const logsChanged = prevState.salesLogs !== this.state.salesLogs;
        if (seqChanged || (logsChanged && !this.state.salesOrderHeaderDirty)) {
            this.syncSalesOrderHeader(seq);
        }
    }

    syncSalesOrderHeader = (seq) => {
        const logs = (this.state.salesLogs || []).filter((log) => log.globalSequenceNumber === Number(seq));
        const first = logs[0];
        if (!first) return;
        this.setState({
            salesOrderHeaderSeq: String(seq),
            salesOrderHeaderDirty: false,
            salesOrderHeader: {
                logDate: first.logDate,
                source: first.source || '',
                orderNumber: first.orderNumber || '',
                customerName: first.customerName || '',
                shippingAddress: first.shippingAddress || '',
                customerEmail: first.customerEmail || '',
            },
        });
    };

    updateSalesOrderHeader = (field, value) => {
        this.setState((prev) => ({
            salesOrderHeaderDirty: true,
            salesOrderHeader: {
                ...(prev.salesOrderHeader || {}),
                [field]: value,
            },
        }));
    };

    handleSaveSalesOrderHeader = async () => {
        const seq = Number(this.props.match?.params?.salesLogSequence);
        const header = this.state.salesOrderHeader;
        const logs = (this.state.salesLogs || []).filter((log) => log.globalSequenceNumber === seq);
        if (!header || logs.length === 0) return;

        this.setState({ savingSalesOrderHeader: true });
        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            await Promise.all(logs.map((log) => assemblyService.updateSalesLog({
                id: log.id,
                globalSequenceNumber: log.globalSequenceNumber,
                logDate: header.logDate,
                source: header.source,
                orderNumber: header.orderNumber,
                assemblyId: log.assemblyId,
                subAssemblyId: log.subAssemblyId,
                partId: log.partId,
                quantity: parseNumberInputValue(log.quantity),
                reason: log.reason,
                status: normalizeStatus(log.status),
                affectsProjections: log.affectsProjections,
                lineNumber: log.lineNumber,
                isManuallyAssigned: log.isManuallyAssigned,
                userId: log.userId,
                customerName: header.customerName || null,
                shippingAddress: header.shippingAddress || null,
                customerEmail: header.customerEmail || null,
            })));
            window.$alertSuccess('Order information saved');
            this.setState({ salesOrderHeaderDirty: false, savingSalesOrderHeader: false });
            this.loadData();
        } catch (error) {
            console.error('Error saving order information:', error);
            window.$alertError('Error saving order information');
            this.setState({ savingSalesOrderHeader: false });
        }
    };

    openSalesOrderLineEditor = (seq) => {
        this.openMultilineEditDrawer(seq);
        const header = this.state.salesOrderHeader;
        if (!header) return;
        this.setState((prev) => ({
            editingSalesOrderLogs: prev.editingSalesOrderLogs.map((log) => ({
                ...log,
                logDate: header.logDate,
                source: header.source,
                orderNumber: header.orderNumber,
                customerName: header.customerName,
                shippingAddress: header.shippingAddress,
                customerEmail: header.customerEmail,
            })),
        }));
    };

    checkVirtualScrollNeeded = () => {
        const shouldEnableVirtualScroll = this.state.salesLogs.length > 1000;
        if (shouldEnableVirtualScroll !== this.state.virtualScrollEnabled) {
            this.setState({ virtualScrollEnabled: shouldEnableVirtualScroll });
        }
    }

    updateLookupMaps = () => {
        this.lookupMaps = createLookupMaps(
            this.state.parts,
            this.state.assemblies,
            this.state.subAssemblies,
            this.state.adminUsers
        );
        

    };

    loadSavedFilterSettings = () => {
        const savedSettings = filterPersistenceService.loadFilterSettings('sales');
        if (savedSettings) {
            this.setState({
                page: savedSettings.page || 0,
                rowsPerPage: savedSettings.rowsPerPage || 100,
                searchTerm: savedSettings.searchTerm || '',
                dateRangeStart: savedSettings.dateRangeStart || null,
                dateRangeEnd: savedSettings.dateRangeEnd || null,
                sortField: savedSettings.sortField || 'logDate',
                sortDirection: savedSettings.sortDirection || 'desc'
            });
        }
    }

    saveFilterSettings = () => {
        const settings = {
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
            searchTerm: this.state.searchTerm,
            dateRangeStart: this.state.dateRangeStart,
            dateRangeEnd: this.state.dateRangeEnd,
            sortField: this.state.sortField,
            sortDirection: this.state.sortDirection
        };
        filterPersistenceService.saveFilterSettings('sales', settings);
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();

        self.setState({loading: true, isProcessingData: true});
        return Promise.all([self.loadAdminUsers(), self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts(), self.loadSalesLogs(), self.loadSalesLogSources()]).then(() => {
            // Clear memoization caches before state update to ensure fresh data
            self.memoizedGetSortedAndFilteredSalesLogs.clear();
            self.memoizedGetFilteredSalesLogs.clear();
            
            self.setState({
                loading: false, 
                user, 
                permissions: user.userPermissions,
                isProcessingData: false
            }, () => {
                // Update lookup maps after state is set
                this.updateLookupMaps();
                // Focus search bar after render
                setTimeout(() => {
                    if (this.searchInputRef) {
                        this.searchInputRef.focus();
                    }
                }, 100);
            });
        });
    };

    loadAdminUsers = () => {
        const self = this;
        const userService = ServiceLocator.Instance.getUserService();
        return userService.getAdminUsers().then((adminUsersResult) => {
            self.setState({adminUsers: adminUsersResult});
        });
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAllAssembliesExpanded().then((assembliesResult) => {
            self.setState({assemblies: assembliesResult});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getAllSubAssembliesExpanded().then((subAssembliesResult) => {
            self.setState({subAssemblies: subAssembliesResult});
        });
    };

    loadParts = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getAllPartsExpanded().then((partsResult) => {
            self.setState({parts: partsResult});
        });
    };

    loadSalesLogs = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getSalesLogsNoCache().then((salesLogsResult) => {
            // Clear memoization caches when sales logs are updated
            self.memoizedGetSortedAndFilteredSalesLogs.clear();
            self.memoizedGetFilteredSalesLogs.clear();
            self.setState({ salesLogs: salesLogsResult }, () => {
                // Clear caches again after state is set to ensure fresh data
                self.memoizedGetSortedAndFilteredSalesLogs.clear();
                self.memoizedGetFilteredSalesLogs.clear();
            });
        });
    };

    loadSalesLogSources = () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        return assemblyService.getSalesLogSources().then((salesLogSourcesResult) => {
            this.setState({ salesLogSources: salesLogSourcesResult.sort((a, b) => a.source > b.source ? 1 : -1) });
        });
    };

    // Optimized filtering function using lookup maps
    getFilteredSalesLogs = (salesLogs, searchTerm = null, dateRangeStart = null, dateRangeEnd = null) => {
        const searchTermToUse = searchTerm !== null ? searchTerm : this.state.searchTerm;
        const dateStart = dateRangeStart !== null ? dateRangeStart : this.state.dateRangeStart;
        const dateEnd = dateRangeEnd !== null ? dateRangeEnd : this.state.dateRangeEnd;
        
        // First apply date range filter if specified
        let filteredLogs = salesLogs;
        if (dateStart || dateEnd) {
            filteredLogs = filteredLogs.filter(log => {
                if (!log.logDate) return false;
                
                // Convert UTC logDate to local time for comparison (matching how it's displayed in the table)
                const logDateLocal = fromUtcForDisplay(log.logDate);
                
                // Get the local date string (YYYY-MM-DD) from the logDate to match what's displayed in the table
                const logDateLocalStr = logDateLocal.format('YYYY-MM-DD');
                
                if (dateStart) {
                    // Compare date strings directly (matching what user sees in the table)
                    if (logDateLocalStr < dateStart) return false;
                }
                
                if (dateEnd) {
                    // Compare date strings directly (matching what user sees in the table)
                    if (logDateLocalStr > dateEnd) return false;
                }
                
                return true;
            });
        }
        
        // Then apply search term filter if specified
        if (!searchTermToUse) {
            return filteredLogs;
        }
        
        const searchTerms = searchTermToUse.split(' ').filter(term => term.trim() !== '');
        if (searchTerms.length === 0) {
            return filteredLogs;
        }
        
        // Safety check: ensure lookupMaps is initialized
        if (!this.lookupMaps) {
            return filteredLogs;
        }
        
        const { partsMap, assembliesMap, subAssembliesMap, adminUsersMap } = this.lookupMaps;
        
        filteredLogs = filteredLogs.filter(log => {
            // All search terms must match at least one field
            return searchTerms.every(term => {
                const searchTerm = term.toLowerCase();
                
                // Check part information using map lookup
                const part = partsMap.get(log.partId);
                if (part && part.part.partId.toLowerCase().includes(searchTerm)) return true;
                
                // Check assembly information using map lookup
                const assembly = assembliesMap.get(log.assemblyId);
                if (assembly && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm)) return true;
                
                // Check assembly information using map lookup
                const subAssembly = subAssembliesMap.get(log.subAssemblyId);
                if (subAssembly && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm)) return true;
                
                // Check source
                if (log.source.toLowerCase().includes(searchTerm)) return true;
                
                // Check JXB order number
                if (log.compositeOrderNumber?.toString().includes(searchTerm)) return true;
                
                // Check dealer order number
                if (log.orderNumber?.toLowerCase().includes(searchTerm)) return true;
                
                // Check reason
                if (log.reason?.toLowerCase().includes(searchTerm)) return true;
                
                // Check customer name
                if (log.customerName?.toLowerCase().includes(searchTerm)) return true;
                
                // Check shipping address
                if (log.shippingAddress?.toLowerCase().includes(searchTerm)) return true;
                if (log.customerEmail?.toLowerCase().includes(searchTerm)) return true;
                
                // Check admin user from sales log using map lookup
                const adminUser = adminUsersMap.get(log.userId);
                if (adminUser) {
                    const adminUserFullName = `${adminUser.firstName} ${adminUser.lastName}`;
                    if (adminUserFullName.toLowerCase().includes(searchTerm)) return true;
                }
                
                return false;
            });
        });
        
        return filteredLogs;
    };



    // Get processed data with caching
    getProcessedData = () => {
        const { salesLogs, searchTerm, dateRangeStart, dateRangeEnd, sortField, sortDirection } = this.state;
        
        // Safety check: if salesLogs is not loaded yet, return empty array
        if (!salesLogs || salesLogs.length === 0) {
            return [];
        }
        
        // Use the memoized function directly - it handles its own caching
        return this.memoizedGetSortedAndFilteredSalesLogs(salesLogs, searchTerm, dateRangeStart, dateRangeEnd, sortField, sortDirection);
    };

    handleSelectAllSalesLogs = (event) => {
        const checked = event.target.checked;
        const processedData = this.getProcessedData();
        const allFilteredIds = processedData.map(log => log.id);
        
        if (checked) {
            // Add all filtered IDs to selection
            this.setState({
                selectedSalesLogs: [...new Set([...this.state.selectedSalesLogs, ...allFilteredIds])]
            });
        } else {
            // Remove all filtered IDs from selection
            this.setState({
                selectedSalesLogs: this.state.selectedSalesLogs.filter(id => !allFilteredIds.includes(id))
            });
        }
    };

    handleSelectNoneSalesLogs = () => {
        const processedData = this.getProcessedData();
        const allFilteredIds = processedData.map(log => log.id);
        this.setState({
            selectedSalesLogs: this.state.selectedSalesLogs.filter(id => !allFilteredIds.includes(id))
        });
    };

    handleSelectSalesLog = (logId, event) => {
        const processedData = this.getProcessedData();
        const currentIndex = processedData.findIndex(log => log.id === logId);
        
        // Get current selection state BEFORE any changes
        const isCurrentlySelected = this.state.selectedSalesLogs.includes(logId);
        
        // Check for modifier keys - try multiple ways to detect them
        const isCtrlPressed = (event && (event.ctrlKey || event.metaKey)) || 
                             (event.nativeEvent && (event.nativeEvent.ctrlKey || event.nativeEvent.metaKey));
        const isShiftPressed = (event && event.shiftKey) || 
                               (event.nativeEvent && event.nativeEvent.shiftKey);
        
        // Set flag to prevent checkbox onChange from processing modifier clicks
        const hasModifiers = isCtrlPressed || isShiftPressed;
        if (hasModifiers) {
            this.setState({ isProcessingModifierClick: true });
            // Prevent default checkbox behavior when modifiers are pressed
            if (event && event.preventDefault) event.preventDefault();
            if (event && event.stopPropagation) event.stopPropagation();
        }
        
        if (currentIndex === -1) {
            // Log not found in processed data, fall back to simple toggle
            this.setState({
                selectedSalesLogs: isCurrentlySelected
                    ? this.state.selectedSalesLogs.filter(id => id !== logId)
                    : [...this.state.selectedSalesLogs, logId],
                lastSelectedIndex: null
            });
            return;
        }

        if (isShiftPressed) {
            // Shift+Click: Select range from lastSelectedIndex to currentIndex
            if (this.state.lastSelectedIndex !== null && this.state.lastSelectedIndex !== -1) {
                const startIndex = Math.min(this.state.lastSelectedIndex, currentIndex);
                const endIndex = Math.max(this.state.lastSelectedIndex, currentIndex);
                const rangeIds = processedData.slice(startIndex, endIndex + 1).map(log => log.id);
                
                if (isCtrlPressed) {
                    // Ctrl+Shift+Click: Toggle range selection
                    const allInRangeSelected = rangeIds.every(id => this.state.selectedSalesLogs.includes(id));
                    if (allInRangeSelected) {
                        // Remove all items in range
                        this.setState({
                            selectedSalesLogs: this.state.selectedSalesLogs.filter(id => !rangeIds.includes(id)),
                            lastSelectedIndex: currentIndex
                        });
                    } else {
                        // Add all items in range
                        this.setState({
                            selectedSalesLogs: [...new Set([...this.state.selectedSalesLogs, ...rangeIds])],
                            lastSelectedIndex: currentIndex
                        });
                    }
                } else {
                    // Shift+Click: Select range (add all items in range)
                    this.setState({
                        selectedSalesLogs: [...new Set([...this.state.selectedSalesLogs, ...rangeIds])],
                        lastSelectedIndex: currentIndex
                    });
                }
            } else {
                // No previous selection, treat Shift+Click as normal click but set lastSelectedIndex
                this.setState({
                    selectedSalesLogs: isCurrentlySelected
                        ? this.state.selectedSalesLogs.filter(id => id !== logId)
                        : [...this.state.selectedSalesLogs, logId],
                    lastSelectedIndex: currentIndex
                });
            }
        } else if (isCtrlPressed) {
            // Ctrl+Click: Toggle individual item based on current state
            this.setState({
                selectedSalesLogs: isCurrentlySelected
                    ? this.state.selectedSalesLogs.filter(id => id !== logId)
                    : [...this.state.selectedSalesLogs, logId],
                lastSelectedIndex: currentIndex
            });
        } else {
            // Normal click: Simple toggle based on current state (not event.target.checked)
            this.setState({
                selectedSalesLogs: isCurrentlySelected
                    ? this.state.selectedSalesLogs.filter(id => id !== logId)
                    : [...this.state.selectedSalesLogs, logId],
                lastSelectedIndex: currentIndex
            });
        }
        
        // Clear the modifier flag after a short delay to allow state to update
        if (hasModifiers) {
            setTimeout(() => {
                this.setState({ isProcessingModifierClick: false });
            }, 100);
        }
    };

    openBulkEditDrawer = () => {
        if (this.state.selectedSalesLogs.length === 0) {
            window.$alertError('Please select at least one sales log');
            return;
        }
        this.setState({
            bulkEditDrawerOpen: true,
            bulkEditAffectsProjections: null,
            bulkEditCustomerName: '',
            bulkEditShippingAddress: '',
            bulkEditStatus: '',
            bulkEditReason: '',
            bulkEditLockUnlock: null
        });
    };

    handleCloseBulkEditDrawer = () => {
        this.setState({
            bulkEditDrawerOpen: false,
            bulkEditAffectsProjections: null,
            bulkEditCustomerName: '',
            bulkEditShippingAddress: '',
            bulkEditStatus: '',
            bulkEditReason: '',
            bulkEditLockUnlock: null
        });
    };

    handleBulkEditSave = async () => {
        const self = this;
        if (this.state.selectedSalesLogs.length === 0) {
            window.$alertError('Please select at least one sales log');
            return;
        }

        this.setState({ savingBulkEdit: true });

        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const selectedLogs = this.state.salesLogs.filter(log => 
                this.state.selectedSalesLogs.includes(log.id)
            );

            const updatePromises = selectedLogs.map(log => {
                // Start with all fields from the log (similar to unlockSalesLog which spreads editingLog)
                const updatePayload = {
                    ...log,
                    id: log.id,
                    source: log.source,
                    orderNumber: log.orderNumber,
                    logDate: log.logDate,
                    assemblyId: log.assemblyId,
                    subAssemblyId: log.subAssemblyId,
                    partId: log.partId,
                    quantity: log.quantity,
                    reason: log.reason,
                    affectsProjections: log.affectsProjections,
                    status: log.status,
                    lineNumber: log.lineNumber,
                    customerName: log.customerName,
                    shippingAddress: log.shippingAddress,
                    customerEmail: log.customerEmail
                };

                // Apply bulk edits
                if (this.state.bulkEditAffectsProjections !== null) {
                    updatePayload.affectsProjections = this.state.bulkEditAffectsProjections;
                }
                if (this.state.bulkEditCustomerName !== '') {
                    updatePayload.customerName = this.state.bulkEditCustomerName;
                }
                if (this.state.bulkEditShippingAddress !== '') {
                    updatePayload.shippingAddress = this.state.bulkEditShippingAddress;
                }
                if (this.state.bulkEditCustomerEmail !== '') {
                    updatePayload.customerEmail = this.state.bulkEditCustomerEmail;
                }
                if (this.state.bulkEditStatus !== '') {
                    updatePayload.status = this.state.bulkEditStatus;
                }
                if (this.state.bulkEditReason !== '') {
                    updatePayload.reason = this.state.bulkEditReason;
                }
                if (this.state.bulkEditLockUnlock !== null) {
                    // Explicitly set isManuallyAssigned as boolean
                    updatePayload.isManuallyAssigned = Boolean(this.state.bulkEditLockUnlock);
                    // When locking, set userId to current user (similar to single log edit)
                    // When unlocking, userId can remain as is (or be cleared if needed)
                    if (this.state.bulkEditLockUnlock === true) {
                        updatePayload.userId = AuthenticationService.getInstance().getUserId();
                    }
                }

                return assemblyService.updateSalesLog(updatePayload);
            });

            await Promise.all(updatePromises);

            // Reload data - this will clear caches internally
            await self.loadData();

            window.$alertSuccess(`${selectedLogs.length} sales log(s) updated successfully`);
            
            // Clear caches again after state update to ensure fresh data
            self.memoizedGetSortedAndFilteredSalesLogs.clear();
            self.memoizedGetFilteredSalesLogs.clear();
            
            this.setState({
                selectedSalesLogs: [],
                bulkEditDrawerOpen: false,
                savingBulkEdit: false,
                bulkEditAffectsProjections: null,
                bulkEditCustomerName: '',
                bulkEditShippingAddress: '',
                bulkEditCustomerEmail: '',
                bulkEditStatus: '',
                bulkEditReason: '',
                bulkEditLockUnlock: null,
                dataRefreshKey: this.state.dataRefreshKey + 1
            }, () => {
                // Clear caches one more time and force re-render to ensure UI updates
                self.memoizedGetSortedAndFilteredSalesLogs.clear();
                self.memoizedGetFilteredSalesLogs.clear();
                setTimeout(() => {
                    self.setState({ dataRefreshKey: self.state.dataRefreshKey + 1 });
                }, 100);
            });
        } catch (error) {
            console.error('Error updating sales logs:', error);
            window.$alertError('Error updating sales logs: ' + (error.response?.data?.message || error.message));
            this.setState({ savingBulkEdit: false });
        }
    };

    // Search function that updates state immediately and uses debounced filtering
    searchSales = (event) => {
        const searchInput = event.target.value;
        this.setState({searchTerm: searchInput, page: 0}, () => {
            this.saveFilterSettings();
            // Clear memoization cache to ensure fresh data
            this.memoizedGetSortedAndFilteredSalesLogs.clear();
            // Trigger debounced filtering for performance
            this.debouncedFilter();
        });
    };

    handleChangePage = (event, newPage) => {
        this.setState({page: newPage}, () => {
            this.saveFilterSettings();
            // Clear memoization cache to ensure fresh data
            this.memoizedGetSortedAndFilteredSalesLogs.clear();
            // Trigger debounced filtering to force re-render
            this.debouncedFilter();
        });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }, () => {
            this.saveFilterSettings();
            // Clear memoization cache to ensure fresh data
            this.memoizedGetSortedAndFilteredSalesLogs.clear();
            // Trigger debounced filtering to force re-render
            this.debouncedFilter();
        });
    };

    handleSort = (field) => {
        this.setState(prevState => ({
            sortField: field,
            sortDirection: prevState.sortField === field && prevState.sortDirection === 'asc' ? 'desc' : 'asc',
            page: 0
        }), () => {
            this.saveFilterSettings();
            // Clear memoization cache to ensure fresh data
            this.memoizedGetSortedAndFilteredSalesLogs.clear();
            // Trigger debounced filtering to force re-render
            this.debouncedFilter();
        });
    };

    // Virtual scrolling render function
    renderVirtualizedRow = (index, style) => {
        const processedData = this.getProcessedData();
        const salesLog = processedData[index];
        
        if (!salesLog) return null;
        
        return this.renderSalesLogRow(salesLog, index, style);
    };

    // Extract row rendering logic
    renderSalesLogRow = (salesLog, index, style = {}) => {
        const part = this.lookupMaps.partsMap.get(salesLog.partId);
        const assembly = this.lookupMaps.assembliesMap.get(salesLog.assemblyId);
        const subAssembly = this.lookupMaps.subAssembliesMap.get(salesLog.subAssemblyId);
        const adminUser = this.lookupMaps.adminUsersMap.get(salesLog.userId);
        
        let partNumber = '';
        let description = '';
        
        if (assembly) {
            partNumber = assembly.assembly.assemblyId;
            description = assembly.assembly.description || '';
        } else if (subAssembly) {
            partNumber = subAssembly.subAssembly.subAssemblyId;
            description = subAssembly.subAssembly.description || '';
        } else if (part) {
            partNumber = part.part.partId;
            description = part.part.description || '';
        }
        
        const adminUserFullName = adminUser ? `${adminUser.firstName} ${adminUser.lastName}` : (salesLog.source === 'SquareSpace' ? 'Automatic Import' : 'Unknown');
        
        return (
            <div key={salesLog.id} className={this.props.classes.virtualizedRow} style={style}>
                <div style={{width: '80px', padding: '0 8px'}}>{salesLog.compositeOrderNumber}</div>
                <div style={{width: '120px', padding: '0 8px'}}>{formatUtcForDisplay(salesLog.logDate)}</div>
                <div style={{width: '150px', padding: '0 8px'}}>{adminUserFullName}</div>
                <div style={{width: '100px', padding: '0 8px'}}>{salesLog.source}</div>
                <div style={{width: '100px', padding: '0 8px'}}>{salesLog.orderNumber}</div>
                <div style={{width: '150px', padding: '0 8px'}}>{partNumber}</div>
                <div style={{width: '200px', padding: '0 8px'}} className="description-ellipsis text-left">{description}</div>
                <div style={{width: '80px', padding: '0 8px', textAlign: 'center'}}>{salesLog.quantity}</div>
                <div style={{width: '50px', padding: '0 8px', textAlign: 'center'}}>
                    {salesLog.affectsProjections ? 
                        <span style={{fontWeight: 'bold'}}>✓</span> : 
                        ''
                    }
                </div>
                <div style={{width: '200px', padding: '0 8px'}} className="description-ellipsis text-left">{salesLog.reason}</div>
                <div style={{width: '100px', padding: '0 8px'}}>{salesLog.status}</div>
            </div>
        );
    };

    editAssemblyOrPart = (assembly, part, subAssembly, newTab) => {
        let url;
        if (assembly) {
            url = `/admin/assemblies/${assembly.assembly.id}`;
        } else if (subAssembly) {
            url = `/admin/assemblies/${subAssembly.subAssembly.id}`;
        } else if (part) {
            url = `/admin/parts/${part.part.id}`;
        } else {
            return; // No valid item to edit
        }
        
        if (newTab) {
            window.open(url);
        } else {
            globalProps.history.push(url);
        }
    };

    changeQuantity = (event) => {
        this.setState({newLogQuantity: event.target.value});
    };

    changeReason = (event) => {
        this.setState({newLogReason: event.target.value});
    };

    handlePartChange = (event, newValue) => {
        this.setState({newLogPart: !newValue ? null : newValue.value});
    };

    openImportSalesLogs = () => {
        this.setState({
            importSalesLogsOpen: true, 
            savingImportedData: false,
            importFiles: [],
            importDealer: null,
            parsedImportData: [],
            importStep: 'upload',
            importDuplicates: [],
            importErrors: []
        });
    };

    openManualSalesLog = () => {
        this.setState({ manualSalesLogOpen: true });
    };

    handleCloseImportLogs = () => {
        this.setState({importSalesLogsOpen: false});
    };

    // Detect dealer type from CSV headers
    detectDealerType = (headers) => {
        let headerStr;
        if (Array.isArray(headers)) {
            headerStr = headers.join(',').toLowerCase();
        } else if (typeof headers === 'object' && headers !== null) {
            // Handle case where headers is an object (from Papa.parse with header: true)
            headerStr = Object.keys(headers).join(',').toLowerCase();
        } else {
            console.error('Unexpected headers format:', headers);
            return null;
        }
        
        if (headerStr.includes('vendorpartnumber2') && headerStr.includes('po qtyordered')) {
            return 'ECS';
        } else if (headerStr.includes('billing name')) {
            return 'SquareSpace';
        } else if (headerStr.includes('date') && headerStr.includes('source') && headerStr.includes('order #') && headerStr.includes('pn w/rev')) {
            return 'LegacySales';
        }
        
        console.log('No dealer type detected');
        return null;
    };

    // Handle multiple file selection with streaming for large files
    handleFileSelection = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        this.setState({ savingImportedData: true });

        try {
            let dealerType = null;
            let allParsedData = [];
            
            // Process files sequentially to avoid memory issues
            for (const file of files) {
                // Check file size - use streaming for files larger than 5MB
                const useStreaming = file.size > 5 * 1024 * 1024;
                
                let fileData;
                if (useStreaming) {
                    fileData = await streamParseCSV(file);
                } else {
                    fileData = await new Promise((resolve, reject) => {
                        Papa.parse(file, {
                            complete: (results) => {
                                if (results.errors.length > 0) {
                                    reject(new Error(`Parse errors: ${results.errors.map(e => e.message).join(', ')}`));
                                } else {
                                    resolve(results.data);
                                }
                            },
                            error: reject
                        });
                    });
                }
                
                    if (fileData && fileData.length > 0) {
                    const currentDealerType = this.detectDealerType(fileData[0]);
                    
                    if (!dealerType) {
                        dealerType = currentDealerType;
                    } else if (dealerType !== currentDealerType) {
                        window.$alertError("All files must be from the same dealer type (SquareSpace or ECS)");
                        this.setState({ savingImportedData: false });
                        return;
                    }

                    const parsedFile = {
                        name: file.name,
                        dealerType: currentDealerType,
                        data: fileData,
                        orderNumber: file.name.replace('.csv', ''),
                        processed: false
                    };
                    allParsedData.push(parsedFile);
                }
            }

            // Initialize logDate for ECS files (default to current time)
            const ecsOrderLogDates = {};
            allParsedData.forEach(file => {
                if (file.dealerType === 'ECS') {
                    ecsOrderLogDates[file.name] = new Date();
                }
            });

            this.setState({
                importFiles: files,
                importDealer: dealerType,
                parsedImportData: allParsedData,
                ecsOrderLogDates: ecsOrderLogDates,
                savingImportedData: false
            });
        } catch (error) {
            console.error('File parsing error:', error);
            window.$alertError(`Error parsing files: ${error.message}`);
            this.setState({ savingImportedData: false });
        }
    };

    // Helper function to assign line numbers to items within each order
    assignLineNumbers = (items) => {
        // Group items by order number
        const orderGroups = {};
        items.forEach(item => {
            if (!orderGroups[item.orderNumber]) {
                orderGroups[item.orderNumber] = [];
            }
            orderGroups[item.orderNumber].push(item);
        });

        // Assign line numbers within each order group
        Object.keys(orderGroups).forEach(orderNumber => {
            const groupItems = orderGroups[orderNumber];
            
            // Sort by rowIndex for ECS (maintains CSV order), by logDate for Squarespace/LegacySales
            const isEcs = groupItems[0].source === 'ECS';
            const isLegacySales = groupItems[0].source === 'LegacySales';
            if (isEcs) {
                groupItems.sort((a, b) => a.rowIndex - b.rowIndex);
            } else if (isLegacySales) {
                // For LegacySales: Sort by log date (oldest first) to maintain chronological order
                groupItems.sort((a, b) => new Date(a.logDate) - new Date(b.logDate));
            } else {
                groupItems.sort((a, b) => new Date(a.logDate) - new Date(b.logDate));
            }
            
            // Assign line numbers starting from 1
            groupItems.forEach((item, index) => {
                item.lineNumber = index + 1;
            });
        });
        
        // Special handling for blank/0/- order numbers to ensure uniqueness
        const blankOrderItems = items.filter(item => 
            !item.orderNumber || item.orderNumber === '' || item.orderNumber === '-' || item.orderNumber === '0'
        );
        
        if (blankOrderItems.length > 0) {
            // Sort all blank order items by log date to ensure consistent ordering
            blankOrderItems.sort((a, b) => new Date(a.logDate) - new Date(b.logDate));
            
            // Assign unique line numbers across all blank order items
            blankOrderItems.forEach((item, index) => {
                item.lineNumber = index + 1;
            });
        }

        return items;
    };

    // Process files and prepare for review with optimized processing
    processImportFiles = async () => {
        if (this.state.parsedImportData.length === 0) {
            window.$alertWarning("No files to process");
            return;
        }

        this.setState({savingImportedData: true});

        try {
            let allImportItems = [];
            let duplicates = [];
            let errors = [];

            // Process files sequentially to avoid memory issues
            for (const file of this.state.parsedImportData) {
                let items = [];
                if (file.dealerType === 'ECS') {
                    // Get logDate for this ECS order from state
                    const orderLogDate = this.state.ecsOrderLogDates[file.name] || new Date();
                    items = await this.parseEcsFile(file, orderLogDate);
                } else if (file.dealerType === 'SquareSpace') {
                    items = await this.parseSquarespaceFile(file);
                } else if (file.dealerType === 'LegacySales') {
                    items = await this.parseLegacySalesFile(file);
                }
                
                allImportItems = allImportItems.concat(items);
                
                // Clear the file data and items array from memory after processing to reduce memory pressure
                file.data = null;
                items = null; // Help GC
                
                // For very large files, force garbage collection more aggressively
                if (allImportItems.length > 20000) {
                    // Force GC hint and longer delay for very large files
                    await new Promise(resolve => setTimeout(resolve, 200));
                } else {
                    // Give browser time for garbage collection between files
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
            }

            // Assign line numbers to all import items
            allImportItems = this.assignLineNumbers(allImportItems);

            // Create lookup maps for faster duplicate detection
            const salesLogsMap = new Map();
            this.state.salesLogs.forEach(log => {
                const key = `${log.source}-${log.orderNumber}`;
                if (!salesLogsMap.has(key)) {
                    salesLogsMap.set(key, []);
                }
                salesLogsMap.get(key).push(log);
            });

            // Process items in batches to avoid blocking the UI
            // Use smaller batch sizes for very large imports to reduce peak memory usage
            const totalItems = allImportItems.length;
            const batchSize = totalItems > 20000 ? 100 : (totalItems > 10000 ? 250 : 500);
            const processBatch = async (startIndex) => {
                const endIndex = Math.min(startIndex + batchSize, allImportItems.length);
                
                // For very large imports, use longer delays between batches to allow GC
                if (startIndex > 0 && totalItems > 20000 && startIndex % 1000 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 50));
                }
                
                for (let index = startIndex; index < endIndex; index++) {
                    const item = allImportItems[index];
                    
                    // Find existing log entries with the same source and dealer order number
                    const key = `${item.source}-${item.orderNumber}`;
                    const existingLogs = salesLogsMap.get(key) || [];

                    if (existingLogs.length > 0) {
                        // For LegacySales, store the GlobalSequenceNumber from existing logs for later use
                        // This allows us to add new lines to existing orders
                        if (item.source === 'LegacySales' && existingLogs[0].globalSequenceNumber) {
                            item.existingGlobalSequenceNumber = existingLogs[0].globalSequenceNumber;
                        }
                        
                        // For Squarespace and LegacySales imports, use line number for more precise matching
                        let matchingExistingLog = null;
                        
                        if ((item.source === 'SquareSpace' || item.source === 'LegacySales') && item.lineNumber) {
                            // Find existing log with matching line number
                            matchingExistingLog = existingLogs.find(log => log.lineNumber === item.lineNumber);
                        } else {
                            // For ECS and other sources, try multiple matching strategies
                            // First, try to match by product and quantity
                            matchingExistingLog = existingLogs.find(log => {
                                const sameProduct = (
                                    (log.assemblyId && item.assemblyId && log.assemblyId === item.assemblyId) ||
                                    (log.subAssemblyId && item.subAssemblyId && log.subAssemblyId === item.subAssemblyId) ||
                                    (log.partId && item.partId && log.partId === item.partId)
                                );
                                const sameQuantity = log.quantity === item.quantity;
                                return sameProduct && sameQuantity;
                            });
                            
                            // If no match found, check for manually assigned logs with same quantity
                            // (locked logs might have different assemblyId)
                            if (!matchingExistingLog) {
                                matchingExistingLog = existingLogs.find(log => {
                                    return log.isManuallyAssigned && log.quantity === item.quantity;
                                });
                            }
                        }
                        
                        // Check if the specific line (not entire order) is manually assigned
                        const isLineManuallyAssigned = matchingExistingLog && matchingExistingLog.isManuallyAssigned === true;
                        
                        if (isLineManuallyAssigned) {
                            // This specific line is locked - ALWAYS show as locked to force review
                            if (matchingExistingLog) {
                                // Check if this is a status update
                                const existingStatus = normalizeStatus(matchingExistingLog.status);
                                const newStatus = normalizeStatus(item.status);
                                const statusChanged = existingStatus !== newStatus;
                                
                                if (statusChanged) {
                                    // Status update - allow status changes even on locked lines
                                    duplicates.push({
                                        ...item, 
                                        index, 
                                        type: 'status_update',
                                        existingLogId: matchingExistingLog.id,
                                        canOverride: true,
                                        isManuallyAssignedLine: true
                                    });
                                } else {
                                    // Locked line - show as locked regardless of whether product matches
                                    // User must explicitly unlock to update
                                    duplicates.push({
                                        ...item, 
                                        index, 
                                        type: 'locked_line', // Special type for locked lines
                                        existingLogId: matchingExistingLog.id,
                                        canOverride: false, // Cannot override without unlocking
                                        isManuallyAssignedLine: true
                                    });
                                }
                            }
                        } else {
                            // Normal processing for non-manually assigned lines
                            if (matchingExistingLog) {
                                // Line exists - check for updates
                                const existingStatus = normalizeStatus(matchingExistingLog.status);
                                const newStatus = normalizeStatus(item.status);
                                
                                if (existingStatus !== newStatus) {
                                    duplicates.push({
                                        ...item, 
                                        index, 
                                        type: 'status_update',
                                        existingLogId: matchingExistingLog.id,
                                        canOverride: true
                                    });
                                } else {
                                    duplicates.push({
                                        ...item, 
                                        index, 
                                        type: 'exact_duplicate',
                                        existingLogId: matchingExistingLog.id,
                                        canOverride: true
                                    });
                                }
                            } else {
                                // Line doesn't exist - check if it's an extra line that should be added
                                if (item.source === 'SquareSpace' || item.source === 'LegacySales') {
                                    // For Squarespace and LegacySales, if we have a line number that doesn't exist, it's likely an extra line
                                    // that should be added (from an earlier bug or new line)
                                    // For LegacySales, also store the GlobalSequenceNumber from existing logs
                                    const duplicateItem = {
                                        ...item, 
                                        index, 
                                        type: 'new_line',
                                        canOverride: true,
                                        isExtraLine: true
                                    };
                                    if (item.source === 'LegacySales' && existingLogs.length > 0 && existingLogs[0].globalSequenceNumber) {
                                        duplicateItem.existingGlobalSequenceNumber = existingLogs[0].globalSequenceNumber;
                                    }
                                    duplicates.push(duplicateItem);
                                } else {
                                    // For other sources, use the existing logic
                                    duplicates.push({
                                        ...item, 
                                        index, 
                                        type: 'potential_update',
                                        existingLogs: existingLogs
                                    });
                                }
                            }
                        }

                        // Re-evaluate assembly/part selection for items with existing logs
                        // BUT: Don't re-evaluate if this is a locked line - keep the import file's assembly selection
                        if (item.partNumber && !isLineManuallyAssigned) {
                            const orderGlobalSequenceNumber = existingLogs[0].globalSequenceNumber;
                            const reEvaluatedAssembly = this.findBestMatchingAssembly(item.partNumber, orderGlobalSequenceNumber);
                            const reEvaluatedSubAssembly = this.state.subAssemblies.find(x => x.subAssembly.subAssemblyId === item.partNumber);
                            const reEvaluatedPart = this.state.parts.find(x => x.part.partId === item.partNumber);
                            
                            let foundMatch = false;
                            
                            if (reEvaluatedAssembly) {
                                if (item.assemblyId !== reEvaluatedAssembly.assembly.id) {
                                    item.assemblyId = reEvaluatedAssembly.assembly.id;
                                    item.subAssemblyId = null;
                                    item.partId = null;
                                    item.isEdited = true;
                                }
                                foundMatch = true;
                            } else if (reEvaluatedSubAssembly) {
                                if (item.subAssemblyId !== reEvaluatedSubAssembly.subAssembly.id) {
                                    item.assemblyId = null;
                                    item.subAssemblyId = reEvaluatedSubAssembly.subAssembly.id;
                                    item.partId = null;
                                    item.isEdited = true;
                                }
                                foundMatch = true;
                            } else if (reEvaluatedPart) {
                                if (item.partId !== reEvaluatedPart.part.id) {
                                    item.assemblyId = null;
                                    item.subAssemblyId = null;
                                    item.partId = reEvaluatedPart.part.id;
                                    item.isEdited = true;
                                }
                                foundMatch = true;
                            }
                            
                            if (foundMatch && item.isUnmatchingPN) {
                                item.isUnmatchingPN = false;
                                item.isEdited = true;
                            }
                        }
                    }

                    // Check for assembly/part matches
                    if (!item.assemblyId && !item.partId && item.partNumber) {
                        errors.push({...item, index, error: 'Unmatching PN', errorType: 'unmatching_pn'});
                    }
                }
                
                // Process next batch if there are more items
                if (endIndex < allImportItems.length) {
                    await new Promise(resolve => setTimeout(resolve, 0));
                    await processBatch(endIndex);
                } else {
                    // Clear lookup maps after processing to free memory
                    salesLogsMap.clear();
                }
            };
            
            // Start processing in batches
            await processBatch(0);
            
            // Force garbage collection hint for very large imports
            if (totalItems > 20000) {
                await new Promise(resolve => setTimeout(resolve, 200));
                // Clear lookup maps to free memory
                salesLogsMap.clear();
            }

            // Initialize pagination for each file (start at page 1)
            const currentPages = {};
            this.state.importFiles.forEach(file => {
                currentPages[file.name] = 1;
            });
            
            this.setState({
                parsedImportData: allImportItems,
                importDuplicates: duplicates,
                importErrors: errors,
                importStep: 'review',
                savingImportedData: false,
                importReviewCurrentPages: currentPages
            });
        } catch (error) {
            console.error('Error processing import files:', error);
            window.$alertError(`Error processing files: ${error.message}`);
            this.setState({savingImportedData: false});
        }
    };

    // Parse ECS CSV format with async batching
    parseEcsFile = async (file, logDate = null) => {
        const data = file.data.slice(1); // Skip header
        const items = [];
        const assemblyCache = new Map(); // Cache for findBestMatchingAssembly results
        
        // Use provided logDate or default to current time
        const orderLogDate = logDate ? localToUtcForStorage(logDate) : localToUtcForStorage(new Date());

        // Process rows in batches to avoid memory issues and UI blocking
        const batchSize = 25; // Reduced to 25 rows at a time for better memory management
        
        for (let batchStart = 0; batchStart < data.length; batchStart += batchSize) {
            const batchEnd = Math.min(batchStart + batchSize, data.length);
            
            for (let rowIndex = batchStart; rowIndex < batchEnd; rowIndex++) {
                const row = data[rowIndex];
                if (row.length < 6) continue; // Skip incomplete rows
                
                const partNumber = row[5]; // vendorPartNumber2
                const quantity = parseInt(row[9]) || 0; // PO qtyOrdered
                
                if (!partNumber || quantity === 0) continue;

                // Use cached assembly lookup to avoid repeated expensive operations
                let matchingAssembly = assemblyCache.get(partNumber);
                if (matchingAssembly === undefined) {
                    matchingAssembly = this.findBestMatchingAssembly(partNumber);
                    assemblyCache.set(partNumber, matchingAssembly);
                }
                
                const item = {
                    id: uuid(),
                    fileName: file.name,
                    orderNumber: file.orderNumber,
                    partNumber: partNumber,
                    quantity: quantity,
                    logDate: orderLogDate,
                    source: 'ECS',
                    assemblyId: matchingAssembly ? matchingAssembly.assembly.id : null,
                    partId: null,
                    reason: '',
                    affectsProjections: true,
                    status: 'Fulfilled', // ECS imports are always "Fulfilled"
                    rowIndex: rowIndex + 1,
                    isEdited: false,
                    // Flag unmatching part numbers for review
                    isUnmatchingPN: !matchingAssembly
                };
                
                items.push(item);
            }
            
            // Yield to browser every batch to prevent freezing and allow garbage collection
            if (batchEnd < data.length) {
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }
        
        return items;
    };

    // Parse Squarespace CSV format with optimized lookups and async batching
    parseSquarespaceFile = async (file) => {
        const data = file.data.slice(1); // Skip header
        const items = [];
        let previousLogPaid;
        let currentOrderNumber = null;
        let orderLineNumbers = {}; // Track line numbers per order

        // Create lookup maps for faster matching
        const assembliesMap = new Map();
        const subAssembliesMap = new Map();
        const partsMap = new Map();
        const assemblyCache = new Map(); // Cache for findBestMatchingAssembly results
        
        this.state.assemblies.forEach(assembly => {
            assembliesMap.set(assembly.assembly.assemblyId, assembly);
        });
        this.state.subAssemblies.forEach(subAssembly => {
            subAssembliesMap.set(subAssembly.subAssembly.subAssemblyId, subAssembly);
        });
        this.state.parts.forEach(part => {
            partsMap.set(part.part.partId, part);
        });

        let previousLogPaidForScan;
        const orderFirstPaidDate = new Map();
        for (let scanIndex = 0; scanIndex < data.length; scanIndex++) {
            const scanRow = data[scanIndex];
            const scanOrderNumber = scanRow[0];
            if (scanRow[3] !== '' && scanRow[3] != null) {
                previousLogPaidForScan = localToUtcForStorage(new Date(scanRow[3]));
            }
            if (scanOrderNumber && !orderFirstPaidDate.has(scanOrderNumber)) {
                orderFirstPaidDate.set(scanOrderNumber, previousLogPaidForScan);
            }
        }

        const existingOrderGlobalSeq = new Map();
        let maxGlobalSequenceNumber = 0;
        this.state.salesLogs.forEach(log => {
            if (log.globalSequenceNumber && log.globalSequenceNumber > maxGlobalSequenceNumber) {
                maxGlobalSequenceNumber = log.globalSequenceNumber;
            }
            if (log.source === 'SquareSpace' && log.orderNumber && log.globalSequenceNumber) {
                if (!existingOrderGlobalSeq.has(log.orderNumber)) {
                    existingOrderGlobalSeq.set(log.orderNumber, log.globalSequenceNumber);
                }
            }
        });

        const orderGlobalSequenceMap = new Map();
        existingOrderGlobalSeq.forEach((globalSeq, orderNum) => {
            orderGlobalSequenceMap.set(orderNum, globalSeq);
        });

        const newOrdersInFile = [...orderFirstPaidDate.keys()].filter(orderNum => !existingOrderGlobalSeq.has(orderNum));
        newOrdersInFile.sort((a, b) => {
            const dateA = orderFirstPaidDate.get(a);
            const dateB = orderFirstPaidDate.get(b);
            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;
            return new Date(dateA) - new Date(dateB);
        });

        let nextGlobalSequenceNumber = maxGlobalSequenceNumber + 1;
        newOrdersInFile.forEach(orderNum => {
            orderGlobalSequenceMap.set(orderNum, nextGlobalSequenceNumber);
            nextGlobalSequenceNumber++;
        });

        const getAssemblyForSquarespacePart = (partNumber, orderNumber) => {
            const orderGlobalSeq = orderGlobalSequenceMap.get(orderNumber);
            const cacheKey = orderGlobalSeq != null ? `${partNumber}:${orderGlobalSeq}` : partNumber;
            let assembly = assemblyCache.get(cacheKey);
            if (assembly === undefined) {
                assembly = this.findBestMatchingAssembly(partNumber, orderGlobalSeq);
                assemblyCache.set(cacheKey, assembly);
            }
            return assembly;
        };

        // Process rows in batches to avoid memory issues and UI blocking
        const batchSize = 25; // Reduced to 25 rows at a time for better memory management
        
        for (let batchStart = 0; batchStart < data.length; batchStart += batchSize) {
            const batchEnd = Math.min(batchStart + batchSize, data.length);
            
            // Process batch
            for (let rowIndex = batchStart; rowIndex < batchEnd; rowIndex++) {
                const row = data[rowIndex];
                const orderNumber = row[0];
                
                // Check if we're starting a new order
                if (orderNumber !== currentOrderNumber) {
                    currentOrderNumber = orderNumber;
                    orderLineNumbers[orderNumber] = 0; // Initialize line counter for new order
                }
                
                // Fill down logic: only update if current row has a value, otherwise keep previous value
                if (row[3] !== '' && row[3] != null) previousLogPaid = localToUtcForStorage(new Date(row[3]));
                
                // Use row[23] for line item fulfillment status instead of row[4]
                const lineItemStatus = row[23] && row[23] !== '' ? row[23] : 'Fulfilled';
                
                if (row[19] && row[19].indexOf('&') !== -1) {
                    const rowSplits = row[19].split('&');
                    rowSplits.forEach((rowSplit) => {
                        const trimmedPart = rowSplit.trim();
                        
                        // Increment line number for this order
                        orderLineNumbers[orderNumber]++;
                        
                        // Use cached assembly lookup to avoid repeated expensive operations
                        let assembly = getAssemblyForSquarespacePart(trimmedPart, orderNumber);
                        const subAssembly = subAssembliesMap.get(trimmedPart);
                        const part = partsMap.get(trimmedPart);
                        
                        // Include all items, even if no match found
                        const item = {
                            id: uuid(),
                            fileName: file.name,
                            orderNumber: row[0],
                            partNumber: trimmedPart,
                            quantity: parseInt(row[16]) || 0,
                            logDate: previousLogPaid,
                            source: 'SquareSpace',
                            assemblyId: assembly ? assembly.assembly.id : null,
                            subAssemblyId: subAssembly ? subAssembly.subAssembly.id : null,
                            partId: part ? part.part.id : null,
                            reason: '',
                            affectsProjections: true,
                            status: normalizeStatus(lineItemStatus),
                            lineNumber: orderLineNumbers[orderNumber],
                            rowIndex: rowIndex + 1,
                            isEdited: false,
                            isUnmatchingPN: !assembly && !subAssembly && !part
                        };
                        items.push(item);
                    });
                } else if (row[19]) {
                    const trimmedPart = row[19].trim();
                    
                    // Increment line number for this order
                    orderLineNumbers[orderNumber]++;
                    
                    // Use cached assembly lookup to avoid repeated expensive operations
                    let assembly = getAssemblyForSquarespacePart(trimmedPart, orderNumber);
                    const subAssembly = subAssembliesMap.get(trimmedPart);
                    const part = partsMap.get(trimmedPart);
                    
                    const item = {
                        id: uuid(),
                        fileName: file.name,
                        orderNumber: row[0],
                        partNumber: trimmedPart,
                        quantity: parseInt(row[16]) || 0,
                        logDate: previousLogPaid,
                        source: 'SquareSpace',
                        assemblyId: assembly ? assembly.assembly.id : null,
                        subAssemblyId: subAssembly ? subAssembly.subAssembly.id : null,
                        partId: part ? part.part.id : null,
                        reason: '',
                        affectsProjections: true,
                        status: normalizeStatus(lineItemStatus),
                        lineNumber: orderLineNumbers[orderNumber],
                        rowIndex: rowIndex + 1,
                        isEdited: false,
                        isUnmatchingPN: !assembly && !subAssembly && !part
                    };
                    items.push(item);
                }
            }
            
            // Yield to browser every batch to prevent freezing and allow garbage collection
            if (batchEnd < data.length) {
                await new Promise(resolve => setTimeout(resolve, 10));
            }
        }
        
        return items;
    };

    // Parse Legacy Sales CSV format (Date,Source,Order #,Qty,PN,Rev,PN w/Rev,Projections,Projection PN,Projection PN w/Rev)
    parseLegacySalesFile = async (file) => {
        const data = file.data.slice(1); // Skip header
        const items = [];
        let orderLineNumbers = {}; // Track line numbers per order

        // Create lookup maps for faster matching
        const assembliesMap = new Map();
        const subAssembliesMap = new Map();
        const partsMap = new Map();
        const assemblyCache = new Map(); // Cache for findBestMatchingAssembly results
        
        this.state.assemblies.forEach(assembly => {
            assembliesMap.set(assembly.assembly.assemblyId, assembly);
        });
        this.state.subAssemblies.forEach(subAssembly => {
            subAssembliesMap.set(subAssembly.subAssembly.subAssemblyId, subAssembly);
        });
        this.state.parts.forEach(part => {
            partsMap.set(part.part.partId, part);
        });

        // Process rows in batches to avoid memory issues and UI blocking
        // Use smaller batch sizes for very large files to reduce peak memory usage
        const totalRows = data.length;
        const batchSize = totalRows > 20000 ? 10 : (totalRows > 10000 ? 15 : 25);
        
        for (let batchStart = 0; batchStart < data.length; batchStart += batchSize) {
            const batchEnd = Math.min(batchStart + batchSize, data.length);
            
            // Process batch
            for (let rowIndex = batchStart; rowIndex < batchEnd; rowIndex++) {
                const row = data[rowIndex];
                if (row.length < 10) continue; // Skip incomplete rows
                
                // CSV columns: Date,Source,Order #,Qty,PN,Rev,PN w/Rev,Projections,Projection PN,Projection PN w/Rev
                const dateStr = row[0];
                const source = row[1] || 'SquareSpace';
                const orderNumber = row[2];
                const quantity = parseInt(row[3]) || 1;
                const partNumberForMatching = row[4]; // Column 4: PN - use this for matching assemblies/subassemblies/parts
                const revision = row[5] || ''; // Column 5: Rev - revision number
                const partNumberDisplay = row[6] || row[4]; // Column 6: PN w/Rev - use for display, fallback to PN
                
                if (!orderNumber || !partNumberForMatching) continue; // Skip rows without order number or part number
                
                // Parse date
                let logDate;
                if (dateStr) {
                    const parsedDate = new Date(dateStr);
                    if (!isNaN(parsedDate.getTime())) {
                        logDate = localToUtcForStorage(parsedDate);
                    } else {
                        logDate = localToUtcForStorage(new Date());
                    }
                } else {
                    logDate = localToUtcForStorage(new Date());
                }
                
                // Initialize line counter for new order if needed
                if (!orderLineNumbers[orderNumber]) {
                    orderLineNumbers[orderNumber] = 0;
                }
                
                // Increment line number for this order
                orderLineNumbers[orderNumber]++;
                
                // Use cached assembly lookup to avoid repeated expensive operations
                // Use column 4 (PN) for matching, not column 6 (PN w/Rev)
                let assembly = assemblyCache.get(partNumberForMatching);
                if (assembly === undefined) {
                    assembly = this.findBestMatchingAssembly(partNumberForMatching);
                    assemblyCache.set(partNumberForMatching, assembly);
                }
                const subAssembly = subAssembliesMap.get(partNumberForMatching);
                const part = partsMap.get(partNumberForMatching);
                
                // Include all items, even if no match found
                const item = {
                    id: uuid(),
                    fileName: file.name,
                    orderNumber: orderNumber.toString(),
                    partNumber: partNumberDisplay.trim(), // Use PN w/Rev for display
                    revision: revision.trim(), // Store revision separately
                    quantity: quantity,
                    logDate: logDate,
                    source: source,
                    assemblyId: assembly ? assembly.assembly.id : null,
                    subAssemblyId: subAssembly ? subAssembly.subAssembly.id : null,
                    partId: part ? part.part.id : null,
                    reason: '',
                    affectsProjections: true,
                    status: 'Fulfilled',
                    lineNumber: orderLineNumbers[orderNumber],
                    rowIndex: rowIndex + 1,
                    isEdited: false,
                    isUnmatchingPN: !assembly && !subAssembly && !part
                };
                items.push(item);
            }
            
            // Yield to browser every batch to prevent freezing and allow garbage collection
            // Use longer delays for very large files to allow more GC time
            const delay = totalRows > 20000 ? 20 : 10;
            if (batchEnd < data.length) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }
            
            // For very large files, clear cache more frequently to free memory
            if (totalRows > 10000) {
                if (batchStart > 0 && batchStart % 500 === 0) {
                    // Clear assembly cache periodically for large files
                    assemblyCache.clear();
                }
            } else if (batchStart > 0 && batchStart % 1000 === 0) {
                assemblyCache.clear();
            }
        }
        
        // Clear caches before returning
        assemblyCache.clear();
        
        // For very large files, force a longer GC pause
        if (totalRows > 20000) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        return items;
    };

    // Find best matching assembly for ECS import
    findBestMatchingAssembly = (partNumber, orderGlobalSequenceNumber = null) => {
        const matchingAssemblies = this.state.assemblies.filter(x => 
            x.assembly.assemblyId === partNumber && x.assembly.isActive
        );

        if (matchingAssemblies.length === 0) return null;
        if (matchingAssemblies.length === 1) return matchingAssemblies[0];

        const assembliesWithFinalOrder = matchingAssemblies
            .filter(x => x.assembly.finalOrderNumber)
            .map(x => ({
                entry: x,
                globalSeq: this.parseGlobalSequenceNumber(x.assembly.finalOrderNumber)
            }))
            .sort((a, b) => a.globalSeq - b.globalSeq);

        const currentlySellingAssemblies = matchingAssemblies
            .filter(x => !x.assembly.finalOrderNumber)
            .sort((a, b) => {
                const revisionA = a.assembly.revision || '';
                const revisionB = b.assembly.revision || '';
                return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
            });

        if (orderGlobalSequenceNumber != null && !Number.isNaN(Number(orderGlobalSequenceNumber))) {
            const gs = Number(orderGlobalSequenceNumber);

            for (let i = 0; i < assembliesWithFinalOrder.length; i++) {
                const current = assembliesWithFinalOrder[i];
                const previousThreshold = i > 0 ? assembliesWithFinalOrder[i - 1].globalSeq : null;
                const inRange = previousThreshold != null
                    ? gs > previousThreshold && gs <= current.globalSeq
                    : gs <= current.globalSeq;

                if (inRange) {
                    return current.entry;
                }
            }

            if (currentlySellingAssemblies.length > 0) {
                return currentlySellingAssemblies[0];
            }

            if (assembliesWithFinalOrder.length > 0) {
                return assembliesWithFinalOrder[assembliesWithFinalOrder.length - 1].entry;
            }
        }

        if (currentlySellingAssemblies.length > 0) {
            return currentlySellingAssemblies[0];
        }

        if (assembliesWithFinalOrder.length > 0) {
            return assembliesWithFinalOrder[assembliesWithFinalOrder.length - 1].entry;
        }

        return matchingAssemblies.sort((a, b) => {
            const revisionA = a.assembly.revision || '';
            const revisionB = b.assembly.revision || '';
            return revisionA.localeCompare(revisionB, undefined, { numeric: true, sensitivity: 'base' });
        })[0];
    };

    // Helper function to parse GlobalSequenceNumber from FinalOrderNumber
    parseGlobalSequenceNumber = (finalOrderNumber) => {
        if (!finalOrderNumber) return 0;
        
        // FinalOrderNumber format: "GlobalSequenceNumber-LineNumber" (e.g., "10101-2")
        const parts = finalOrderNumber.split('-');
        if (parts.length >= 1) {
            return parseInt(parts[0]) || 0;
        }
        
        return 0;
    };

    // Update import item
    updateImportItem = (index, field, value) => {
        const items = [...this.state.parsedImportData];
        items[index][field] = value;
        items[index].isEdited = true;
        this.setState({parsedImportData: items});
    };

    // Handle assembly/part/subassembly change for import item
    handleImportItemAssemblyChange = (index, newValue) => {
        const items = [...this.state.parsedImportData];
        const itemId = items[index].id;
        let errors = [...this.state.importErrors];
        
        if (newValue) {
            if (newValue.type === 'assembly') {
                items[index].assemblyId = newValue.value;
                items[index].subAssemblyId = null;
                items[index].partId = null;
            } else if (newValue.type === 'subassembly') {
                items[index].assemblyId = null;
                items[index].subAssemblyId = newValue.value;
                items[index].partId = null;
            } else {
                items[index].assemblyId = null;
                items[index].subAssemblyId = null;
                items[index].partId = newValue.value;
            }
            // Clear the unmatching PN flag when a user selects a correct assembly/part
            items[index].isUnmatchingPN = false;
            
            // Mark as manually fixed so we can highlight it light green
            items[index].wasManuallyFixed = true;
            
            // CRITICAL: Remove this item from importErrors since it now has a valid part assignment
            // This will change the row from red to light green (fixed) and update the counts
            errors = errors.filter(error => error.id !== itemId);
        } else {
            items[index].assemblyId = null;
            items[index].subAssemblyId = null;
            items[index].partId = null;
            
            // Clear the fixed flag when selection is removed
            items[index].wasManuallyFixed = false;
            
            // If cleared and has a part number, add back to errors
            if (items[index].partNumber && !errors.some(e => e.id === itemId)) {
                errors.push({...items[index], error: 'Unmatching PN', errorType: 'unmatching_pn'});
            }
        }
        items[index].isEdited = true;
        this.setState({parsedImportData: items, importErrors: errors}, () => {
            // After state update, focus and highlight the quantity field for this row
            if (newValue) {
                setTimeout(() => {
                    // Find the quantity field for this specific row
                    const quantityField = document.querySelector(`input[type="number"][data-row-index="${index}"]`);
                    if (quantityField) {
                        quantityField.focus();
                        quantityField.select();
                    }
                }, 100);
            }
        });
    };

    // Remove individual row from import with optimized performance
    removeImportRow = (rowId) => {
        // Use setTimeout to prevent UI blocking
        setTimeout(() => {
            const items = this.state.parsedImportData.filter(item => item.id !== rowId);
            this.setState({parsedImportData: items});
        }, 0);
    };

    // Unlock a sales log in the import review
    unlockImportRow = (rowId) => {
        const confirm = window.confirm("Are you sure you want to unlock this sales log? This will update the existing sales log with the assembly/part from the import file.");
        if (!confirm) return;

        // Find the item in the import data
        const itemIndex = this.state.parsedImportData.findIndex(item => item.id === rowId);
        if (itemIndex === -1) return;

        const item = this.state.parsedImportData[itemIndex];
        
        // Find the duplicate info for this item
        const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
        if (!duplicateInfo || !duplicateInfo.existingLogId) {
            window.$alertWarning("Could not find existing sales log to update");
            return;
        }

        // Find the existing sales log to determine if it's a revision update
        const existingSalesLog = this.state.salesLogs.find(log => log.id === duplicateInfo.existingLogId);
        if (!existingSalesLog) {
            window.$alertWarning("Could not find existing sales log");
            return;
        }

        // Determine if this is a revision update (same assembly base ID, different revision)
        let isRevisionUpdate = false;
        if (item.assemblyId && existingSalesLog.assemblyId) {
            const newAssembly = this.state.assemblies.find(a => a.assembly.id === item.assemblyId);
            const existingAssembly = this.state.assemblies.find(a => a.assembly.id === existingSalesLog.assemblyId);
            
            if (newAssembly && existingAssembly && 
                newAssembly.assembly.assemblyId === existingAssembly.assembly.assemblyId &&
                newAssembly.assembly.revision !== existingAssembly.assembly.revision) {
                isRevisionUpdate = true;
            }
        }

        // Update the item to mark it as unlocked
        const updatedItems = [...this.state.parsedImportData];
        updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            isManuallyAssigned: false
        };

        // Update the duplicate info based on whether it's a revision update or general update
        const updatedDuplicates = this.state.importDuplicates.map(dup => {
            if (dup.id === item.id) {
                return {
                    ...dup,
                    type: isRevisionUpdate ? 'revision_update' : 'potential_update',
                    isManuallyAssignedLine: false
                };
            }
            return dup;
        });

        this.setState({
            parsedImportData: updatedItems,
            importDuplicates: updatedDuplicates
        });
        window.$alertSuccess(isRevisionUpdate ? 
            "Sales log will be unlocked and updated to new revision during import" : 
            "Sales log will be unlocked and updated during import");
    };

    // Remove entire file from import
    removeImportFile = (fileName) => {
        const items = this.state.parsedImportData.filter(item => item.fileName !== fileName);
        const files = this.state.importFiles.filter(file => file.name !== fileName);
        // Clean up logDate state for removed ECS file
        const updatedEcsOrderLogDates = {...this.state.ecsOrderLogDates};
        delete updatedEcsOrderLogDates[fileName];
        this.setState({parsedImportData: items, importFiles: files, ecsOrderLogDates: updatedEcsOrderLogDates});
    };

    // Final import after review
    importSalesLogs = () => {
        const self = this;
        if (this.state.parsedImportData.length === 0) {
            window.$alertWarning("No data to import");
            return;
        }

        self.setState({savingImportedData: true});

        // Filter items based on new duplicate detection logic
        let validItems = this.state.parsedImportData.filter(item => {
            const hasAssemblyOrSubAssemblyOrPart = item.assemblyId || item.subAssemblyId || item.partId;
            
            // Find if this item has any duplicate information
            const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
            
            if (duplicateInfo) {
                if (duplicateInfo.type === 'exact_duplicate') {
                    // Check for both global and individual overrides
                    const globalOverride = this.state.importOverrideExactDuplicates;
                    const individualOverride = this.state.importIndividualLineOverrides[item.id];
                    const shouldOverride = globalOverride || individualOverride;
                    
                    // Skip exact duplicates unless override is enabled (global or individual)
                    return shouldOverride && hasAssemblyOrSubAssemblyOrPart;
                } else if (duplicateInfo.type === 'revision_update') {
                    // Revision updates are automatically included - no override required
                    return hasAssemblyOrSubAssemblyOrPart;
                } else if (duplicateInfo.type === 'status_update') {
                    // Status updates are automatically included - no override required
                    return hasAssemblyOrSubAssemblyOrPart;
                } else if (duplicateInfo.type === 'potential_update') {
                    // Allow potential updates to go through (same source and order, but different product or quantity)
                    return hasAssemblyOrSubAssemblyOrPart;
                }
            }
            
            // Include items that have been corrected from unmatching PN status
            const wasUnmatchingPNButNowCorrected = item.isUnmatchingPN && hasAssemblyOrSubAssemblyOrPart;
            
            return (hasAssemblyOrSubAssemblyOrPart || wasUnmatchingPNButNowCorrected);
        });

        if (validItems.length === 0) {
            window.$alertWarning("No valid items to import");
            self.setState({savingImportedData: false});
            return;
        }

        // Group items by order number and sort within each group
        const orderGroups = {};
        validItems.forEach(item => {
            if (!orderGroups[item.orderNumber]) {
                orderGroups[item.orderNumber] = [];
            }
            orderGroups[item.orderNumber].push(item);
        });

        // Sort items within each order group
        // For ECS: Maintain CSV row order (all rows are same order number)
        // For Squarespace: Sort by log date (newest first) for same order numbers
        const sortedItems = [];
        Object.keys(orderGroups).forEach(orderNumber => {
            const groupItems = orderGroups[orderNumber];
            
            // Determine if this is ECS, Squarespace, or LegacySales based on the first item's source
            const isEcs = groupItems[0].source === 'ECS';
            const isLegacySales = groupItems[0].source === 'LegacySales';
            
            if (isEcs) {
                // For ECS: Maintain CSV row order (all rows are same order number)
                groupItems.sort((a, b) => a.rowIndex - b.rowIndex);
            } else if (isLegacySales) {
                // For LegacySales: Sort by log date (oldest first) to maintain chronological order
                groupItems.sort((a, b) => new Date(a.logDate) - new Date(b.logDate));
            } else {
                // For Squarespace: Sort by log date (newest first) for same order numbers
                groupItems.sort((a, b) => new Date(b.logDate) - new Date(a.logDate));
            }
            
            sortedItems.push(...groupItems);
        });

        // Separate items that need to be created vs updated
        const itemsToCreate = [];
        const itemsToUpdate = [];
        const processedOrderUpdates = new Set(); // Track which orders have been processed for status updates

        sortedItems.forEach(item => {
            const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
            
            if (duplicateInfo && (duplicateInfo.type === 'revision_update' || duplicateInfo.type === 'status_update')) {
                // For revision updates and status updates, we need to update the existing sales log
                // But only if it's not a manually assigned line (revision updates are blocked for manually assigned lines)
                if (!duplicateInfo.isManuallyAssignedLine || duplicateInfo.type === 'status_update') {
                    itemsToUpdate.push({
                        ...item,
                        existingLogId: duplicateInfo.existingLogId
                    });
                }
                // Skip revision updates for manually assigned lines
            } else if (!duplicateInfo || !duplicateInfo.isManuallyAssignedLine) {
                // For all other items, create new sales logs (but not for manually assigned lines)
                // For LegacySales items with existingGlobalSequenceNumber, include it to add lines to existing order
                const itemToCreate = {...item};
                if (item.source === 'LegacySales' && duplicateInfo && duplicateInfo.existingGlobalSequenceNumber) {
                    itemToCreate.globalSequenceNumber = duplicateInfo.existingGlobalSequenceNumber;
                }
                itemsToCreate.push(itemToCreate);
            }
            // Skip creating/updating items for manually assigned orders (except status updates)
        });

        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        
        // First, update existing sales logs for revision changes and status updates
        const updatePromises = [];
        
        itemsToUpdate.forEach(item => {
            // Update individual line
            const updatePayload = {
                ...item,
                id: item.existingLogId,
                partId: item.partId,
                assemblyId: item.assemblyId,
                subAssemblyId: item.subAssemblyId,
                status: item.status, // Include status field to update from pending to fulfilled
                lineNumber: item.lineNumber, // Include line number
                // Keep other fields from the existing log unchanged
                userId: AuthenticationService.getInstance().getUserId()
            };
            updatePromises.push(assemblyService.updateSalesLog(updatePayload));
        });

        // Then, create new sales logs for new items
        const createPromise = assemblyService.importSalesLogs(itemsToCreate);

        // Execute both operations and wait for ALL to complete
        Promise.all([...updatePromises, createPromise]).then((results) => {
            const totalProcessed = itemsToCreate.length + itemsToUpdate.length;
            const createMessage = itemsToCreate.length > 0 ? `${itemsToCreate.length} new sales logs created` : '';
            
            // Calculate total updates
            const totalUpdates = itemsToUpdate.length;
            
            const updateMessage = totalUpdates > 0 ? `${totalUpdates} existing sales logs updated` : '';
            const message = [createMessage, updateMessage].filter(msg => msg).join(' and ');
            
            window.$alertSuccess(`${message} successfully`);
            self.setState({
                importSalesLogsOpen: false, 
                savingImportedData: false,
                importStep: 'upload'
            });
            
            // Wait for database operations to complete, then refresh page
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        }).catch((error) => {
            window.$alertError("Error importing sales logs: " + error.message);
            self.setState({savingImportedData: false});
        });
    };

    // Pagination controls for import review
    changeImportReviewPage = (fileName, newPage) => {
        this.setState(prevState => ({
            importReviewCurrentPages: {
                ...prevState.importReviewCurrentPages,
                [fileName]: newPage
            }
        }));
    };

    // Toggle filter for showing only problematic rows
    toggleImportReviewProblemsOnly = () => {
        this.setState(prevState => {
            const newShowProblemsOnly = !prevState.importReviewShowProblemsOnly;
            // Reset all pages to 1 when toggling filter
            const resetPages = {};
            this.state.importFiles.forEach(file => {
                resetPages[file.name] = 1;
            });
            return {
                importReviewShowProblemsOnly: newShowProblemsOnly,
                importReviewCurrentPages: resetPages
            };
        });
    };

    toggleImportReviewHideExactDuplicates = () => {
        this.setState(prevState => {
            const newHideExactDuplicates = !prevState.importReviewHideExactDuplicates;
            // Reset all pages to 1 when toggling filter
            const resetPages = {};
            this.state.importFiles.forEach(file => {
                resetPages[file.name] = 1;
            });
            return {
                importReviewHideExactDuplicates: newHideExactDuplicates,
                importReviewCurrentPages: resetPages
            };
        });
    };

    resetImport = () => {
        this.setState({
            importFiles: [],
            importDealer: null,
            parsedImportData: [],
            importStep: 'upload',
            importDuplicates: [],
            importErrors: [],
            importOverrideExactDuplicates: false,
            importIndividualLineOverrides: {},
            importReviewCurrentPages: {},
            importReviewShowProblemsOnly: false,
            ecsOrderLogDates: {}
        });
    };

    changeSource = (newValue) => {
        this.setState({newLogSource: newValue});
    };

    // Handle individual line override toggle for exact duplicates
    toggleIndividualLineOverride = (itemId) => {
        this.setState(prevState => ({
            importIndividualLineOverrides: {
                ...prevState.importIndividualLineOverrides,
                [itemId]: !prevState.importIndividualLineOverrides[itemId]
            }
        }));
    };

    changeOrderNumber = (event) => {
        this.setState({newLogOrderNumber: event.target.value});
    };

    changeStatus = (event) => {
        this.setState({newLogStatus: normalizeStatus(event.target.value)});
    };

    addManualLog = () => {
        const self = this;
        if (!this.state.newLogPart || this.state.newLogSource === '') return window.$alertWarning("You must select a source and assembly / subassembly / part for this log");

        const assembly = self.state.assemblies.find(x => x.assembly.id === this.state.newLogPart);
        const subAssembly = self.state.subAssemblies.find(x => x.subAssembly.id === this.state.newLogPart);
        const part = self.state.parts.find(x => x.part.id === this.state.newLogPart);

        self.setState({savingImportedData: true});
        const payload = {
            id: uuid(),
            source: this.state.newLogSource,
            quantity: parseNumberInputValue(this.state.newLogQuantity),
            reason: this.state.newLogReason ?? '',
            orderNumber: this.state.newLogOrderNumber,
            partId: part ? part.part.id : null,
            assemblyId: assembly ? assembly.assembly.id : null,
            subAssemblyId: subAssembly ? subAssembly.subAssembly.id : null,
            logDate: this.state.newLogDateManuallyChanged
                ? localToUtcForStorage(this.state.newLogDate)
                : localToUtcForStorage(new Date()),
            affectsProjections: this.state.newLogAffectsProjections,
            status: normalizeStatus(this.state.newLogStatus),
            customerEmail: this.state.newLogCustomerEmail || null
        };
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.importSalesLogs([payload]).then(() => {
            window.$alertSuccess("Sales log created");
            self.setState({manualSalesLogOpen: false, savingImportedData: false});
            self.loadData();
        });
    };

    editLog = (salesLog) => {
        const editingLog = salesLog;
        
        // Set the current assembly/part for the autocomplete dropdown
        let currentAssemblyPart = null;
        if (editingLog.assemblyId) {
            const assembly = this.state.assemblies.find(x => x.assembly.id === editingLog.assemblyId);
            if (assembly) {
                currentAssemblyPart = {
                    title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${assembly.assembly.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies) ? ' 🛒' : ''}`,
                    value: assembly.assembly.id,
                    type: 'assembly',
                    partId: assembly.assembly.assemblyId,
                    revision: assembly.assembly.revision,
                    description: assembly.assembly.description,
                    currentlyPackaging: assembly.assembly.currentlyPackaging,
                    currentlySelling: isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies)
                };
            }
        } else if (editingLog.subAssemblyId) {
            const subAssembly = this.state.subAssemblies.find(x => x.subAssembly.id === editingLog.subAssemblyId);
            if (subAssembly) {
                currentAssemblyPart = {
                    title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                    value: subAssembly.subAssembly.id,
                    type: 'subassembly',
                    partId: subAssembly.subAssembly.subAssemblyId,
                    revision: subAssembly.subAssembly.revision,
                    description: subAssembly.subAssembly.description,
                    currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
                };
            }
        } else if (editingLog.partId) {
            const part = this.state.parts.find(x => x.part.id === editingLog.partId);
            if (part) {
                currentAssemblyPart = {
                    title: `${part.part.partId} rev ${part.part.revision}`,
                    value: part.part.id,
                    type: 'part',
                    partId: part.part.partId,
                    revision: part.part.revision,
                    part: part.part,
                    currentlyPurchasing: false
                };
            }
        }
        
        this.setState({
            editingLog, 
            editingLogOriginal: JSON.parse(JSON.stringify(salesLog)), // Store original for comparison
            editLogIsReceived: editingLog.receivedDate !== null, 
            editLogOpen: true,
            editLogAssemblyPart: currentAssemblyPart
        });
    };

    editSource = (newValue) => {
        const editingLog = {...this.state.editingLog, source: newValue};
        this.setState({editingLog});
    };

    editOrderNumber = (event) => {
        const editingLog = {...this.state.editingLog, orderNumber: event.target.value};
        this.setState({editingLog});
    };

    editQuantity = (event) => {
        const editingLog = {...this.state.editingLog, quantity: event.target.value};
        this.setState({editingLog});
    }

    editReason = (event) => {
        const editingLog = {...this.state.editingLog, reason: event.target.value};
        this.setState({editingLog});
    }

    editCustomerName = (event) => {
        const editingLog = {...this.state.editingLog, customerName: event.target.value};
        this.setState({editingLog});
    }

    editShippingAddress = (event) => {
        const editingLog = {...this.state.editingLog, shippingAddress: event.target.value};
        this.setState({editingLog});
    };

    editCustomerEmail = (event) => {
        const editingLog = {...this.state.editingLog, customerEmail: event.target.value};
        this.setState({editingLog});
    };

    editAffectsProjections = (event) => {
        const editingLog = {...this.state.editingLog, affectsProjections: event.target.checked};
        this.setState({editingLog});
    };

    editStatus = (event) => {
        const editingLog = {...this.state.editingLog, status: normalizeStatus(event.target.value)};
        this.setState({editingLog});
    };

    editAssemblyPartChange = (event, newValue) => {
        let editingLog = {...this.state.editingLog};
        if (newValue) {
            if (newValue.type === 'assembly') {
                editingLog.assemblyId = newValue.value;
                editingLog.subAssemblyId = null;
                editingLog.partId = null;
            } else if (newValue.type === 'subassembly') {
                editingLog.assemblyId = null;
                editingLog.subAssemblyId = newValue.value;
                editingLog.partId = null;
            } else {
                editingLog.assemblyId = null;
                editingLog.subAssemblyId = null;
                editingLog.partId = newValue.value;
            }
        } else {
            editingLog.assemblyId = null;
            editingLog.subAssemblyId = null;
            editingLog.partId = null;
        }
        this.setState({editingLog, editLogAssemblyPart: newValue});
    };

    handleCloseManualLog = () => {
        this.setState({ manualSalesLogOpen: false });
    };

    handleManualSalesLogCreated = (validEntries) => {
        this.showSalesLogCopyTable(validEntries);
        this.setState({ manualSalesLogOpen: false });
        this.loadData();
    };

    showSalesLogCopyTable = (salesLogEntries) => {
        const copyTableData = salesLogEntries.map(entry => {
            const assembly = this.state.assemblies.find(x => x.assembly.id === entry.part.value);
            const subAssembly = this.state.subAssemblies.find(x => x.subAssembly.id === entry.part.value);
            const part = this.state.parts.find(x => x.part.id === entry.part.value);
            
            let partNumber = 'Unknown';
            let revision = '';
            
            if (assembly) {
                partNumber = assembly.assembly.assemblyId;
                revision = assembly.assembly.revision;
            } else if (subAssembly) {
                partNumber = subAssembly.subAssembly.subAssemblyId;
                revision = subAssembly.subAssembly.revision;
            } else if (part) {
                partNumber = part.part.partId;
                revision = part.part.revision;
            }
            
            return {
                partNumber: partNumber,
                revision: revision,
                quantity: entry.quantity
            };
        });
        
        this.setState({
            showCopyTable: true,
            copyTableData
        });
    };

    copyTableToClipboard = async () => {
        // Create HTML table string
        const tableHtml = `
            <table style="border-collapse: collapse; border: 1px solid black; font-family: Arial, sans-serif; font-size: 12px;">
                <thead>
                    <tr>
                        <th style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: center;">Quantity</th>
                        <th style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: left;">Item</th>
                    </tr>
                </thead>
                <tbody>
                    ${(this.state.copyTableData || []).map(item => 
                        `<tr>
                            <td style="border: 1px solid black; padding: 8px; text-align: center;">${item.quantity}</td>
                            <td style="border: 1px solid black; padding: 8px; text-align: left;">${item.partNumber} rev ${item.revision}</td>
                        </tr>`
                    ).join('')}
                </tbody>
            </table>
        `;

        // Create plain text version as fallback
        const plainText = `Quantity\tItem\n${(this.state.copyTableData || []).map(item => 
            `${item.quantity}\t${item.partNumber} rev ${item.revision}`
        ).join('\n')}`;

        try {
            // Try to copy HTML to clipboard
            if (window.ClipboardItem) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([tableHtml], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' })
                    })
                ]);
                window.$alertSuccess('Table copied to clipboard!');
            } else {
                // Fallback if ClipboardItem is not available
                await navigator.clipboard.writeText(plainText);
                window.$alertSuccess('Table copied to clipboard as text!');
            }
        } catch (err) {
            // Fallback to plain text if HTML clipboard fails
            try {
                await navigator.clipboard.writeText(plainText);
                window.$alertSuccess('Table copied to clipboard as text!');
            } catch (fallbackErr) {
                window.$alertError('Failed to copy to clipboard. Please copy manually.');
                console.error('Clipboard error:', fallbackErr);
            }
        }
    };

    handleCloseEditLog = () => {
        this.setState({editingLog: null, editingLogOriginal: null, editLogOpen: false});
    };

    hasEditLogUnsavedChanges = () => {
        if (!this.state.editingLog || !this.state.editingLogOriginal) return false;
        
        const current = this.state.editingLog;
        const original = this.state.editingLogOriginal;
        
        return (
            current.source !== original.source ||
            current.orderNumber !== original.orderNumber ||
            current.quantity !== original.quantity ||
            current.reason !== original.reason ||
            current.assemblyId !== original.assemblyId ||
            current.subAssemblyId !== original.subAssemblyId ||
            current.partId !== original.partId ||
            current.affectsProjections !== original.affectsProjections ||
            current.lineNumber !== original.lineNumber ||
            (current.customerName || '') !== (original.customerName || '') ||
            (current.shippingAddress || '') !== (original.shippingAddress || '') ||
            (current.customerEmail || '') !== (original.customerEmail || '') ||
            new Date(current.logDate).getTime() !== new Date(original.logDate).getTime()
        );
    };

    updateSalesLog = () => {
        const self = this;
        self.setState({savingSalesLog: true});
        const payload = {
            id: this.state.editingLog.id,
            source: this.state.editingLog.source,
            quantity: parseNumberInputValue(this.state.editingLog.quantity),
            reason: this.state.editingLog.reason,
            partId: this.state.editingLog.partId,
            assemblyId: this.state.editingLog.assemblyId,
            subAssemblyId: this.state.editingLog.subAssemblyId,
            orderNumber: this.state.editingLog.orderNumber,
            logDate: this.state.editingLog.logDate,
            affectsProjections: this.state.editingLog.affectsProjections,
            userId: AuthenticationService.getInstance().getUserId(),
            status: this.state.editingLog.status,
            lineNumber: this.state.editingLog.lineNumber,
            customerName: this.state.editingLog.customerName || null,
            shippingAddress: this.state.editingLog.shippingAddress || null,
            customerEmail: this.state.editingLog.customerEmail || null
            // isManuallyAssigned will be determined by the backend based on whether assembly/part changed
        };
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.updateSalesLog(payload).then(() => {
            window.$alertSuccess("Sales log updated");
            
            // Directly update the sales log in the state instead of reloading all data
            const updatedSalesLogs = self.state.salesLogs.map(log => 
                log.id === payload.id ? { ...log, ...payload } : log
            );
            
            // Clear memoization cache to ensure fresh data
            self.memoizedGetSortedAndFilteredSalesLogs.clear();
            self.memoizedGetFilteredSalesLogs.clear();
            
            self.setState({
                editLogOpen: false, 
                savingSalesLog: false, 
                dataRefreshKey: self.state.dataRefreshKey + 1,
                salesLogs: updatedSalesLogs
            }, () => {
                // Force a re-render by updating the dataRefreshKey again
                setTimeout(() => {
                    self.setState({ dataRefreshKey: self.state.dataRefreshKey + 1 });
                }, 50);
            });
        });
    };

    deleteSalesLog = () => {
        const confirm = window.confirm("Are you sure you want to delete this sales log?");
        if (!confirm) return;

        const self = this;
        self.setState({savingImportedData: true});
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.deleteSalesLog(this.state.editingLog).then(() => {
            window.$alertSuccess("Sales log deleted");
            self.setState({editLogOpen: false, savingImportedData: false});
            self.loadData();
        });
    };

    deleteSelectedSalesLogs = () => {
        const count = this.state.selectedSalesLogs.length;
        if (count === 0) {
            window.$alertError('Please select at least one sales log to delete');
            return;
        }
        const confirm = window.confirm(`Are you sure you want to delete ${count} sales log(s)? This cannot be undone.`);
        if (!confirm) return;

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const logsToDelete = this.state.selectedSalesLogs
            .map(id => this.state.salesLogs.find(log => log.id === id))
            .filter(log => log != null);
        if (logsToDelete.length === 0) {
            self.setState({ selectedSalesLogs: [] });
            return;
        }
        self.setState({ savingImportedData: true });
        Promise.all(logsToDelete.map(log => assemblyService.deleteSalesLog(log)))
            .then(() => {
                window.$alertSuccess(`${logsToDelete.length} sales log(s) deleted`);
                self.setState({ selectedSalesLogs: [], savingImportedData: false });
                self.loadData();
            })
            .catch((err) => {
                console.error('Error deleting sales logs:', err);
                window.$alertError('Error deleting one or more sales logs');
                self.setState({ savingImportedData: false });
                self.loadData();
            });
    };

    getPackageableSalesLogs = () => {
        return this.state.salesLogs.filter(log =>
            this.state.selectedSalesLogs.includes(log.id) &&
            (log.assemblyId || log.subAssemblyId)
        );
    };

    packageSelectedSalesLogs = () => {
        const packageableLogs = this.getPackageableSalesLogs();
        const selectedCount = this.state.selectedSalesLogs.length;

        if (selectedCount === 0) {
            window.$alertError('Please select at least one sales log');
            return;
        }
        if (packageableLogs.length === 0) {
            window.$alertWarning('None of the selected lines are assemblies or subassemblies. Part-only lines cannot be packaged.');
            return;
        }

        const skippedCount = selectedCount - packageableLogs.length;
        let confirmMessage = `Create ${packageableLogs.length} package log(s) for the selected line(s)?`;
        if (skippedCount > 0) {
            confirmMessage += ` (${skippedCount} part-only line(s) will be skipped)`;
        }
        if (!window.confirm(confirmMessage)) {
            return;
        }

        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const userId = AuthenticationService.getInstance().getUserId();
        const logDate = toUtcForStorage(new Date());

        self.setState({ packagingSelectedSalesLogs: true });
        Promise.all(packageableLogs.map(salesLog => {
            const orderRef = salesLog.compositeOrderNumber || salesLog.orderNumber;
            const reason = orderRef
                ? `From sales order ${orderRef}`
                : 'Packaged from sales log';

            return assemblyService.createPackageLog({
                id: uuid(),
                assemblyId: salesLog.assemblyId || null,
                subAssemblyId: salesLog.subAssemblyId || null,
                quantity: salesLog.quantity,
                logDate,
                checkedBom: false,
                reason,
                userId
            });
        })).then(() => {
            let message = `${packageableLogs.length} package log${packageableLogs.length > 1 ? 's' : ''} created`;
            if (skippedCount > 0) {
                message += ` (${skippedCount} part-only line(s) skipped)`;
            }
            window.$alertSuccess(message);
            self.setState({ packagingSelectedSalesLogs: false, selectedSalesLogs: [] });
        }).catch((error) => {
            window.$alertError('Error creating package logs: ' + (error.message || 'Unknown error'));
            self.setState({ packagingSelectedSalesLogs: false });
        });
    };

    unlockSalesLog = () => {
        const confirm = window.confirm("Are you sure you want to unlock this sales log? This will allow it to be automatically reassigned by the system.");
        if (!confirm) return;

        const self = this;
        self.setState({savingSalesLog: true});
        
        // Create a copy of the editing log with isManuallyAssigned set to false
        const unlockedLog = {
            ...this.state.editingLog,
            isManuallyAssigned: false
        };
        
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.updateSalesLog(unlockedLog).then(() => {
            window.$alertSuccess("Sales log unlocked");
            self.setState({savingSalesLog: false});
            self.loadData();
            setTimeout(() => window.location.reload(), 500);
        }).catch((error) => {
            console.error('Error unlocking sales log:', error);
            window.$alertError("Failed to unlock sales log");
            self.setState({savingSalesLog: false});
        });
    };

    unlockSalesOrderLog = (log) => {
        if (!window.confirm("Are you sure you want to unlock this sales log? This will allow it to be automatically reassigned by the system.")) return;
        const self = this;
        self.setState({ unlockingSalesOrderLogId: log.id });
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const unlockedLog = { ...log, isManuallyAssigned: false };
        assemblyService.updateSalesLog(unlockedLog).then(() => {
            window.$alertSuccess("Sales log unlocked");
            const updatedLogs = self.state.editingSalesOrderLogs.map(l => l.id === log.id ? { ...l, isManuallyAssigned: false } : l);
            self.setState({ editingSalesOrderLogs: updatedLogs, unlockingSalesOrderLogId: null, salesOrderDataNeedsReload: true });
        }).catch((error) => {
            console.error('Error unlocking sales log:', error);
            window.$alertError("Failed to unlock sales log");
            self.setState({ unlockingSalesOrderLogId: null });
        });
    };

    // Multiline Sales Order Edit Methods
    buildEditingSalesOrderLogsFromOrderLogs = (salesLogsForOrder) => {
        return salesLogsForOrder.map(log => {
            let currentAssemblyPart = null;
            
            if (log.assemblyId) {
                const assembly = this.state.assemblies.find(x => x.assembly.id === log.assemblyId);
                if (assembly) {
                    currentAssemblyPart = {
                        title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${assembly.assembly.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies) ? ' 🛒' : ''}`,
                        value: assembly.assembly.id,
                        type: 'assembly',
                        partId: assembly.assembly.assemblyId,
                        revision: assembly.assembly.revision,
                        description: assembly.assembly.description,
                        currentlyPackaging: assembly.assembly.currentlyPackaging,
                        currentlySelling: isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies)
                    };
                }
            } else if (log.subAssemblyId) {
                const subAssembly = this.state.subAssemblies.find(x => x.subAssembly.id === log.subAssemblyId);
                if (subAssembly) {
                    currentAssemblyPart = {
                        title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                        value: subAssembly.subAssembly.id,
                        type: 'subassembly',
                        partId: subAssembly.subAssembly.subAssemblyId,
                        revision: subAssembly.subAssembly.revision,
                        description: subAssembly.subAssembly.description,
                        currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
                    };
                }
            } else if (log.partId) {
                const part = this.state.parts.find(x => x.part.id === log.partId);
                if (part) {
                    currentAssemblyPart = {
                        title: `${part.part.partId} rev ${part.part.revision}`,
                        value: part.part.id,
                        type: 'part',
                        partId: part.part.partId,
                        revision: part.part.revision,
                        description: part.part.description,
                        part: part.part
                    };
                }
            }

            return {
                ...log,
                currentAssemblyPart,
                tempId: log.id
            };
        });
    };

    openMultilineEditDrawer = (globalSequenceNumber) => {
        const salesLogsForOrder = this.state.salesLogs.filter(log =>
            log.globalSequenceNumber === globalSequenceNumber
        );
        if (salesLogsForOrder.length === 0) {
            window.$alertWarning("No sales logs found for this order");
            return;
        }
        const editingSalesOrderLogs = this.buildEditingSalesOrderLogsFromOrderLogs(salesLogsForOrder);
        this.setState({
            editSalesOrderOpen: true,
            editingSalesOrderLogs,
            editingSalesOrderGlobalSeq: globalSequenceNumber,
            deletedSalesLogIds: [],
            pendingNewSalesLogs: []
        });
    };

    handleCloseMultilineEdit = () => {
        const needsReload = this.state.salesOrderDataNeedsReload;
        this.setState({
            editSalesOrderOpen: false,
            editingSalesOrderLogs: [],
            editingSalesOrderGlobalSeq: null,
            deletedSalesLogIds: [],
            pendingNewSalesLogs: [],
            unlockingSalesOrderLogId: null,
            salesOrderDataNeedsReload: false,
            showCopyTableLineSelector: false,
            copyTableSelectedIds: []
        });
        if (needsReload) {
            this.loadData();
        }
    };

    openCopyTableLineSelector = () => {
        const logs = this.state.editingSalesOrderLogs;
        const newLineIds = logs.filter(l => l.isNew).map(l => l.tempId);
        const defaultIds = newLineIds.length > 0 ? newLineIds : logs.map(l => l.tempId);
        this.setState({ showCopyTableLineSelector: true, copyTableSelectedIds: defaultIds });
    };

    toggleCopyTableLine = (tempId) => {
        this.setState(prev => ({
            copyTableSelectedIds: prev.copyTableSelectedIds.includes(tempId)
                ? prev.copyTableSelectedIds.filter(id => id !== tempId)
                : [...prev.copyTableSelectedIds, tempId]
        }));
    };

    submitCopyTableFromOrder = () => {
        const logs = this.state.editingSalesOrderLogs.filter(l =>
            this.state.copyTableSelectedIds.includes(l.tempId) &&
            (l.assemblyId || l.subAssemblyId || l.partId)
        );
        const entries = logs.map(log => ({
            part: { value: log.assemblyId || log.subAssemblyId || log.partId },
            quantity: log.quantity
        }));
        if (entries.length === 0) {
            window.$alertWarning('Select at least one line with an assembly/part to show the copy table.');
            return;
        }
        this.showSalesLogCopyTable(entries);
    };

    handleSalesOrderLogChange = (index, field, value) => {
        const updatedLogs = [...this.state.editingSalesOrderLogs];
        updatedLogs[index] = {
            ...updatedLogs[index],
            [field]: value
        };
        this.setState({ editingSalesOrderLogs: updatedLogs });
    };

    handleSalesOrderPartChange = (index, newValue) => {
        const updatedLogs = [...this.state.editingSalesOrderLogs];
        
        if (newValue && newValue.type === 'assembly') {
            updatedLogs[index] = {
                ...updatedLogs[index],
                assemblyId: newValue.value,
                subAssemblyId: null,
                partId: null,
                currentAssemblyPart: newValue
            };
        } else if (newValue && newValue.type === 'subassembly') {
            updatedLogs[index] = {
                ...updatedLogs[index],
                assemblyId: null,
                subAssemblyId: newValue.value,
                partId: null,
                currentAssemblyPart: newValue
            };
        } else if (newValue && newValue.type === 'part') {
            updatedLogs[index] = {
                ...updatedLogs[index],
                assemblyId: null,
                subAssemblyId: null,
                partId: newValue.value,
                currentAssemblyPart: newValue
            };
        } else {
            updatedLogs[index] = {
                ...updatedLogs[index],
                assemblyId: null,
                subAssemblyId: null,
                partId: null,
                currentAssemblyPart: null
            };
        }
        
        this.setState({ editingSalesOrderLogs: updatedLogs });
    };

    handleDeleteSalesOrderLog = (index) => {
        const logToDelete = this.state.editingSalesOrderLogs[index];
        
        // If it's an existing log (has an id that's not a temp id), mark for deletion
        if (logToDelete.id && !logToDelete.isNew) {
            this.setState({
                deletedSalesLogIds: [...this.state.deletedSalesLogIds, logToDelete.id],
                editingSalesOrderLogs: this.state.editingSalesOrderLogs.filter((_, i) => i !== index)
            });
        } else {
            // If it's a new log, just remove it from the array
            this.setState({
                editingSalesOrderLogs: this.state.editingSalesOrderLogs.filter((_, i) => i !== index)
            });
        }
    };

    handleAddNewSalesOrderLog = () => {
        // Use the existing order's properties from the first log (or defaults if somehow empty)
        const existingLog = this.state.editingSalesOrderLogs.length > 0 ? this.state.editingSalesOrderLogs[0] : null;
        
        const newLog = {
            id: uuid(),
            globalSequenceNumber: this.state.editingSalesOrderGlobalSeq,
            logDate: existingLog ? existingLog.logDate : new Date(),
            source: existingLog ? existingLog.source : 'Customer Service',
            orderNumber: existingLog ? existingLog.orderNumber : '',
            customerName: existingLog ? existingLog.customerName : null,
            shippingAddress: existingLog ? existingLog.shippingAddress : null,
            customerEmail: existingLog ? existingLog.customerEmail : null,
            assemblyId: null,
            subAssemblyId: null,
            partId: null,
            quantity: 1,
            reason: '',
            status: 'Fulfilled',
            affectsProjections: true,
            lineNumber: null,
            currentAssemblyPart: null,
            isNew: true,
            tempId: uuid()
        };
        
        this.setState({
            editingSalesOrderLogs: [...this.state.editingSalesOrderLogs, newLog]
        });
    };

    handleSaveSalesOrder = async () => {
        const self = this;
        const newLinesCount = self.state.editingSalesOrderLogs.filter(
            log => log.isNew && (log.assemblyId || log.subAssemblyId || log.partId)
        ).length;
        self.setState({ savingSalesOrder: true });

        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const user = AuthenticationService.getInstance().getUser();

            // Update existing logs
            const updatePromises = this.state.editingSalesOrderLogs
                .filter(log => !log.isNew)
                .map(log => {
                    const payload = {
                        id: log.id,
                        globalSequenceNumber: log.globalSequenceNumber,
                        logDate: log.logDate,
                        source: log.source,
                        orderNumber: log.orderNumber,
                        assemblyId: log.assemblyId,
                        subAssemblyId: log.subAssemblyId,
                        partId: log.partId,
                        quantity: parseNumberInputValue(log.quantity),
                        reason: log.reason,
                        status: normalizeStatus(log.status),
                        affectsProjections: log.affectsProjections,
                        lineNumber: log.lineNumber,
                        isManuallyAssigned: log.isManuallyAssigned,
                        userId: log.userId,
                        customerName: log.customerName || null,
                        shippingAddress: log.shippingAddress || null,
                        customerEmail: log.customerEmail || null
                    };
                    return assemblyService.updateSalesLog(payload);
                });

            // Create new logs (use createManualSalesLogs so same part number creates a new line; ImportSalesLogs would match and update existing)
            const existingLineNumbers = this.state.editingSalesOrderLogs
                .filter(log => !log.isNew && log.lineNumber != null)
                .map(log => log.lineNumber);
            let nextLineNumber = existingLineNumbers.length > 0 ? Math.max(...existingLineNumbers) + 1 : 1;
            
            const newLinePayloads = this.state.editingSalesOrderLogs
                .filter(log => log.isNew && (log.assemblyId || log.subAssemblyId || log.partId))
                .map(log => ({
                    globalSequenceNumber: log.globalSequenceNumber,
                    logDate: log.logDate,
                    source: log.source,
                    orderNumber: log.orderNumber,
                    assemblyId: log.assemblyId,
                    subAssemblyId: log.subAssemblyId,
                    partId: log.partId,
                    quantity: parseNumberInputValue(log.quantity),
                    reason: log.reason,
                    status: normalizeStatus(log.status),
                    affectsProjections: log.affectsProjections,
                    lineNumber: nextLineNumber++,
                    isManuallyAssigned: true,
                    userId: user.id,
                    customerName: log.customerName || null,
                    shippingAddress: log.shippingAddress || null,
                    customerEmail: log.customerEmail || null
                }));
            const createPromises = newLinePayloads.length > 0
                ? [assemblyService.createManualSalesLogs(newLinePayloads)]
                : [];

            // Delete marked logs
            const deletePromises = this.state.deletedSalesLogIds.map(logId => {
                const logToDelete = this.state.salesLogs.find(log => log.id === logId);
                return assemblyService.deleteSalesLog(logToDelete);
            });

            await Promise.all([...updatePromises, ...createPromises, ...deletePromises]);

            // Clear memoization cache to ensure fresh data
            self.memoizedGetSortedAndFilteredSalesLogs.clear();
            self.memoizedGetFilteredSalesLogs.clear();

            // Reload data first
            await self.loadData();
            
            window.$alertSuccess('Sales order updated successfully');
            
            if (newLinesCount > 0) {
                // Keep drawer open and show copy table line selector with new lines checked
                const salesLogsForOrder = self.state.salesLogs.filter(
                    log => log.globalSequenceNumber === self.state.editingSalesOrderGlobalSeq
                );
                const editingSalesOrderLogs = self.buildEditingSalesOrderLogsFromOrderLogs(salesLogsForOrder);
                const copyTableSelectedIds = editingSalesOrderLogs.slice(-newLinesCount).map(l => l.tempId);
                self.setState({
                    editingSalesOrderLogs,
                    savingSalesOrder: false,
                    salesOrderDataNeedsReload: false,
                    showCopyTableLineSelector: true,
                    copyTableSelectedIds,
                    dataRefreshKey: self.state.dataRefreshKey + 1
                }, () => {
                    setTimeout(() => self.setState({ dataRefreshKey: self.state.dataRefreshKey + 1 }), 100);
                });
            } else {
                // Close drawer and force refresh
                self.setState({
                    editSalesOrderOpen: false,
                    editingSalesOrderLogs: [],
                    editingSalesOrderGlobalSeq: null,
                    deletedSalesLogIds: [],
                    pendingNewSalesLogs: [],
                    savingSalesOrder: false,
                    salesOrderDataNeedsReload: false,
                    dataRefreshKey: self.state.dataRefreshKey + 1
                }, () => {
                    setTimeout(() => self.setState({ dataRefreshKey: self.state.dataRefreshKey + 1 }), 100);
                });
            }
        } catch (error) {
            console.error('Error saving sales order:', error);
            window.$alertError('Error saving sales order');
            self.setState({ savingSalesOrder: false });
        }
    };

    renderSalesOrderPage = () => {
        const seq = Number(this.props.match?.params?.salesLogSequence);
        const logs = (this.state.salesLogs || [])
            .filter((log) => log.globalSequenceNumber === seq)
            .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0));
        const first = logs[0];
        const tile = {
            maxWidth: 1400,
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: 15,
        };
        return (
            <>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, minHeight: 48 }}>
                    <Button variant="outlined" className="success-button-lg" onClick={() => followAppLink(this.props.history, '/admin/sales')}>
                        Back to Sales
                    </Button>
                    <Button
                        className="save-button-lg"
                        onClick={this.handleSaveSalesOrderHeader}
                        disabled={!first || !this.state.salesOrderHeaderDirty || this.state.savingSalesOrderHeader}
                        style={{ backgroundColor: this.state.salesOrderHeaderDirty ? '#7fff84' : '#93e7eb', color: 'black' }}
                    >
                        {this.state.savingSalesOrderHeader ? 'Saving...' : 'Save Order'}
                    </Button>
                    <h1 style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', margin: 0, color: 'white', whiteSpace: 'nowrap' }}>
                        Sales Order {first?.compositeOrderNumber || this.props.match?.params?.salesLogSequence}
                    </h1>
                </div>
                {!first ? (
                    <div className="container" style={{ ...tile }}>
                        <p>No sales logs were found for this order.</p>
                    </div>
                ) : (
                    <>
                        <div className="container" style={tile}>
                            <h2 style={{ marginTop: 0, marginBottom: 0 }}>Order Information</h2>
                            {this.state.salesOrderHeader && (
                                <div style={{ marginTop: 16 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 15, marginBottom: 15 }}>
                                        <DateTimeLocalInput
                                            label="Order Date"
                                            value={this.state.salesOrderHeader.logDate}
                                            onChange={(parsedDate) => this.updateSalesOrderHeader('logDate', parsedDate)}
                                            style={{ width: '100%' }}
                                        />
                                        <FormControl variant="outlined" style={{ width: '100%' }}>
                                            <InputLabel>Source</InputLabel>
                                            <Select
                                                value={this.state.salesOrderHeader.source || ''}
                                                label="Source"
                                                onChange={(e) => this.updateSalesOrderHeader('source', e.target.value)}
                                            >
                                                {this.state.salesLogSources.map((source) => (
                                                    <MenuItem key={source.source} value={source.source}>
                                                        {source.source}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            label="Dealer Order #"
                                            variant="outlined"
                                            value={this.state.salesOrderHeader.orderNumber || ''}
                                            onChange={(e) => this.updateSalesOrderHeader('orderNumber', e.target.value)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                                        <TextField
                                            label="Customer Name"
                                            variant="outlined"
                                            value={this.state.salesOrderHeader.customerName || ''}
                                            onChange={(e) => this.updateSalesOrderHeader('customerName', e.target.value)}
                                            inputProps={{ maxLength: 200 }}
                                        />
                                        <TextField
                                            label="Customer Email"
                                            variant="outlined"
                                            value={this.state.salesOrderHeader.customerEmail || ''}
                                            onChange={(e) => this.updateSalesOrderHeader('customerEmail', e.target.value)}
                                            inputProps={{ maxLength: 255 }}
                                        />
                                        <TextField
                                            label="Shipping Address"
                                            variant="outlined"
                                            value={this.state.salesOrderHeader.shippingAddress || ''}
                                            onChange={(e) => this.updateSalesOrderHeader('shippingAddress', e.target.value)}
                                            inputProps={{ maxLength: 500 }}
                                            multiline
                                            style={{ gridColumn: '1 / -1' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="container" style={tile}>
                            <h2 style={{ marginTop: 0, marginBottom: 8 }}>Line Items</h2>
                            <div style={{ marginBottom: 12 }}>
                                <Button className="save-button-lg" onClick={() => this.openSalesOrderLineEditor(seq)} style={{ backgroundColor: '#7fff84', color: 'black' }}>
                                    Edit
                                </Button>
                            </div>
                            <div className="parts-table-container">
                                <table className="parts-table">
                                    <thead>
                                        <tr>
                                            <th style={{ minWidth: 120, width: '1%', whiteSpace: 'nowrap' }}>User</th>
                                            <th style={{ minWidth: 50, width: '1%', whiteSpace: 'nowrap' }}>Line #</th>
                                            <th style={{ minWidth: 55, width: '1%', whiteSpace: 'nowrap' }}>Quantity</th>
                                            <th style={{ minWidth: 110, width: '1%', whiteSpace: 'nowrap' }}>PN</th>
                                            <th style={{ minWidth: 30, width: '1%', whiteSpace: 'nowrap' }}>Rev</th>
                                            <th style={{ minWidth: 30, width: '1%', whiteSpace: 'nowrap' }}>Lock</th>
                                            <th style={{ minWidth: 75, width: '1%', whiteSpace: 'nowrap' }}>Affects Sales<br/>Projections</th>
                                            <th style={{ minWidth: 70, width: '1%', whiteSpace: 'nowrap' }}>Status</th>
                                            <th style={{ minWidth: 400 }}>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((log) => {
                                            const part = this.lookupMaps.partsMap.get(log.partId);
                                            const assembly = this.lookupMaps.assembliesMap.get(log.assemblyId);
                                            const subAssembly = this.lookupMaps.subAssembliesMap.get(log.subAssemblyId);
                                            const adminUser = this.lookupMaps.adminUsersMap.get(log.userId);
                                            const partNumber = assembly ? assembly.assembly.assemblyId : subAssembly ? subAssembly.subAssembly.subAssemblyId : part ? part.part.partId : '';
                                            const revision = assembly ? assembly.assembly.revision : subAssembly ? subAssembly.subAssembly.revision : part ? part.part.revision : '';
                                            return (
                                            <tr key={log.id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{adminUser ? `${adminUser.firstName} ${adminUser.lastName}` : (log.source === 'SquareSpace' ? 'Automatic Import' : 'Unknown')}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{log.lineNumber}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{log.quantity}</td>
                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                                                    {this.state.permissions.includes('EditAssembliesAndParts') ?
                                                        <span
                                                            style={{ fontWeight: 'bold', color: 'black', cursor: 'pointer' }}
                                                            onClick={(event) => this.editAssemblyOrPart(assembly, part, subAssembly, event.ctrlKey)}
                                                        >{partNumber}</span>
                                                        :
                                                        <span>{partNumber}</span>
                                                    }
                                                </td>
                                                <td>{revision}</td>
                                                <td>{log.isManuallyAssigned ? <FontAwesomeIcon icon={faLock} size="1x" style={{ color: '#666' }} /> : ''}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {log.affectsProjections ? <span style={{ fontWeight: 'bold' }}>✓</span> : ''}
                                                </td>
                                                <td style={{
                                                    color: normalizeStatus(log.status) === 'Fulfilled' ? '#4caf50' :
                                                           normalizeStatus(log.status) === 'Pending' ? '#ff9800' :
                                                           normalizeStatus(log.status) === 'Cancelled' ? '#f44336' :
                                                           normalizeStatus(log.status) === 'Refunded' ? '#9c27b0' : '#666',
                                                    fontWeight: 'bold'
                                                }}>{normalizeStatus(log.status)}</td>
                                                <td className="text-left">{log.reason || ''}</td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    };

    render() {
        const {classes} = this.props;
        const self = this;
        if (this.state.loading) {
            return (
                <DashboardTemplate title="Sales Logs" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress/>
                    </div>
                </DashboardTemplate>
            );
        }

        // Show processing indicator for large datasets
        if (this.state.isProcessingData && this.state.salesLogs.length > 1000) {
            return (
                <DashboardTemplate title="Sales Logs" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <div style={{textAlign: 'center', padding: '20px'}}>
                            <CircularProgress/>
                            <p>Processing {this.state.salesLogs.length.toLocaleString()} sales logs...</p>
                            <p style={{fontSize: '12px', color: '#666'}}>
                                Large dataset detected - optimizing for performance
                            </p>
                        </div>
                    </div>
                </DashboardTemplate>
            );
        }

        // Build parts options for dropdowns
        let partsOptions = [];
        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            partsOptions.push({
                title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${assembly.assembly.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies) ? ' 🛒' : ''}`, 
                value: assembly.assembly.id,
                type: 'assembly',
                partId: assembly.assembly.assemblyId,
                revision: assembly.assembly.revision,
                description: assembly.assembly.description,
                currentlyPackaging: assembly.assembly.currentlyPackaging,
                currentlySelling: isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies)
            });
        });
        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((subAssembly) => {
            partsOptions.push({
                title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`, 
                value: subAssembly.subAssembly.id,
                type: 'subassembly',
                partId: subAssembly.subAssembly.subAssemblyId,
                revision: subAssembly.subAssembly.revision,
                description: subAssembly.subAssembly.description,
                currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
            });
        });
        this.state.parts.filter(x => x.part.isActive).forEach((part) => {
            partsOptions.push({
                title: `${part.part.partId} rev ${part.part.revision}`, 
                value: part.part.id,
                type: 'part',
                partId: part.part.partId,
                revision: part.part.revision,
                part: part.part,
                currentlyPurchasing: false
            });
        });

        // Sort partsOptions by partId, then by revision
        partsOptions.sort((a, b) => {
            const partIdCompare = a.partId.localeCompare(b.partId);
            if (partIdCompare !== 0) return partIdCompare;
            
            // If partIds are the same, sort by revision (numeric)
            const revisionA = parseInt(a.revision) || 0;
            const revisionB = parseInt(b.revision) || 0;
            return revisionA - revisionB;
        });

        // Use optimized data processing with lazy loading for large datasets
        const processedData = this.getProcessedData();
        
        // For very large datasets, only load the current page to reduce memory usage
        const startIndex = this.state.page * this.state.rowsPerPage;
        const endIndex = startIndex + this.state.rowsPerPage;
        const paginatedSalesLogs = processedData.length > 10000 ? 
            processedData.slice(startIndex, endIndex) :
            processedData.slice(startIndex, endIndex);

        const isSalesOrderPage = this.props.match?.params?.salesLogSequence != null
            && this.props.match.params.salesLogSequence !== '';

        return (
            <DashboardTemplate title={isSalesOrderPage ? 'Sales Order' : 'Sales Logs'} onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className={isSalesOrderPage ? undefined : 'container'} style={{margin: '0 auto 20px'}}>
                            {isSalesOrderPage ? this.renderSalesOrderPage() : (
                            <>
                            <div className={classes.toolbar}>
                                <div style={{position: 'relative', display: 'flex', alignItems: 'center', width: '100%'}}>
                                    {/* Left: Search Bar */}
                                    <div style={{flex: '1', display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                                        <TextField
                                            value={this.state.searchTerm || ''}
                                            onChange={this.searchSales}
                                            label="Search by PN, Source, User, Reason, Order, or Customer..."
                                            variant="outlined"
                                            style={{width: '400px'}}
                                            autoComplete="off"
                                            inputRef={ref => this.searchInputRef = ref}

                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                const processedData = this.getProcessedData();
                                                const allFilteredIds = processedData.map(log => log.id);
                                                this.setState({
                                                    selectedSalesLogs: [...new Set([...this.state.selectedSalesLogs, ...allFilteredIds])]
                                                });
                                            }}
                                        >
                                            Select All
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={this.handleSelectNoneSalesLogs}
                                        >
                                            Select None
                                        </Button>
                                    </div>
                                
                                {/* Center: Buttons - Absolutely centered */}
                                <div style={{position: 'absolute', left: '50%', transform: 'translateX(-50%)'}}>
                                    <div style={{display: 'flex', gap: '10px'}}>
                                        <Button 
                                            variant="contained"
                                            color="primary"
                                            onClick={this.openImportSalesLogs} 
                                            className="success-button-lg"
                                        >
                                            Import
                                        </Button>
                                        <Button 
                                            variant="contained"
                                            color="primary"
                                            onClick={this.openManualSalesLog} 
                                            className="success-button-lg"
                                        >
                                            Add Manual
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Right: Automated import control and Date Range Filters */}
                                <div style={{flex: '1', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                                    {(this.state.dateRangeStart || this.state.dateRangeEnd) && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                this.setState({ 
                                                    dateRangeStart: null,
                                                    dateRangeEnd: null,
                                                    page: 0
                                                }, () => {
                                                    this.saveFilterSettings();
                                                    // Clear memoization cache to ensure fresh data
                                                    this.memoizedGetSortedAndFilteredSalesLogs.clear();
                                                });
                                            }}
                                            style={{height: '40px'}}
                                        >
                                            Clear Dates
                                        </Button>
                                    )}
                                    <TextField
                                        type="date"
                                        label="Date From"
                                        value={this.state.dateRangeStart || ''}
                                        onChange={(e) => {
                                            this.setState({ 
                                                dateRangeStart: e.target.value || null,
                                                page: 0
                                            }, () => {
                                                this.saveFilterSettings();
                                                // Clear memoization cache to ensure fresh data
                                                this.memoizedGetSortedAndFilteredSalesLogs.clear();
                                            });
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        variant="outlined"
                                        style={{width: '150px'}}
                                    />
                                    <TextField
                                        type="date"
                                        label="Date To"
                                        value={this.state.dateRangeEnd || ''}
                                        onChange={(e) => {
                                            this.setState({ 
                                                dateRangeEnd: e.target.value || null,
                                                page: 0
                                            }, () => {
                                                this.saveFilterSettings();
                                                // Clear memoization cache to ensure fresh data
                                                this.memoizedGetSortedAndFilteredSalesLogs.clear();
                                            });
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        variant="outlined"
                                        style={{width: '150px'}}
                                    />
                                    {this.state.loadingImportStatus ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <>
                                            <Button 
                                                variant="outlined"
                                                color={this.state.squarespaceImportPaused ? "primary" : "default"}
                                                onClick={this.toggleSquarespaceImport}
                                                size="small"
                                                style={{
                                                    borderColor: this.state.squarespaceImportPaused ? '#4caf50' : '#f44336',
                                                    color: this.state.squarespaceImportPaused ? '#4caf50' : '#f44336',
                                                    fontWeight: 'bold',
                                                    minWidth: '120px'
                                                }}
                                            >
                                                {this.state.squarespaceImportPaused ? '▶ Resume Auto Import' : '⏸ Pause Auto Import'}
                                            </Button>
                                            <span style={{
                                                fontSize: '12px',
                                                color: this.state.squarespaceImportPaused ? '#f44336' : '#4caf50',
                                                fontWeight: 'bold'
                                            }}>
                                                {this.state.squarespaceImportPaused ? 'PAUSED' : 'RUNNING'}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <center>
                            <TablePagination
                                rowsPerPageOptions={[50, 100, 200, 500]}
                                colSpan={4}
                                count={processedData.length}
                                rowsPerPage={this.state.rowsPerPage}
                                page={this.state.page}
                                slotProps={{
                                    select: {
                                        'aria-label': 'rows per page',
                                    },
                                    actions: {
                                        showFirstButton: true,
                                        showLastButton: true,
                                        slots: {
                                            firstPageIcon: FirstPageIcon,
                                            lastPageIcon: LastPageIcon,
                                            nextPageIcon: ChevronRightIcon,
                                            backPageIcon: ChevronLeftIcon,
                                        },
                                    },
                                }}
                                onPageChange={this.handleChangePage}
                                onRowsPerPageChange={this.handleChangeRowsPerPage}
                            />
                        </center>
                        <div className="parts-table-container" key={`table-${this.state.dataRefreshKey}`}>
                            <table className="parts-table">
                                <thead>
                                <tr>
                                    <th style={{width: 50}}>
                                        <Checkbox
                                            checked={(() => {
                                                const processedData = this.getProcessedData();
                                                const paginatedLogs = processedData.slice(
                                                    this.state.page * this.state.rowsPerPage,
                                                    this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
                                                );
                                                return paginatedLogs.length > 0 && paginatedLogs.every(log => 
                                                    this.state.selectedSalesLogs.includes(log.id)
                                                );
                                            })()}
                                            indeterminate={(() => {
                                                const processedData = this.getProcessedData();
                                                const paginatedLogs = processedData.slice(
                                                    this.state.page * this.state.rowsPerPage,
                                                    this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
                                                );
                                                const selectedCount = paginatedLogs.filter(log => 
                                                    this.state.selectedSalesLogs.includes(log.id)
                                                ).length;
                                                return selectedCount > 0 && selectedCount < paginatedLogs.length;
                                            })()}
                                            onChange={this.handleSelectAllSalesLogs}
                                        />
                                    </th>
                                    <th style={{width: 40}}>Edit</th>
                                    <th 
                                        style={{minWidth: 70, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('compositeOrderNumber')}
                                    >
                                        Sales Order {this.state.sortField === 'compositeOrderNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 140, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('logDate')}
                                    >
                                        Date {this.state.sortField === 'logDate' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 120, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('user')}
                                    >
                                        User {this.state.sortField === 'user' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 100, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('source')}
                                    >
                                        Source {this.state.sortField === 'source' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 150, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('customerName')}
                                    >
                                        Customer Name {this.state.sortField === 'customerName' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 200}}
                                    >
                                        Email
                                    </th>
                                    <th 
                                        style={{minWidth: 60, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('orderNumber')}
                                    >
                                        Dealer<br/>Order # {this.state.sortField === 'orderNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th 
                                        style={{minWidth: 50, cursor: 'pointer', userSelect: 'none'}}
                                    >
                                        Line #
                                    </th>
                                    <th style={{minWidth: 55}}>Quantity</th>
                                    <th 
                                        style={{minWidth: 110, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('partNumber')}
                                    >
                                        PN {this.state.sortField === 'partNumber' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{minWidth: 30}}>Rev</th>
                                    <th style={{minWidth: 30}}>Lock</th>
                                    <th style={{minWidth: 75}}>Affects Sales<br/>Projections</th>
                                    <th 
                                        style={{minWidth: 70, cursor: 'pointer', userSelect: 'none'}}
                                        onClick={() => this.handleSort('status')}
                                    >
                                        Status {this.state.sortField === 'status' && (this.state.sortDirection === 'asc' ? '↑' : '↓')}
                                    </th>
                                    <th style={{minWidth: 400}}>Reason</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(paginatedSalesLogs || []).map((salesLog) => {
                                    // Use optimized lookup maps instead of array.find()
                                    const part = this.lookupMaps.partsMap.get(salesLog.partId);
                                    const assembly = this.lookupMaps.assembliesMap.get(salesLog.assemblyId);
                                    const subAssembly = this.lookupMaps.subAssembliesMap.get(salesLog.subAssemblyId);

                                    const adminUser = this.lookupMaps.adminUsersMap.get(salesLog.userId);

                                    return (
                                        <tr key={`${salesLog.id}-${this.state.dataRefreshKey}`}>
                                            <td
                                                onClick={(e) => {
                                                    // Handle ALL clicks at the td level to properly capture modifiers
                                                    const isCtrlPressed = e.ctrlKey || e.metaKey;
                                                    const isShiftPressed = e.shiftKey;
                                                    
                                                    // If modifiers are pressed, handle it here and prevent checkbox from processing
                                                    if (isCtrlPressed || isShiftPressed) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        // Stop event from bubbling to checkbox
                                                        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                                                            e.nativeEvent.stopImmediatePropagation();
                                                        }
                                                        // Create event object with modifier keys
                                                        const syntheticEvent = {
                                                            ctrlKey: isCtrlPressed,
                                                            metaKey: e.metaKey,
                                                            shiftKey: isShiftPressed,
                                                            preventDefault: () => {},
                                                            stopPropagation: () => {},
                                                            target: { checked: !this.state.selectedSalesLogs.includes(salesLog.id) },
                                                            nativeEvent: e.nativeEvent
                                                        };
                                                        this.handleSelectSalesLog(salesLog.id, syntheticEvent);
                                                        return false;
                                                    }
                                                    // Normal clicks will fall through to checkbox onChange
                                                }}
                                                onMouseDown={(e) => {
                                                    // Also catch on mousedown to prevent checkbox from processing
                                                    const isCtrlPressed = e.ctrlKey || e.metaKey;
                                                    const isShiftPressed = e.shiftKey;
                                                    
                                                    if (isCtrlPressed || isShiftPressed) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                                                            e.nativeEvent.stopImmediatePropagation();
                                                        }
                                                    }
                                                }}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <span
                                                    onClick={(e) => {
                                                        // Wrap checkbox in span to catch clicks before they reach checkbox
                                                        const isCtrlPressed = e.ctrlKey || e.metaKey;
                                                        const isShiftPressed = e.shiftKey;
                                                        
                                                        if (isCtrlPressed || isShiftPressed) {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                                                                e.nativeEvent.stopImmediatePropagation();
                                                            }
                                                        }
                                                    }}
                                                    style={{display: 'inline-block'}}
                                                >
                                                    <Checkbox
                                                        checked={this.state.selectedSalesLogs.includes(salesLog.id)}
                                                        onChange={(e) => {
                                                            // Ignore if we're currently processing a modifier click
                                                            if (this.state.isProcessingModifierClick) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                return;
                                                            }
                                                            
                                                            // Check if modifiers were pressed - if so, ignore this event
                                                            const isCtrlPressed = e.ctrlKey || e.metaKey;
                                                            const isShiftPressed = e.shiftKey;
                                                        
                                                            if (isCtrlPressed || isShiftPressed) {
                                                                // Prevent checkbox from toggling
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                return;
                                                            }
                                                        
                                                            // Normal click - handle it
                                                            this.handleSelectSalesLog(salesLog.id, e);
                                                        }}
                                                        onClick={(e) => {
                                                            // Prevent checkbox from processing modifier clicks
                                                            const isCtrlPressed = e.ctrlKey || e.metaKey;
                                                            const isShiftPressed = e.shiftKey;
                                                        
                                                            if (isCtrlPressed || isShiftPressed) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                                                                    e.nativeEvent.stopImmediatePropagation();
                                                                }
                                                                return false;
                                                            }
                                                        }}
                                                        style={{padding: 0}}
                                                    />
                                                </span>
                                            </td>
                                            <td><FontAwesomeIcon icon={faPencilAlt} size="1x"
                                                                 style={{width: 28, fontSize: 18, cursor: 'pointer'}}
                                                                 onClick={() => this.openMultilineEditDrawer(salesLog.globalSequenceNumber)}
                                                                 title="Edit order"/></td>
                                            <td>
                                                <a
                                                    href={`/admin/sales/${salesLog.globalSequenceNumber}`}
                                                    style={{
                                                        cursor: 'pointer',
                                                        textDecoration: 'none',
                                                        fontWeight: '800',
                                                        color: 'inherit'
                                                    }}
                                                    onClick={(event) => followAppLink(this.props.history, `/admin/sales/${salesLog.globalSequenceNumber}`, event)}
                                                    title="Open this sales order. Ctrl+click opens it in a new tab."
                                                >
                                                    {salesLog.compositeOrderNumber}
                                                </a>
                                            </td>
                                            <td>{formatUtcForDisplay(salesLog.logDate)}</td>
                                            <td>{adminUser ? `${adminUser.firstName} ${adminUser.lastName}` : (salesLog.source === 'SquareSpace' ? 'Automatic Import' : 'Unknown')}</td>
                                            <td>
                                                {salesLog.source}
                                                {(salesLog.source === 'Customer Site' && (salesLog.customerOrderLineItemId || salesLog.globalSequenceNumber)) && (
                                                    <>
                                                        {' '}
                                                        <a
                                                            href={`/admin/orders?seq=${salesLog.globalSequenceNumber}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ marginLeft: 6, fontSize: 12 }}
                                                        >
                                                            View order
                                                        </a>
                                                    </>
                                                )}
                                            </td>
                                            <td className="text-left">{salesLog.customerName || ''}</td>
                                            <td className="text-left address-ellipsis">
                                            <Tooltip
                                                title={salesLog.customerEmail || ''}
                                                placement="bottom" TransitionComponent={Zoom}>
                                                <span>{salesLog.customerEmail || ''}</span>
                                            </Tooltip></td>
                                            <td>{salesLog.orderNumber}</td>
                                            <td>{salesLog.lineNumber}</td>
                                            <td>{salesLog.quantity}</td>
                                            <td className="text-left">
                                                {this.state.permissions.includes('EditAssembliesAndParts') ?
                                                    <span
                                                        style={{fontWeight: 'bold', color: 'black', cursor: 'pointer'}}
                                                        onClick={(event) => this.editAssemblyOrPart(assembly, part, subAssembly, event.ctrlKey)}>{assembly ? `${assembly.assembly.assemblyId}` : subAssembly ? `${subAssembly.subAssembly.subAssemblyId}` : part ? `${part.part.partId}` : ''}</span>
                                                    :
                                                    <span>{assembly ? `${assembly.assembly.assemblyId}` : subAssembly ? `${subAssembly.subAssembly.subAssemblyId}` : part ? `${part.part.partId}` : ''}</span>
                                                }
                                            </td>
                                            <td>{assembly ? `${assembly.assembly.revision}` : subAssembly ? `${subAssembly.subAssembly.revision}` : part ? `${part.part.revision}` : ''}</td>
                                            <td>{salesLog.isManuallyAssigned ? <FontAwesomeIcon icon={faLock} size="1x" style={{color: '#666'}} /> : ''}</td>
                                            <td style={{textAlign: 'center'}}>
                                                {salesLog.affectsProjections ? 
                                                    <span style={{fontWeight: 'bold'}}>✓</span> : 
                                                    ''
                                                }
                                            </td>
                                            <td style={{
                                                color: normalizeStatus(salesLog.status) === 'Fulfilled' ? '#4caf50' : 
                                                       normalizeStatus(salesLog.status) === 'Pending' ? '#ff9800' : 
                                                       normalizeStatus(salesLog.status) === 'Cancelled' ? '#f44336' : 
                                                       normalizeStatus(salesLog.status) === 'Refunded' ? '#9c27b0' : '#666',
                                                fontWeight: 'bold'
                                            }}>{normalizeStatus(salesLog.status)}</td>
                                            <td className="text-left">{salesLog.reason}</td>
                                        </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                        </div>
                        <center>
                            <TablePagination
                                rowsPerPageOptions={[50, 100, 200, 500]}
                                colSpan={4}
                                count={processedData.length}
                                rowsPerPage={this.state.rowsPerPage}
                                page={this.state.page}
                                slotProps={{
                                    select: {
                                        'aria-label': 'rows per page',
                                    },
                                    actions: {
                                        showFirstButton: true,
                                        showLastButton: true,
                                        slots: {
                                            firstPageIcon: FirstPageIcon,
                                            lastPageIcon: LastPageIcon,
                                            nextPageIcon: ChevronRightIcon,
                                            backPageIcon: ChevronLeftIcon,
                                        },
                                    },
                                }}
                                onPageChange={this.handleChangePage}
                                onRowsPerPageChange={this.handleChangeRowsPerPage}
                            />
                        </center>
                            </>
                            )}
                        {this.state.importSalesLogsOpen ?
                            <DrawerMenuRight title={"Import Sales Logs"} customWidth={950}
                                             open={this.state.importSalesLogsOpen}
                                             onClose={this.handleCloseImportLogs}
                                             hasUnsavedChanges={this.state.importFiles.length > 0 || this.state.parsedImportData.length > 0}>
                                <div 
                                    style={{
                                        padding: 20, 
                                        border: '2px dashed #ddd', 
                                        borderRadius: 8, 
                                        textAlign: 'center', 
                                        backgroundColor: '#fafafa',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.style.borderColor = '#2196f3';
                                        e.currentTarget.style.backgroundColor = '#e3f2fd';
                                    }}
                                    onDragLeave={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.style.borderColor = '#ddd';
                                        e.currentTarget.style.backgroundColor = '#fafafa';
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.style.borderColor = '#ddd';
                                        e.currentTarget.style.backgroundColor = '#fafafa';
                                        
                                        const files = Array.from(e.dataTransfer.files).filter(file => 
                                            file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
                                        );
                                        if (files.length > 0) {
                                            this.handleFileSelection({ target: { files } });
                                        }
                                    }}
                                    onClick={() => document.getElementById('file-upload-input').click()}
                                >
                                    <div style={{marginBottom: 15}}>
                                        <h4 style={{margin: 0, color: '#333'}}>📁 Upload Sales Log CSV Files</h4>
                                        <p style={{margin: '5px 0', fontSize: 14, color: '#666'}}>
                                            Drag and drop CSV files here or click to browse
                                        </p>
                                    </div>
                                    
                                    <input
                                        id="file-upload-input"
                                        type="file"
                                        accept=".csv"
                                        onChange={this.handleFileSelection}
                                        multiple
                                        style={{display: 'none'}}
                                    />
                                    
                                    <div style={{fontSize: 12, color: '#888', marginTop: 10}}>
                                        <strong>Supported Formats:</strong><br/>
                                        🔷 <strong>ECS Tuning:</strong> Files should contain 'vendorPartNumber2' and 'PO qtyOrdered' columns<br/>
                                        🔷 <strong>SquareSpace:</strong> Standard SquareSpace export format with 'Billing Name' column<br/>
                                        ⚠️ All files must be from the same dealer type<br/>
                                    </div>
                                </div>
                                
                                {this.state.savingImportedData === true && (
                                    <div style={{textAlign: 'center', marginTop: 15}}>
                                        <CircularProgress/>
                                        <div style={{marginTop: 10, fontSize: 14, color: '#666'}}>
                                            Processing files...
                                        </div>
                                    </div>
                                )}
                                                                 {this.state.importStep === 'review' && this.state.parsedImportData.length > 0 && (
                                     <>
                                         <h3>Review Imported Data ({this.state.importDealer} Format)</h3>
                                         <div style={{marginBottom: 10}}>
                                             <span style={{color: '#666', fontSize: 14}}>
                                                 {this.state.parsedImportData.length} items found | 
                                                 {this.state.importDuplicates.length} existing duplicates | 
                                                 {this.state.importErrors.length} errors
                                             </span>
                                         </div>
                                         
                                         {/* Override exact duplicates and revision updates option */}
                                         {(this.state.importDuplicates.filter(dup => dup.type === 'exact_duplicate').length > 0) && (
                                             <div style={{
                                                 marginBottom: 15, 
                                                 padding: 15, 
                                                 backgroundColor: '#fff3e0', 
                                                 border: '1px solid #ff9800', 
                                                 borderRadius: 4,
                                                 display: 'flex',
                                                 alignItems: 'center',
                                                 gap: '10px'
                                             }}>
                                                 <Checkbox
                                                     checked={this.state.importOverrideExactDuplicates}
                                                     onChange={(e) => this.setState({importOverrideExactDuplicates: e.target.checked})}
                                                     color="primary"
                                                     style={{padding: 0}}
                                                 />
                                                 <div>
                                                     <div style={{fontSize: 14, color: '#f57c00', fontWeight: 'bold'}}>
                                                         ⚠️ Override Exact Duplicates
                                                     </div>
                                                     <div style={{fontSize: 12, color: '#666', marginTop: 2}}>
                                                         Check this box to import all exact duplicates. 
                                                         Exact duplicates will create new sales log entries. Revision updates are automatically included and will update existing sales logs with new part IDs.
                                                         You can also override individual lines using the checkboxes in the table below.
                                                     </div>
                                                 </div>
                                             </div>
                                        )}
                                        
                                        {/* Filter toggle for showing only problematic rows */}
                                        <div style={{
                                            marginBottom: 15, 
                                            padding: 12, 
                                            backgroundColor: '#f5f5f5', 
                                            border: '1px solid #ddd', 
                                            borderRadius: 4,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}>
                                            <Checkbox
                                                checked={this.state.importReviewShowProblemsOnly}
                                                onChange={this.toggleImportReviewProblemsOnly}
                                                color="primary"
                                                style={{padding: 0}}
                                            />
                                            <div>
                                                <div style={{fontSize: 14, color: '#333', fontWeight: 'bold'}}>
                                                    🔍 Show Only Problematic Rows
                                                </div>
                                                <div style={{fontSize: 12, color: '#666', marginTop: 2}}>
                                                    Hide rows with "Ready", "Will Update Existing", or "Revision Update" status to focus on items that need attention.
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginTop: 10
                                        }}>
                                            <Checkbox
                                                checked={this.state.importReviewHideExactDuplicates}
                                                onChange={this.toggleImportReviewHideExactDuplicates}
                                                color="primary"
                                                style={{padding: 0}}
                                            />
                                            <div>
                                                <div style={{fontSize: 14, color: '#333', fontWeight: 'bold'}}>
                                                    🚫 Hide Exact Duplicates
                                                </div>
                                                <div style={{fontSize: 12, color: '#666', marginTop: 2}}>
                                                    Hide rows that are exact duplicates of existing sales logs.
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Group items by file */}
                                        {this.state.importFiles.map((file, fileIndex) => {
                                            let fileItems = this.state.parsedImportData.filter(item => item.fileName === file.name);
                                            
                                            // Apply hide exact duplicates filter if enabled
                                            if (this.state.importReviewHideExactDuplicates) {
                                                fileItems = fileItems.filter(item => {
                                                    const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                    // Hide exact duplicates that aren't being overridden
                                                    if (duplicateInfo && duplicateInfo.type === 'exact_duplicate') {
                                                        const individualOverride = this.state.importIndividualLineOverrides[item.id];
                                                        const globalOverride = this.state.importOverrideExactDuplicates;
                                                        // Hide if not overridden (either globally or individually)
                                                        return individualOverride === true || globalOverride === true;
                                                    }
                                                    return true; // Keep all non-exact-duplicates
                                                });
                                            }
                                            
                                            // Apply problems-only filter if enabled
                                            if (this.state.importReviewShowProblemsOnly) {
                                                fileItems = fileItems.filter(item => {
                                                    const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                    const hasError = this.state.importErrors.some(error => error.id === item.id) || 
                                                                    (!item.assemblyId && !item.partId);
                                                    
                                                    // Show if: has error, is unmatching PN, or is exact duplicate (not being overridden), or is manually assigned line
                                                    if (hasError || item.isUnmatchingPN || (!item.assemblyId && !item.subAssemblyId && !item.partId)) {
                                                        return true;
                                                    }
                                                    
                                                    // Show exact duplicates if not overridden
                                                    if (duplicateInfo && duplicateInfo.type === 'exact_duplicate') {
                                                        const individualOverride = this.state.importIndividualLineOverrides[item.id];
                                                        if (individualOverride === undefined) {
                                                            return !this.state.importOverrideExactDuplicates;
                                                        } else {
                                                            return !individualOverride;
                                                        }
                                                    }
                                                    
                                                    // Show manually assigned lines
                                                    if (duplicateInfo && duplicateInfo.isManuallyAssignedLine) {
                                                        return true;
                                                    }
                                                    
                                                    // Hide "potential_update", "revision_update", "status_update" (these are "Ready" or "Will Update")
                                                    return false;
                                                });
                                            }
                                             const hasErrors = fileItems.some(item => 
                                                 this.state.importErrors.some(error => error.id === item.id) ||
                                                 (!item.assemblyId && !item.partId)
                                             );
                                             const hasDuplicates = fileItems.some(item => 
                                                 this.state.importDuplicates.some(dup => dup.id === item.id)
                                             );
                                             const hasExactDuplicates = fileItems.some(item => {
                                                 const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                 return duplicateInfo && duplicateInfo.type === 'exact_duplicate';
                                             });
                                             const hasPotentialUpdates = fileItems.some(item => {
                                                 const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                 return duplicateInfo && duplicateInfo.type === 'potential_update';
                                             });
                                             const hasRevisionUpdates = fileItems.some(item => {
                                                 const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                 return duplicateInfo && duplicateInfo.type === 'revision_update';
                                             });
                                             const hasStatusUpdates = fileItems.some(item => {
                                                 const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                 return duplicateInfo && duplicateInfo.type === 'status_update';
                                             });
                                             const hasManuallyAssignedLines = fileItems.some(item => {
                                                 const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                 return duplicateInfo && duplicateInfo.isManuallyAssignedLine;
                                             });
                                             const hasManuallyFixedItems = fileItems.some(item => item.wasManuallyFixed);
                                             
                                             // Determine border color based on status
                                             let borderColor = '#4caf50'; // Default green
                                             if (hasErrors) {
                                                 borderColor = '#f44336'; // Red for errors
                                             } else if (hasManuallyFixedItems) {
                                                 borderColor = '#66bb6a'; // Light green for manually fixed items
                                             } else if (hasManuallyAssignedLines) {
                                                 borderColor = '#9c27b0'; // Purple for manually assigned lines
                                             } else if (hasExactDuplicates && !this.state.importOverrideExactDuplicates) {
                                                 borderColor = '#d32f2f'; // Dark red for exact duplicates (when not overridden)
                                             } else if (hasExactDuplicates && this.state.importOverrideExactDuplicates) {
                                                 borderColor = '#ff9800'; // Orange for exact duplicates (when overridden)
                                             } else if (hasPotentialUpdates) {
                                                 borderColor = '#1976d2'; // Blue for potential updates
                                             } else if (hasRevisionUpdates || hasStatusUpdates) {
                                                 borderColor = '#4caf50'; // Green for revision/status updates
                                             } else if (hasDuplicates) {
                                                 borderColor = '#ff9800'; // Orange for other duplicates
                                             }
                                             
                                             // Determine background color
                                             let backgroundColor = '#f1f8e9'; // Default green background
                                             if (hasErrors) {
                                                 backgroundColor = '#ffebee'; // Red background
                                             } else if (hasManuallyFixedItems) {
                                                 backgroundColor = '#e8f5e9'; // Very light green background for manually fixed items
                                             } else if (hasManuallyAssignedLines) {
                                                 backgroundColor = '#f3e5f5'; // Purple background for manually assigned lines
                                             } else if (hasExactDuplicates && !this.state.importOverrideExactDuplicates) {
                                                 backgroundColor = '#ffcdd2'; // Dark red background for exact duplicates (when not overridden)
                                             } else if (hasExactDuplicates && this.state.importOverrideExactDuplicates) {
                                                 backgroundColor = '#fff3e0'; // Orange background for exact duplicates (when overridden)
                                             } else if (hasPotentialUpdates) {
                                                 backgroundColor = '#e3f2fd'; // Blue background
                                             } else if (hasDuplicates) {
                                                 backgroundColor = '#fff3e0'; // Orange background
                                             }
                                             
                                             // Determine text color
                                             let textColor = '#388e3c'; // Default green text
                                             if (hasErrors) {
                                                 textColor = '#d32f2f'; // Red text
                                             } else if (hasManuallyAssignedLines) {
                                                 textColor = '#7b1fa2'; // Purple text for manually assigned lines
                                             } else if (hasExactDuplicates && !this.state.importOverrideExactDuplicates) {
                                                 textColor = '#d32f2f'; // Dark red text for exact duplicates (when not overridden)
                                             } else if (hasExactDuplicates && this.state.importOverrideExactDuplicates) {
                                                 textColor = '#f57c00'; // Orange text for exact duplicates (when overridden)
                                             } else if (hasPotentialUpdates) {
                                                 textColor = '#1976d2'; // Blue text
                                             } else if (hasDuplicates) {
                                                 textColor = '#f57c00'; // Orange text
                                             }
                                             
                                             return (
                                                 <div key={file.name} style={{
                                                     border: `2px solid ${borderColor}`,
                                                     borderRadius: 8,
                                                     padding: 15,
                                                     marginBottom: 15,
                                                     backgroundColor: backgroundColor
                                                 }}>
                                                     <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                                                         <h4 style={{margin: 0, color: textColor}}>
                                                             📄 {file.name} 
                                                             {hasErrors && ' ⚠️'} 
                                                             {hasManuallyAssignedLines && ' 🔒'} 
                                                             {hasExactDuplicates && !this.state.importOverrideExactDuplicates && !hasManuallyAssignedLines && ' 🚫'} 
                                                             {hasExactDuplicates && this.state.importOverrideExactDuplicates && !hasManuallyAssignedLines && ' ⚠️'} 
                                                             {hasPotentialUpdates && ' 🔄'} 
                                                             {hasDuplicates && !hasExactDuplicates && !hasPotentialUpdates && !hasManuallyAssignedLines && ' 🔄'}
                                                         </h4>
                                                         <button
                                                             onClick={() => this.removeImportFile(file.name)}
                                                             style={{
                                                                 background: 'none',
                                                                 border: '1px solid #d32f2f',
                                                                 color: '#d32f2f',
                                                                 borderRadius: 4,
                                                                 padding: '4px 8px',
                                                                 fontSize: 12,
                                                                 cursor: 'pointer',
                                                                 fontWeight: 'bold'
                                                             }}
                                                             title="Remove entire file from import"
                                                         >
                                                             🗑️ Remove File
                                                         </button>
                                                     </div>
                                                     
                                                    {/* ECS Order Date/Time Picker */}
                                                    {this.state.importDealer === 'ECS' && (
                                                        <div style={{
                                                            marginBottom: 15, 
                                                            padding: 15, 
                                                            backgroundColor: '#fff', 
                                                            border: '1px solid #eee', 
                                                            borderRadius: 4,
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                        }}>
                                                            <div style={{fontSize: 14, fontWeight: 'bold', marginBottom: 10, color: '#333'}}>
                                                                📅 Set Order Date/Time
                                                            </div>
                                                            <DateTimeLocalInput
                                                                label="Order Date/Time"
                                                                value={this.state.ecsOrderLogDates && this.state.ecsOrderLogDates[file.name] 
                                                                    ? this.state.ecsOrderLogDates[file.name] 
                                                                    : new Date()}
                                                                onChange={(parsedDate) => {
                                                                    const updatedDates = {...this.state.ecsOrderLogDates};
                                                                    updatedDates[file.name] = parsedDate;
                                                                    // Update logDate on all items for this file immediately
                                                                    const updatedItems = this.state.parsedImportData.map(item => {
                                                                        if (item.fileName === file.name && item.source === 'ECS') {
                                                                            return {
                                                                                ...item,
                                                                                logDate: localToUtcForStorage(parsedDate)
                                                                            };
                                                                        }
                                                                        return item;
                                                                    });
                                                                    this.setState({
                                                                        ecsOrderLogDates: updatedDates,
                                                                        parsedImportData: updatedItems
                                                                    });
                                                                }}
                                                                fullWidth
                                                            />
                                                            <div style={{fontSize: 12, color: '#666', marginTop: 8}}>
                                                                ℹ️ This date/time will be applied to all rows in this order
                                                            </div>
                                                        </div>
                                                    )}
                                                     
                                                    <div style={{marginBottom: 10, fontSize: 14, color: '#666'}}>
                                                        Order: {file.orderNumber} | Dealer: {this.state.importDealer} | Items: {fileItems.length}
                                                    </div>
                                                    
                                                    {(() => {
                                                        // Pagination logic
                                                        const itemsPerPage = this.state.importReviewItemsPerPage;
                                                        const currentPage = this.state.importReviewCurrentPages[file.name] || 1;
                                                        const totalPages = Math.ceil(fileItems.length / itemsPerPage);
                                                        const startIndex = (currentPage - 1) * itemsPerPage;
                                                        const endIndex = Math.min(startIndex + itemsPerPage, fileItems.length);
                                                        const paginatedItems = fileItems.slice(startIndex, endIndex);
                                                        
                                                        return (
                                                            <>
                                                                {totalPages > 1 && (
                                                                    <div style={{marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10}}>
                                                                        <button
                                                                            onClick={() => this.changeImportReviewPage(file.name, currentPage - 1)}
                                                                            disabled={currentPage === 1}
                                                                            style={{
                                                                                padding: '5px 10px',
                                                                                fontSize: 12,
                                                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                                                opacity: currentPage === 1 ? 0.5 : 1
                                                                            }}
                                                                        >
                                                                            ← Previous
                                                                        </button>
                                                                        <span style={{fontSize: 14, color: '#666'}}>
                                                                            Page {currentPage} of {totalPages} ({startIndex + 1}-{endIndex} of {fileItems.length})
                                                                        </span>
                                                                        <button
                                                                            onClick={() => this.changeImportReviewPage(file.name, currentPage + 1)}
                                                                            disabled={currentPage === totalPages}
                                                                            style={{
                                                                                padding: '5px 10px',
                                                                                fontSize: 12,
                                                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                                                opacity: currentPage === totalPages ? 0.5 : 1
                                                                            }}
                                                                        >
                                                                            Next →
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                <div style={{maxHeight: '45vh', overflowY: 'auto'}}>
                                                                    <table style={{width: '100%', fontSize: 14, borderCollapse: 'collapse'}}>
                                                                        <thead>
                                                                            <tr style={{backgroundColor: '#f5f5f5'}}>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', width: 40}}>Row</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', width: 60}}>Order #</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', width: 50}}>Line #</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', minWidth: 260}}>Part Number</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', width: 80}}>Quantity</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', width: 90}}>Sales Status</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'left', width: 150}}>Import Status</th>
                                                                                <th style={{padding: 5, border: '1px solid #ddd', textAlign: 'center', width: 60}}>Actions</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {paginatedItems.map((item, itemIndex) => {
                                                                                const actualItemIndex = startIndex + itemIndex;
                                                                                const hasError = this.state.importErrors.some(error => error.id === item.id) || 
                                                                                        (!item.assemblyId && !item.partId);
                                                                                const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                                                const isUnmatchingPN = item.isUnmatchingPN || (!item.assemblyId && !item.subAssemblyId && !item.partId);
                                                                                const globalIndex = this.state.parsedImportData.findIndex(dataItem => dataItem.id === item.id);
                                                                                
                                                                                // Calculate import order for this item
                                                                     const validItems = this.state.parsedImportData.filter(dataItem => 
                                                                         dataItem.assemblyId || dataItem.partId
                                                                     );
                                                                     const orderGroups = {};
                                                                     validItems.forEach(validItem => {
                                                                         if (!orderGroups[validItem.orderNumber]) {
                                                                             orderGroups[validItem.orderNumber] = [];
                                                                         }
                                                                         orderGroups[validItem.orderNumber].push(validItem);
                                                                     });
                                                                     
                                                                     let importOrder = 0;
                                                                     Object.keys(orderGroups).forEach(orderNumber => {
                                                                         const groupItems = orderGroups[orderNumber];
                                                                         
                                                                         // Use same logic as import: ECS by row index, Squarespace by date
                                                                         const isEcs = groupItems[0].source === 'ECS';
                                                                         if (isEcs) {
                                                                             groupItems.sort((a, b) => a.rowIndex - b.rowIndex);
                                                                         } else {
                                                                             groupItems.sort((a, b) => new Date(b.logDate) - new Date(a.logDate));
                                                                         }
                                                                         
                                                                         const itemIndexInGroup = groupItems.findIndex(groupItem => groupItem.id === item.id);
                                                                         if (itemIndexInGroup !== -1) {
                                                                             importOrder = validItems.indexOf(groupItems[itemIndexInGroup]) + 1;
                                                                         }
                                                                     });
                                                                     
                                                                     // Find current assembly/part for autocomplete
                                                                     let currentAssemblyPart = null;
                                                                     if (item.assemblyId) {
                                                                         const assembly = this.state.assemblies.find(x => x.assembly.id === item.assemblyId);
                                                                         if (assembly) {
                                                                             currentAssemblyPart = {
                                                                                 title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${assembly.assembly.currentlyPackaging ? ' 📦' : ''}${isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies) ? ' 🛒' : ''}`,
                                                                                 value: assembly.assembly.id,
                                                                                 type: 'assembly',
                                                                                 partId: assembly.assembly.assemblyId,
                                                                                 revision: assembly.assembly.revision,
                                                                                 description: assembly.assembly.description,
                                                                                 currentlyPackaging: assembly.assembly.currentlyPackaging,
                                                                                 currentlySelling: isAssemblyCurrentlySelling(assembly.assembly, this.state.assemblies)
                                                                             };
                                                                         }
                                                                     } else if (item.subAssemblyId) {
                                                                         const subAssembly = this.state.subAssemblies.find(x => x.subAssembly.id === item.subAssemblyId);
                                                                         if (subAssembly) {
                                                                             currentAssemblyPart = {
                                                                                 title: `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}${subAssembly.subAssembly.currentlyPackaging ? ' 📦' : ''}`,
                                                                                 value: subAssembly.subAssembly.id,
                                                                                 type: 'subassembly',
                                                                                 partId: subAssembly.subAssembly.subAssemblyId,
                                                                                 revision: subAssembly.subAssembly.revision,
                                                                                 description: subAssembly.subAssembly.description,
                                                                                 currentlyPackaging: subAssembly.subAssembly.currentlyPackaging
                                                                             };
                                                                         }
                                                                     } else if (item.partId) {
                                                                         const part = this.state.parts.find(x => x.part.id === item.partId);
                                                                         if (part) {
                                                                             currentAssemblyPart = {
                                                                                 title: `${part.part.partId} rev ${part.part.revision}`,
                                                                                 value: part.part.id,
                                                                                 type: 'part',
                                                                                 partId: part.part.partId,
                                                                                 revision: part.part.revision,
                                                                                 part: part.part,
                                                                                 currentlyPurchasing: false
                                                                             };
                                                                         }
                                                                     }
                                                                     
                                                                     return (
                                                                         <tr key={item.id} style={{
                                                                             backgroundColor: hasError ? '#ffcdd2' : 
                                                                                              item.wasManuallyFixed ? '#dfffe0' : // Light green for manually fixed mismatched PNs
                                                                                              (duplicateInfo && duplicateInfo.type === 'status_update' && duplicateInfo.isManuallyAssignedLine) ? '#f3e5f5' :
                                                                                              (duplicateInfo && duplicateInfo.isManuallyAssignedLine && duplicateInfo.type === 'exact_duplicate') ? '#f3e5f5' :
                                                                                              (duplicateInfo && duplicateInfo.type === 'exact_duplicate') ? 
                                                                                                  ((this.state.importOverrideExactDuplicates || this.state.importIndividualLineOverrides[item.id]) ? '#fff3e0' : '#ffcdd2') :
                                                                                              (duplicateInfo && duplicateInfo.type === 'revision_update') ? '#e8f5e8' :
                                                                                              (duplicateInfo && duplicateInfo.type === 'status_update') ? '#e8f5e8' :
                                                                                              (duplicateInfo && duplicateInfo.type === 'potential_update') ? '#e3f2fd' :
                                                                                              isUnmatchingPN ? '#fff3e0' : 'white'
                                                                         }}>
                                                                             <td style={{padding: 5, border: '1px solid #ddd'}}>{item.rowIndex}</td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd'}}>
                                                                                 <span style={{fontWeight: 'bold', color: '#1976d2'}}>
                                                                                     {item.orderNumber}
                                                                                 </span>
                                                                             </td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd', textAlign: 'center'}}>
                                                                                 <span style={{fontWeight: 'bold', color: '#1976d2'}}>
                                                                                     {item.lineNumber || 'N/A'}
                                                                                 </span>
                                                                             </td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd'}}>
                                                                                 {isUnmatchingPN ? (
                                                                                     <div>
                                                                                         <div style={{fontSize: 12, color: '#f57c00', fontWeight: 'bold', marginBottom: 5}}>
                                                                                             ⚠️ Imported: {item.partNumber}
                                                                                         </div>
                                                                                         <Autocomplete autoHighlight
                                                                                             options={partsOptions}
                                                                                             value={currentAssemblyPart}
                                                                                             onChange={(event, newValue) => this.handleImportItemAssemblyChange(globalIndex, newValue)}
                                                                                             getOptionLabel={(option) => {
                                                                                                 if (option.type === 'assembly') {
                                                                                                     return `${option.partId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}${option.currentlySelling ? ' 🛒' : ''}`;
                                                                                                 } else if (option.type === 'subassembly') {
                                                                                                     return `${option.partId} rev ${option.revision}`;
                                                                                                 } else if (option.type === 'part') {
                                                                                                     return `${option.partId} rev ${option.revision}`;
                                                                                                 }
                                                                                                 return option.title || '';
                                                                                             }}
                                                                                             style={{width: '100%'}}
                                                                                             size="small"
                                                                                             renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                                             renderInput={(params) => (
                                                                                                 <TextField 
                                                                                                     {...params} 
                                                                                                     variant="outlined" 
                                                                                                     placeholder="Select correct assembly/part..."
                                                                                                     size="small"
                                                                                                     style={{backgroundColor: '#fff3e0'}}
                                                                                                 />
                                                                                             )}
                                                                                         />
                                                                                     </div>
                                                                                 ) : (
                                                                                     <Autocomplete autoHighlight
                                                                                         options={partsOptions}
                                                                                         value={currentAssemblyPart}
                                                                                         onChange={(event, newValue) => this.handleImportItemAssemblyChange(globalIndex, newValue)}
                                                                                         getOptionLabel={(option) => {
                                                                                             if (option.type === 'assembly') {
                                                                                                 return `${option.partId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}${option.currentlySelling ? ' 🛒' : ''}`;
                                                                                             } else if (option.type === 'subassembly') {
                                                                                                 return `${option.partId} rev ${option.revision}`;
                                                                                             } else if (option.type === 'part') {
                                                                                                 return `${option.partId} rev ${option.revision}`;
                                                                                             }
                                                                                             return option.title || '';
                                                                                         }}
                                                                                         style={{width: '100%'}}
                                                                                         size="small"
                                                                                         renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                                         renderInput={(params) => (
                                                                                             <TextField 
                                                                                                 {...params} 
                                                                                                 variant="outlined" 
                                                                                                 placeholder="Select assembly/part..."
                                                                                                 size="small"
                                                                                             />
                                                                                         )}
                                                                                     />
                                                                                 )}
                                                                             </td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd'}}>
                                                                                 <TextField
                                                                                     value={item.quantity || ''}
                                                                                     onChange={(e) => this.updateImportItem(globalIndex, 'quantity', parseNumberInputValue(e.target.value))}
                                                                                     variant="outlined"
                                                                                     size="small"
                                                                                     type="number"
                                                                                     style={{width: '100%'}}
                                                                                     inputProps={{'data-row-index': globalIndex}}
                                                                                 />
                                                                             </td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd'}}>
                                                                                 <span style={{
                                                                                     fontSize: 12,
                                                                                     fontWeight: 'bold',
                                                                                     color: normalizeStatus(item.status) === 'Fulfilled' ? '#388e3c' : 
                                                                                            normalizeStatus(item.status) === 'Pending' ? '#f57c00' : 
                                                                                            normalizeStatus(item.status) === 'Cancelled' ? '#d32f2f' : 
                                                                                            normalizeStatus(item.status) === 'Refunded' ? '#9c27b0' : '#666'
                                                                                 }}>
                                                                                     {normalizeStatus(item.status)}
                                                                                 </span>
                                                                             </td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd'}}>
                                                                                 {duplicateInfo && duplicateInfo.type === 'exact_duplicate' && (
                                                                                     <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                                                         <span style={{
                                                                                             color: duplicateInfo.isManuallyAssignedLine ? '#9c27b0' : 
                                                                                                    (this.state.importOverrideExactDuplicates || this.state.importIndividualLineOverrides[item.id]) ? '#f57c00' : '#d32f2f', 
                                                                                             fontSize: 14
                                                                                         }}>
                                                                                             {duplicateInfo.isManuallyAssignedLine ? 
                                                                                                 '🔒 Locked' : 
                                                                                                 (this.state.importOverrideExactDuplicates || this.state.importIndividualLineOverrides[item.id]) ? 
                                                                                                     '⚠️ Will Import' : '🚫 Exact Duplicate'}
                                                                                         </span>
                                                                                         {!duplicateInfo.isManuallyAssignedLine && !this.state.importOverrideExactDuplicates && (
                                                                                             <Checkbox
                                                                                                 checked={!!this.state.importIndividualLineOverrides[item.id]}
                                                                                                 onChange={() => this.toggleIndividualLineOverride(item.id)}
                                                                                                 size="small"
                                                                                                 style={{padding: 0}}
                                                                                                 title="Override this specific line"
                                                                                             />
                                                                                         )}
                                                                                     </div>
                                                                                 )}
                                                                                {duplicateInfo && duplicateInfo.type === 'revision_update' && (
                                                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                                                        <span style={{
                                                                                            color: '#1976d2', 
                                                                                            fontSize: 14
                                                                                        }}>
                                                                                            🔄 Revision Update
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                {duplicateInfo && duplicateInfo.type === 'status_update' && (
                                                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                                                        <span style={{
                                                                                            color: duplicateInfo.isManuallyAssignedLine ? '#9c27b0' : '#4caf50', 
                                                                                            fontSize: 14
                                                                                        }}>
                                                                                            {duplicateInfo.isManuallyAssignedLine ? '📝 Line Status Update' : '📝 Status Update'}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                {duplicateInfo && duplicateInfo.type === 'locked_line' && (
                                                                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                                                        <span style={{
                                                                                            color: '#9c27b0', 
                                                                                            fontSize: 14
                                                                                        }}>
                                                                                            🔒 Locked
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                 {duplicateInfo && duplicateInfo.type === 'potential_update' && <span style={{color: '#1976d2', fontSize: 14}}>🔄 Will Update Existing</span>}
                                                                                 {isUnmatchingPN && (item.assemblyId || item.subAssemblyId || item.partId) && <span style={{color: '#388e3c', fontSize: 14}}>✅ Corrected</span>}
                                                                                 {isUnmatchingPN && !(item.assemblyId || item.subAssemblyId || item.partId) && <span style={{color: '#f57c00', fontSize: 14}}>⚠️ No Match</span>}
                                                                                 {!hasError && !duplicateInfo && !isUnmatchingPN && <span style={{color: '#388e3c', fontSize: 14}}>✅ Ready</span>}
                                                                             </td>
                                                                             <td style={{padding: 5, border: '1px solid #ddd', textAlign: 'center'}}>
                                                                                 <div style={{display: 'flex', gap: '5px', justifyContent: 'center'}}>
                                                                                     {duplicateInfo && duplicateInfo.isManuallyAssignedLine && (
                                                                                         <button
                                                                                             onClick={() => this.unlockImportRow(item.id)}
                                                                                             style={{
                                                                                                 background: 'none',
                                                                                                 border: '1px solid #9c27b0',
                                                                                                 color: '#9c27b0',
                                                                                                 borderRadius: 3,
                                                                                                 padding: '2px 6px',
                                                                                                 fontSize: 11,
                                                                                                 cursor: 'pointer',
                                                                                                 fontWeight: 'bold'
                                                                                             }}
                                                                                             title="Unlock this sales log to allow automatic reassignment"
                                                                                         >
                                                                                             🔓
                                                                                         </button>
                                                                                     )}
                                                                                     <button
                                                                                         onClick={() => this.removeImportRow(item.id)}
                                                                                         style={{
                                                                                             background: 'none',
                                                                                             border: '1px solid #d32f2f',
                                                                                             color: '#d32f2f',
                                                                                             borderRadius: 3,
                                                                                             padding: '2px 6px',
                                                                                             fontSize: 11,
                                                                                             cursor: 'pointer',
                                                                                             fontWeight: 'bold'
                                                                                         }}
                                                                                         title="Remove this row from import"
                                                                                     >
                                                                                         ✕
                                                                                     </button>
                                                                                 </div>
                                                                             </td>
                                                                         </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                                {totalPages > 1 && (
                                                                    <div style={{marginTop: 10, display: 'flex', alignItems: 'center', gap: 10}}>
                                                                        <button
                                                                            onClick={() => this.changeImportReviewPage(file.name, currentPage - 1)}
                                                                            disabled={currentPage === 1}
                                                                            style={{
                                                                                padding: '5px 10px',
                                                                                fontSize: 12,
                                                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                                                opacity: currentPage === 1 ? 0.5 : 1
                                                                            }}
                                                                        >
                                                                            ← Previous
                                                                        </button>
                                                                        <span style={{fontSize: 14, color: '#666'}}>
                                                                            Page {currentPage} of {totalPages} ({startIndex + 1}-{endIndex} of {fileItems.length})
                                                                        </span>
                                                                        <button
                                                                            onClick={() => this.changeImportReviewPage(file.name, currentPage + 1)}
                                                                            disabled={currentPage === totalPages}
                                                                            style={{
                                                                                padding: '5px 10px',
                                                                                fontSize: 12,
                                                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                                                opacity: currentPage === totalPages ? 0.5 : 1
                                                                            }}
                                                                        >
                                                                            Next →
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            );
                                        })}
                                         
                                         <div style={{marginTop: 20}}>
                                             <div style={{
                                                 marginBottom: 15, 
                                                 padding: 10, 
                                                 backgroundColor: (this.state.importOverrideExactDuplicates || Object.keys(this.state.importIndividualLineOverrides).length > 0) ? '#fff3e0' : '#e8f5e8', 
                                                 border: `1px solid ${(this.state.importOverrideExactDuplicates || Object.keys(this.state.importIndividualLineOverrides).length > 0) ? '#ff9800' : '#4caf50'}`, 
                                                 borderRadius: 4
                                             }}>
                                                 <div style={{
                                                     fontSize: 14, 
                                                     color: (this.state.importOverrideExactDuplicates || Object.keys(this.state.importIndividualLineOverrides).length > 0) ? '#f57c00' : '#2e7d32', 
                                                     fontWeight: 'bold'
                                                 }}>
                                                     📊 Import Summary
                                                     {(this.state.importOverrideExactDuplicates || Object.keys(this.state.importIndividualLineOverrides).length > 0) && ' (Override Mode)'}
                                                 </div>
                                                 <div style={{fontSize: 14, color: '#666', marginTop: 5}}>
                                                     Files: {this.state.importFiles.length} | 
                                                     Total Items: {this.state.parsedImportData.length} | 
                                                     Valid Items: {this.state.parsedImportData.filter(item => {
                                                         const hasAssemblyOrSubAssemblyOrPart = item.assemblyId || item.subAssemblyId || item.partId;
                                                         const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                         
                                                         if (duplicateInfo && duplicateInfo.type === 'exact_duplicate') {
                                                             const globalOverride = this.state.importOverrideExactDuplicates;
                                                             const individualOverride = this.state.importIndividualLineOverrides[item.id];
                                                             return (globalOverride || individualOverride) && hasAssemblyOrSubAssemblyOrPart;
                                                         } else if (duplicateInfo && duplicateInfo.type === 'revision_update') {
                                                             // Revision updates are automatically included
                                                             return hasAssemblyOrSubAssemblyOrPart;
                                                         }
                                                         
                                                         return hasAssemblyOrSubAssemblyOrPart;
                                                     }).length} | 
                                                     Unmatching PN: {this.state.parsedImportData.filter(item => item.isUnmatchingPN || (!item.assemblyId && !item.subAssemblyId && !item.partId)).length} | 
                                                     Errors: {this.state.importErrors.length} | 
                                                     Exact Duplicates: {this.state.importDuplicates.filter(dup => dup.type === 'exact_duplicate').length} | 
                                                     Revision Updates: {this.state.importDuplicates.filter(dup => dup.type === 'revision_update').length} | 
                                                     Status Updates: {this.state.importDuplicates.filter(dup => dup.type === 'status_update').length} | 
                                                     Potential Updates: {this.state.importDuplicates.filter(dup => dup.type === 'potential_update').length}
                                                     {Object.keys(this.state.importIndividualLineOverrides).length > 0 && ` | Individual Overrides: ${Object.keys(this.state.importIndividualLineOverrides).length}`}
                                                 </div>
                                             </div>
                                             <Button 
                                                 variant="contained"
                                                 color="primary"
                                                 onClick={this.importSalesLogs} 
                                                 className="success-button-lg"
                                                 disabled={this.state.parsedImportData.filter(item => {
                                                     const hasAssemblyOrSubAssemblyOrPart = item.assemblyId || item.subAssemblyId || item.partId;
                                                     const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                     
                                                     if (duplicateInfo && duplicateInfo.type === 'exact_duplicate') {
                                                         const globalOverride = this.state.importOverrideExactDuplicates;
                                                         const individualOverride = this.state.importIndividualLineOverrides[item.id];
                                                         return (globalOverride || individualOverride) && hasAssemblyOrSubAssemblyOrPart;
                                                     } else if (duplicateInfo && duplicateInfo.type === 'revision_update') {
                                                         // Revision updates are automatically included
                                                         return hasAssemblyOrSubAssemblyOrPart;
                                                     }
                                                     
                                                     return hasAssemblyOrSubAssemblyOrPart;
                                                 }).length === 0}
                                             >
                                                 {`Import ${this.state.parsedImportData.filter(item => {
                                                     const hasAssemblyOrSubAssemblyOrPart = item.assemblyId || item.subAssemblyId || item.partId;
                                                     const duplicateInfo = this.state.importDuplicates.find(dup => dup.id === item.id);
                                                     
                                                     if (duplicateInfo && duplicateInfo.type === 'exact_duplicate') {
                                                         const globalOverride = this.state.importOverrideExactDuplicates;
                                                         const individualOverride = this.state.importIndividualLineOverrides[item.id];
                                                         return (globalOverride || individualOverride) && hasAssemblyOrSubAssemblyOrPart;
                                                     } else if (duplicateInfo && duplicateInfo.type === 'revision_update') {
                                                         // Revision updates are automatically included
                                                         return hasAssemblyOrSubAssemblyOrPart;
                                                     }
                                                     
                                                     return hasAssemblyOrSubAssemblyOrPart;
                                                 }).length} Valid Items`}
                                             </Button>
                                             <Button 
                                                 variant="contained"
                                                 color="primary"
                                                 onClick={this.resetImport} 
                                                 className="success-button-lg" 
                                                 style={{marginLeft: 10}}
                                             >
                                                 Reset Import
                                             </Button>
                                         </div>
                                     </>
                                 )}
                                                                 {this.state.importStep === 'upload' && this.state.importFiles.length > 0 && (
                                     <>
                                         <h3>Files Selected for Import</h3>
                                         <div style={{maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4, padding: 15, marginTop: 10, backgroundColor: '#fafafa'}}>
                                             {this.state.importFiles.map((file, index) => {
                                                 const matchingParsedFile = this.state.parsedImportData.find(pf => pf.name === file.name);
                                                 return (
                                                     <div key={file.name} style={{
                                                         padding: '10px 0', 
                                                         borderBottom: index < this.state.importFiles.length - 1 ? '1px dashed #ddd' : 'none',
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         justifyContent: 'space-between'
                                                     }}>
                                                         <div>
                                                             <div style={{fontWeight: 'bold', fontSize: 14}}>📄 {file.name}</div>
                                                             <div style={{fontSize: 14, color: '#666', marginTop: 2}}>
                                                                 Order: {file.name.replace('.csv', '')} | 
                                                                 Dealer: {matchingParsedFile ? matchingParsedFile.dealerType : 'Unknown'} |
                                                                 Size: {(file.size / 1024).toFixed(1)} KB
                                                             </div>
                                                         </div>
                                                         <div style={{color: '#4caf50', fontSize: 14}}>
                                                             ✅ Ready
                                                         </div>
                                                     </div>
                                                 );
                                             })}
                                         </div>
                                         <div style={{marginTop: 15, padding: 10, backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: 4}}>
                                             <div style={{fontSize: 14, color: '#1976d2', marginBottom: 5}}>
                                                 ℹ️ Ready to process {this.state.importFiles.length} file(s) from {this.state.importDealer || 'Unknown'} dealer
                                             </div>
                                                                                           <div style={{fontSize: 12, color: '#666'}}>
                                                  Click "Next" to analyze the files for existing duplicates and errors before importing.
                                              </div>
                                         </div>
                                         <br/>
                                         <Button 
                                             variant="contained"
                                             color="primary"
                                             onClick={this.processImportFiles} 
                                             className="success-button-lg"
                                         >
                                             Next - Analyze Files
                                         </Button>
                                     </>
                                 )}
                            </DrawerMenuRight>
                            : ""}
                        <ManualSalesLogDrawer
                            open={this.state.manualSalesLogOpen}
                            onClose={this.handleCloseManualLog}
                            onCreated={this.handleManualSalesLogCreated}
                            salesLogSources={this.state.salesLogSources}
                            partsOptions={partsOptions}
                            assemblies={this.state.assemblies}
                            subAssemblies={this.state.subAssemblies}
                            parts={this.state.parts}
                            classes={this.props.classes}
                        />
                        {/* Multiline Sales Order Edit Drawer */}
                        {this.state.editSalesOrderOpen ?
                            <DrawerMenuRight 
                                title={`Edit Sales Order - ${this.state.editingSalesOrderGlobalSeq}`} 
                                customWidth={1200} 
                                open={this.state.editSalesOrderOpen}
                                onClose={this.handleCloseMultilineEdit}
                                disableEnterKeySubmit
                            >
                                <div style={{padding: '20px'}}>
                                    {/* Line Items Table */}
                                    <div style={{marginBottom: '20px'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                                            <h3 style={{margin: 0}}>Line Items ({this.state.editingSalesOrderLogs.length})</h3>
                                            <span style={{display: 'flex', gap: '8px'}}>
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={this.openCopyTableLineSelector}
                                                    size="small"
                                                >
                                                    Show Copy Table
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={this.handleAddNewSalesOrderLog}
                                                    size="small"
                                                >
                                                    + Add Line Item
                                                </Button>
                                            </span>
                                        </div>
                                        
                                        <div className="parts-table-container">
                                            <table className="parts-table" style={{minWidth: '1000px'}}>
                                                <thead>
                                                    <tr>
                                                        <th style={{minWidth: '40px', textAlign: 'center'}}>#</th>
                                                        <th style={{minWidth: '300px'}}>Assembly / Part</th>
                                                        <th style={{minWidth: '80px'}}>Qty</th>
                                                        <th style={{minWidth: '120px'}}>Status</th>
                                                        <th style={{minWidth: '300px'}}>Reason</th>
                                                        <th style={{minWidth: '80px', textAlign: 'center'}}>Affects<br/>Projections</th>
                                                        <th style={{minWidth: '60px', textAlign: 'center'}}>Lock</th>
                                                        <th style={{minWidth: '60px', textAlign: 'center'}}>Del</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {this.state.editingSalesOrderLogs.map((log, index) => (
                                                        <tr key={log.tempId} style={{
                                                            backgroundColor: log.isNew ? '#f0f8ff' : 'white',
                                                            borderLeft: log.isNew ? '3px solid #2196f3' : 'none'
                                                        }}>
                                                            <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                                                {index + 1}
                                                                {log.isNew && <span style={{color: '#2196f3', fontSize: '10px', display: 'block'}}>NEW</span>}
                                                            </td>
                                                            <td style={{padding: '8px'}}>
                                                                <Autocomplete 
                                                                    autoHighlight
                                                                    options={partsOptions}
                                                                    onChange={(event, newValue) => this.handleSalesOrderPartChange(index, newValue)}
                                                                    value={log.currentAssemblyPart}
                                                                    getOptionLabel={(option) => {
                                                                        if (!option) return '';
                                                                        if (option.type === 'assembly') {
                                                                            return `${option.partId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}${option.currentlySelling ? ' 🛒' : ''}`;
                                                                        } else if (option.type === 'subassembly') {
                                                                            return `${option.partId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}`;
                                                                        } else if (option.type === 'part') {
                                                                            return `${option.partId} rev ${option.revision}`;
                                                                        }
                                                                        return option.title || '';
                                                                    }}
                                                                    getOptionSelected={(option, value) => option.value === value.value}
                                                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                    renderInput={(params) => (
                                                                        <TextField 
                                                                            {...params} 
                                                                            variant="outlined"
                                                                            size="small"
                                                                            autoComplete="new-password"
                                                                        />
                                                                    )}
                                                                />
                                                            </td>
                                                            <td style={{padding: '8px'}}>
                                                                <TextField 
                                                                    type="number"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={log.quantity || ''}
                                                                    onChange={(e) => this.handleSalesOrderLogChange(index, 'quantity', e.target.value)}
                                                                    inputProps={{style: {textAlign: 'center'}}}
                                                                    style={{width: '100%'}}
                                                                />
                                                            </td>
                                                            <td style={{padding: '8px'}}>
                                                                <FormControl variant="outlined" size="small" style={{width: '100%'}}>
                                                                    <Select 
                                                                        value={normalizeStatus(log.status)} 
                                                                        onChange={(e) => this.handleSalesOrderLogChange(index, 'status', e.target.value)}
                                                                    >
                                                                        <MenuItem value="Fulfilled">Fulfilled</MenuItem>
                                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </td>
                                                            <td style={{padding: '8px'}}>
                                                                <TextField 
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={log.reason || ''}
                                                                    onChange={(e) => this.handleSalesOrderLogChange(index, 'reason', e.target.value)}
                                                                    inputProps={{maxLength: 500}}
                                                                    style={{width: '100%'}}
                                                                    multiline
                                                                />
                                                            </td>
                                                            <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                                                <Checkbox 
                                                                    checked={log.affectsProjections}
                                                                    onChange={(e) => this.handleSalesOrderLogChange(index, 'affectsProjections', e.target.checked)}
                                                                    style={{padding: '0'}}
                                                                />
                                                            </td>
                                                            <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                                                {log.isManuallyAssigned && !log.isNew && (
                                                                    <span style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                                                                        <FontAwesomeIcon icon={faLock} style={{color: '#9c27b0'}} />
                                                                        {this.state.unlockingSalesOrderLogId === log.id ? (
                                                                            <CircularProgress size={20} />
                                                                        ) : (
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                onClick={() => this.unlockSalesOrderLog(log)}
                                                                                style={{minWidth: '60px', padding: '2px 6px', fontSize: '11px'}}
                                                                            >
                                                                                Unlock
                                                                            </Button>
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td style={{textAlign: 'center', verticalAlign: 'middle'}}>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="secondary"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        if (window.confirm('Delete this line item?')) {
                                                                            this.handleDeleteSalesOrderLog(index);
                                                                        }
                                                                    }}
                                                                    style={{minWidth: '50px', padding: '4px'}}
                                                                >
                                                                    ✕
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Copy table line selector */}
                                    {this.state.showCopyTableLineSelector && this.state.editingSalesOrderLogs.length > 0 && (
                                        <div style={{
                                            backgroundColor: '#f9f9f9',
                                            padding: '16px',
                                            borderRadius: '8px',
                                            marginBottom: '20px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <div style={{marginBottom: '12px', fontWeight: 'bold'}}>Include in copy table</div>
                                            <div style={{display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px'}}>
                                                {this.state.editingSalesOrderLogs.map((log, index) => {
                                                    const label = log.currentAssemblyPart
                                                        ? `${index + 1}. ${log.currentAssemblyPart.partId || ''} rev ${log.currentAssemblyPart.revision || ''} × ${log.quantity || 0}`
                                                        : `${index + 1}. (no part selected) × ${log.quantity || 0}`;
                                                    return (
                                                        <label key={log.tempId} style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                                                            <Checkbox
                                                                checked={this.state.copyTableSelectedIds.includes(log.tempId)}
                                                                onChange={() => this.toggleCopyTableLine(log.tempId)}
                                                                style={{padding: '4px'}}
                                                            />
                                                            <span style={log.isNew ? {color: '#2196f3'} : {}}>{label}{log.isNew ? ' (new)' : ''}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={this.submitCopyTableFromOrder}
                                            >
                                                Open Copy Table
                                            </Button>
                                        </div>
                                    )}
                                    
                                    <center>
                                        <div style={{display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px'}}>
                                            <Button 
                                                variant="outlined"
                                                onClick={this.handleCloseMultilineEdit}
                                                className="cancel-button-lg"
                                            >
                                                Cancel
                                            </Button>
                                            {this.state.savingSalesOrder ? 
                                                <CircularProgress />
                                                :
                                                <Button 
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={this.handleSaveSalesOrder}
                                                    className="success-button-lg"
                                                >
                                                    Save All Changes
                                                </Button>
                                            }
                                        </div>
                                    </center>
                                </div>
                            </DrawerMenuRight>
                            : ""}
                        {this.state.bulkEditDrawerOpen ?
                            <DrawerMenuRight title={"Bulk Edit Sales Logs"} customWidth={500} open={this.state.bulkEditDrawerOpen}
                                             onClose={this.handleCloseBulkEditDrawer}>
                                <div style={{padding: '20px'}}>
                                    <div style={{marginBottom: '20px'}}>
                                        <strong>{this.state.selectedSalesLogs.length}</strong> sales log(s) selected
                                    </div>

                                    {/* Toggle Affects Sales Projections */}
                                    <div style={{marginBottom: '20px'}}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel id="bulk-edit-affects-projections-label">Affects Sales Projections</InputLabel>
                                            <Select
                                                labelId="bulk-edit-affects-projections-label"
                                                value={this.state.bulkEditAffectsProjections === null ? 'noChange' : (this.state.bulkEditAffectsProjections ? 'true' : 'false')}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    this.setState({
                                                        bulkEditAffectsProjections: value === 'noChange' ? null : value === 'true'
                                                    });
                                                }}
                                                label="Affects Sales Projections"
                                                renderValue={(selected) => {
                                                    if (selected === 'noChange') return 'No Change';
                                                    return selected === 'true' ? 'Yes' : 'No';
                                                }}
                                            >
                                                <MenuItem value="noChange">No Change</MenuItem>
                                                <MenuItem value="true">Yes</MenuItem>
                                                <MenuItem value="false">No</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    {/* Update Contact Info */}
                                    <div style={{marginBottom: '20px'}}>
                                        <TextField
                                            label="Customer Name (leave blank for no change)"
                                            variant="outlined"
                                            value={this.state.bulkEditCustomerName}
                                            onChange={(e) => this.setState({ bulkEditCustomerName: e.target.value })}
                                            style={{width: '100%', marginBottom: '10px'}}
                                            inputProps={{maxLength: 200}}
                                        />
                                        <TextField
                                            label="Shipping Address (leave blank for no change)"
                                            variant="outlined"
                                            value={this.state.bulkEditShippingAddress}
                                            onChange={(e) => this.setState({ bulkEditShippingAddress: e.target.value })}
                                            style={{width: '100%', marginBottom: '10px'}}
                                            inputProps={{maxLength: 500}}
                                            multiline
                                            rows={3}
                                        />
                                        <TextField
                                            label="Customer Email (leave blank for no change)"
                                            variant="outlined"
                                            value={this.state.bulkEditCustomerEmail}
                                            onChange={(e) => this.setState({ bulkEditCustomerEmail: e.target.value })}
                                            style={{width: '100%'}}
                                            inputProps={{maxLength: 255}}
                                        />
                                    </div>

                                    {/* Lock/Unlock */}
                                    <div style={{marginBottom: '20px'}}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel id="bulk-edit-lock-unlock-label">Lock/Unlock</InputLabel>
                                            <Select
                                                labelId="bulk-edit-lock-unlock-label"
                                                value={this.state.bulkEditLockUnlock === null ? 'noChange' : (this.state.bulkEditLockUnlock ? 'lock' : 'unlock')}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    this.setState({
                                                        bulkEditLockUnlock: value === 'noChange' ? null : value === 'lock'
                                                    });
                                                }}
                                                label="Lock/Unlock"
                                                renderValue={(selected) => {
                                                    if (selected === 'noChange') return 'No Change';
                                                    return selected === 'lock' ? 'Lock' : 'Unlock';
                                                }}
                                            >
                                                <MenuItem value="noChange">No Change</MenuItem>
                                                <MenuItem value="lock">Lock</MenuItem>
                                                <MenuItem value="unlock">Unlock</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    {/* Change Status */}
                                    <div style={{marginBottom: '20px'}}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel id="bulk-edit-status-label">Status</InputLabel>
                                            <Select
                                                labelId="bulk-edit-status-label"
                                                value={this.state.bulkEditStatus === '' ? 'noChange' : this.state.bulkEditStatus}
                                                onChange={(e) => this.setState({ bulkEditStatus: e.target.value === 'noChange' ? '' : e.target.value })}
                                                label="Status"
                                                renderValue={(selected) => {
                                                    if (selected === 'noChange') return 'No Change';
                                                    return selected;
                                                }}
                                            >
                                                <MenuItem value="noChange">No Change</MenuItem>
                                                <MenuItem value="Fulfilled">Fulfilled</MenuItem>
                                                <MenuItem value="Pending">Pending</MenuItem>
                                                <MenuItem value="Cancelled">Cancelled</MenuItem>
                                                <MenuItem value="Refunded">Refunded</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    {/* Add Reason */}
                                    <div style={{marginBottom: '20px'}}>
                                        <TextField
                                            label="Reason (leave blank for no change)"
                                            variant="outlined"
                                            value={this.state.bulkEditReason}
                                            onChange={(e) => this.setState({ bulkEditReason: e.target.value })}
                                            style={{width: '100%'}}
                                            multiline
                                            rows={3}
                                        />
                                    </div>

                                    <div style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px'}}>
                                        <Button
                                            variant="outlined"
                                            onClick={this.handleCloseBulkEditDrawer}
                                            disabled={this.state.savingBulkEdit}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={this.handleBulkEditSave}
                                            disabled={this.state.savingBulkEdit}
                                        >
                                            {this.state.savingBulkEdit ? <CircularProgress size={20} /> : 'Save Changes'}
                                        </Button>
                                    </div>
                                </div>
                            </DrawerMenuRight>
                            : ""}

                        {/* Copy Table Dialog */}
                        <Dialog 
                            open={this.state.showCopyTable} 
                            onClose={() => this.setState({showCopyTable: false})}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle>
                                Copy Table for Email
                            </DialogTitle>
                            <DialogContent>
                                <table style={{
                                    margin: '0 auto',
                                    borderCollapse: 'collapse',
                                    border: '1px solid black',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '12px'
                                }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'center'
                                            }}>
                                                Quantity
                                            </th>
                                            <th style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'left'
                                            }}>
                                                Item
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(this.state.copyTableData || []).map((item, index) => (
                                            <tr key={index}>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'center'
                                                }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'left'
                                                }}>
                                                    {item.partNumber} rev {item.revision}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.copyTableToClipboard} className="success-button-lg">Copy Table</Button>
                                <Button onClick={() => this.setState({showCopyTable: false})} className="cancel-button-lg">Close</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Bulk Edit Toolbar - Fixed Bottom */}
                        {this.state.selectedSalesLogs.length > 0 && (
                            <div style={{
                                position: 'fixed',
                                bottom: 20,
                                left: 182,
                                right: 0,
                                zIndex: 1000,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '16px',
                                pointerEvents: 'none'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    gap: '16px',
                                    pointerEvents: 'auto'
                                }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => this.setState({ selectedSalesLogs: [] })}
                                        className="success-button-lg"
                                    >
                                        Deselect All
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.openBulkEditDrawer}
                                        className="success-button-lg"
                                    >
                                        Bulk Edit
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.packageSelectedSalesLogs}
                                        className="success-button-lg"
                                        disabled={this.state.packagingSelectedSalesLogs || this.state.savingImportedData}
                                    >
                                        {this.state.packagingSelectedSalesLogs
                                            ? 'Packaging...'
                                            : `Package Selected (${this.getPackageableSalesLogs().length})`}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={this.deleteSelectedSalesLogs}
                                        className="delete-button-lg"
                                        disabled={this.state.savingImportedData || this.state.packagingSelectedSalesLogs}
                                    >
                                        {this.state.savingImportedData ? 'Deleting...' : 'Delete selected'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

let globalProps;

AdminSales.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(AdminSales);
